/**
 * CompliCore console MVP checklists (NC-627 Website, NC-628 DoD, Education Compliance).
 * Website console includes a homepage privacy/cookie/DMCA scan plus DNS
 * email-auth (SPF/DKIM/DMARC) and response-header checks. Cloud, container,
 * and git Zero Trust items are checklist + evidence; coverage deepens by plan.
 */

import {
  emptyDodEvidenceConnectors,
  emptyDodTaxonomy,
  newDodId,
  type DodEvidenceConnectors,
  type DodLevelAssessment,
  type DodTaxonomyState,
  type PracticeDisposition,
} from './dodCmmcCatalog';
import type { PricingTierId } from './pricingTiers';
import {
  calendarSuggestionsFor,
  calendarTaskId,
  parseCalendarEvents,
  type ComplianceCalendarEvent,
} from '../complianceCalendar';

export type ComplianceItemStatus = 'not_started' | 'in_progress' | 'reviewed' | 'gap';

/** Bid-readiness gates for DoD IT consulting (FCI first; CUI and classified later). */
export type DodComplianceGate = 'entity' | 'fci' | 'cui' | 'classified' | 'pursuit';

export type ComplianceItemPriority = 'blocking' | 'required' | 'recommended';

export type ComplianceVerifyStepKind = 'do' | 'wait' | 'proof';

/** What must exist before the operator can mark this control complete. */
export type ComplianceCompleteRequires = 'uei-cage' | 'evidence';

export type ComplianceVerifyStepDef = {
  id: string;
  text: string;
  evidenceHint: string;
  kind?: ComplianceVerifyStepKind;
};

export type ComplianceStepEvidence = {
  done: boolean;
  doneAt?: string;
  inferred?: boolean;
  evidenceName?: string;
  evidenceNote?: string;
  evidenceDataUrl?: string;
  evidenceMime?: string;
};

/**
 * How Do-it-for-me / the operator reaches the work site.
 * - none: public page or no login (homepage scan, public docs)
 * - operator: human signs into accessUrl; agent does not need a vault secret first
 * - vault: Do-it-for-me asks for Gates credentials (portalId) before portal / API work
 */
export type TaskCredentialsNeed = 'none' | 'operator' | 'vault';

export type ComplianceChecklistItemDef = {
  id: string;
  title: string;
  action: string;
  /** Numbered how-to shown on Next steps and the checklist item. */
  actionSteps?: string[];
  /** Replaces actionSteps when notes show an external wait (e.g. SAM Entity Under Review). */
  waitingSteps?: string[];
  /** Confirmable steps with per-step proof. Preferred over a static how-to list. */
  verifySteps?: ComplianceVerifyStepDef[];
  /** Operator button. Most consoles say Mark finished. Website scan cards say Mark compliant. */
  completeLabel?: string;
  /** Gate the complete button until identifiers or uploaded/noted proof exist. */
  completeRequires?: ComplianceCompleteRequires;
  evidenceHint: string;
  gate?: DodComplianceGate;
  /** Lowest subscription that includes this control. Default CompliCore. */
  planMin?: PricingTierId;
  priority?: ComplianceItemPriority;
  /** Checklist item ids that should be reviewed before this one. */
  dependsOn?: string[];
  /** Plain-language dependency, including soft waits (e.g. SAM pay vs SAM start). */
  dependencyNote?: string;
  sourceLabel?: string;
  sourceUrl?: string;
  docsLabel?: string;
  docsUrl?: string;
  /**
   * Where the work is performed (CMS admin, SPRS, Comptroller API).
   * Distinct from sourceUrl / docsUrl, which stay public guidance.
   */
  accessUrl?: string;
  accessLabel?: string;
  /** Vault portal id in Gates and credentials (sprs, tx_comptroller, cms, aws, github, …). */
  portalId?: string;
  /** Whether a login / vault secret is needed to finish this task. Default none. */
  credentials?: TaskCredentialsNeed;
  /** When true, the operator must download this record and keep a local copy for the company file. */
  keepCopy?: boolean;
};

export const taskCredentialsNeed = (item: ComplianceChecklistItemDef): TaskCredentialsNeed => (
  item.credentials || 'none'
);

export const taskNeedsVaultCredentials = (item: ComplianceChecklistItemDef): boolean => (
  taskCredentialsNeed(item) === 'vault'
);

export const taskWorkUrl = (item: ComplianceChecklistItemDef): string => (
  String(item.accessUrl || item.docsUrl || item.sourceUrl || '').trim()
);

export type ChecklistDependencyState = {
  item: ComplianceChecklistItemDef;
  done: boolean;
};

export const calendarChecklistItem = (kind: string): ComplianceChecklistItemDef => {
  const suggestions = calendarSuggestionsFor(kind);
  return {
    id: calendarTaskId(kind),
    title: 'Put the dates for this work on the calendar',
    action: 'Open the calendar on this page and save a date for each row that applies. Choose Not this year only when that row does not apply. Do not invent a date. Dates for other compliance work stay on their own pages and also appear on the all-projects calendar.',
    verifySteps: suggestions.map((suggestion) => ({
      id: suggestion.id,
      kind: 'proof' as const,
      text: `Save the date for ${suggestion.title.en}, or mark it not this year.`,
      evidenceHint: 'Date on the compliance calendar',
    })),
    evidenceHint: 'Each applicable date saved on the calendar',
    completeLabel: 'Mark complete (dates on the calendar)',
    completeRequires: 'evidence',
    priority: 'required',
  };
};

export const itemCompleteLabel = (
  item: ComplianceChecklistItemDef,
  language: 'en' | 'es',
  kind: ComplianceConsoleKind,
): string => {
  if (item.completeLabel) {
    return item.completeLabel;
  }
  if (kind === 'website-compliance') {
    return language === 'es' ? 'Marcar cumple' : 'Mark compliant';
  }
  return language === 'es' ? 'Marcar terminado' : 'Mark finished';
};

export const workflowStatusLabel = (
  status: ComplianceItemStatus,
  language: 'en' | 'es',
  kind: ComplianceConsoleKind,
): string => {
  if (kind !== 'website-compliance' && status === 'reviewed') {
    return language === 'es' ? 'Terminado' : 'Finished';
  }
  if (kind === 'website-compliance') {
    const website: Record<ComplianceItemStatus, { en: string; es: string }> = {
      not_started: { en: 'Not started', es: 'Sin iniciar' },
      in_progress: { en: 'In progress', es: 'En progreso' },
      reviewed: { en: 'Compliant', es: 'Cumple' },
      gap: { en: 'Not compliant', es: 'No cumple' },
    };
    return language === 'es' ? website[status].es : website[status].en;
  }
  const labels: Record<ComplianceItemStatus, { en: string; es: string }> = {
    not_started: { en: 'Not started', es: 'Sin iniciar' },
    in_progress: { en: 'In progress', es: 'En progreso' },
    reviewed: { en: 'Reviewed', es: 'Revisado' },
    gap: { en: 'Gap / needs work', es: 'Brecha / pendiente' },
  };
  return language === 'es' ? labels[status].es : labels[status].en;
};

export const isItemCompleteLocked = (
  item: ComplianceChecklistItemDef,
  itemState: { notes?: string; stepProgress?: Record<string, ComplianceStepEvidence> } | undefined,
  ids?: { uei?: string; cage?: string },
): boolean => {
  if (item.completeRequires === 'uei-cage') {
    return !(String(ids?.uei || '').trim() && String(ids?.cage || '').trim());
  }
  if (item.completeRequires !== 'evidence') {
    return false;
  }
  const steps = item.verifySteps || [];
  const proofSteps = steps.filter((step) => step.kind === 'proof');
  const needed = proofSteps.length ? proofSteps : steps.slice(-1);
  if (!needed.length) {
    return !String(itemState?.notes || '').trim();
  }
  return needed.some((step) => itemState?.stepProgress?.[step.id]?.done !== true);
};

export const itemCompleteLockedReason = (
  item: ComplianceChecklistItemDef,
  language: 'en' | 'es',
): string => {
  if (item.completeRequires === 'uei-cage') {
    return language === 'es'
      ? 'No marque completo hasta que SAM esté Active y tenga UEI y CAGE.'
      : 'Do not mark complete until SAM is Active and UEI and CAGE are on file.';
  }
  if (item.completeRequires === 'evidence') {
    return language === 'es'
      ? 'No marque completo hasta archivar la prueba de este control (captura, COI o nota).'
      : 'Do not mark complete until this control\'s proof is on file (screenshot, COI, or note).';
  }
  return '';
};

export type CompliancePillarDef = {
  id: string;
  label: string;
  summary: string;
  items: ComplianceChecklistItemDef[];
};

export type ComplianceConsoleKind =
  | 'website-compliance'
  | 'dod-contracts-compliance'
  | 'education-compliance'
  | 'ai-compliance'
  | 'business-creation-compliance'
  | 'wills-trusts-compliance'
  | 'cloud-compliance'
  | 'ofac-compliance';

export type BusinessEntityKind = 'llc' | 'corporation' | 'nonprofit' | 'partnership' | 'sole-prop';

/** Answers from the business-creation interview. One plan per console. */
export type BusinessCreationAnswers = {
  entity?: BusinessEntityKind;
  origin?: 'us' | 'territory' | 'foreign';
  homeCountry?: string;
  stage?: 'not-filed' | 'pending' | 'approved';
  state?: string;
  legalName?: string;
  useDba?: 'yes' | 'no';
  dbaName?: string;
  owners?: 'one' | 'many';
  management?: 'members' | 'managers';
  agent?: 'owner' | 'service' | 'later';
  workers?: 'employees' | 'contractors' | 'owners';
  tax?: 'default' | 's-corp' | 'c-corp';
  exempt?: 'apply' | 'later';
  activity?: 'professional' | 'food' | 'goods' | 'field' | 'online' | 'other';
  /** company = this legal entity only. product = a subscription or software line it sells. subsidiary = a separate company it owns. */
  offers?: 'company' | 'product' | 'subsidiary';
  salesTax?: 'yes' | 'no' | 'unsure';
  extraStates?: 'no' | 'yes';
  extraStateList?: string;
  bank?: 'no' | 'yes';
};

export type WillsPlanKind = 'will' | 'trust';
export type WillsCapacity = 'adult' | 'minor';
export type WillsMarital = 'single' | 'married' | 'widowed' | 'divorced';
export type WillsChildren = 'none' | 'minors' | 'adults';
export type WillsCounsel = 'retained' | 'looking';
export type WillsStage = 'none' | 'draft' | 'signed';

/** Answers from the wills and trusts interview. One plan per person. */
export type WillsTrustsAnswers = {
  plan?: WillsPlanKind;
  state?: string;
  person?: string;
  capacity?: WillsCapacity;
  marital?: WillsMarital;
  children?: WillsChildren;
  counsel?: WillsCounsel;
  stage?: WillsStage;
};

const asChoice = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined => (
  allowed.includes(value as T) ? value as T : undefined
);

export const parseBusinessCreationAnswers = (value: unknown): BusinessCreationAnswers => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const row = value as Record<string, unknown>;
  const text = (key: string): string | undefined => {
    const raw = typeof row[key] === 'string' ? row[key].trim() : '';
    return raw || undefined;
  };
  return {
    entity: asChoice(row.entity, ['llc', 'corporation', 'nonprofit', 'partnership', 'sole-prop'] as const),
    origin: asChoice(row.origin, ['us', 'territory', 'foreign'] as const),
    homeCountry: text('homeCountry'),
    stage: asChoice(row.stage, ['not-filed', 'pending', 'approved'] as const),
    state: text('state'),
    legalName: text('legalName'),
    useDba: asChoice(row.useDba, ['yes', 'no'] as const),
    dbaName: text('dbaName'),
    owners: asChoice(row.owners, ['one', 'many'] as const),
    management: asChoice(row.management, ['members', 'managers'] as const),
    agent: asChoice(row.agent, ['owner', 'service', 'later'] as const),
    workers: asChoice(row.workers, ['employees', 'contractors', 'owners'] as const),
    tax: asChoice(row.tax, ['default', 's-corp', 'c-corp'] as const),
    exempt: asChoice(row.exempt, ['apply', 'later'] as const),
    activity: asChoice(row.activity, ['professional', 'food', 'goods', 'field', 'online', 'other'] as const),
    offers: asChoice(row.offers, ['company', 'product', 'subsidiary'] as const),
    salesTax: asChoice(row.salesTax, ['yes', 'no', 'unsure'] as const),
    extraStates: asChoice(row.extraStates, ['no', 'yes'] as const),
    extraStateList: text('extraStateList'),
    bank: asChoice(row.bank, ['no', 'yes'] as const),
  };
};

export const parseWillsTrustsAnswers = (value: unknown): WillsTrustsAnswers => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const row = value as Record<string, unknown>;
  const text = (key: string): string | undefined => {
    const raw = typeof row[key] === 'string' ? row[key].trim() : '';
    return raw || undefined;
  };
  return {
    plan: asChoice(row.plan, ['will', 'trust'] as const),
    state: text('state'),
    person: text('person'),
    capacity: asChoice(row.capacity, ['adult', 'minor'] as const),
    marital: asChoice(row.marital, ['single', 'married', 'widowed', 'divorced'] as const),
    children: asChoice(row.children, ['none', 'minors', 'adults'] as const),
    counsel: asChoice(row.counsel, ['retained', 'looking'] as const),
    stage: asChoice(row.stage, ['none', 'draft', 'signed'] as const),
  };
};

export type ComplianceConsoleNotice = {
  id: string;
  title: string;
  body: string;
  sourceLabel?: string;
  sourceUrl?: string;
};

export type ComplianceConsoleDefinition = {
  kind: ComplianceConsoleKind;
  entitlement: string;
  title: string;
  subtitle: string;
  storageKey: string;
  pillars: CompliancePillarDef[];
  notices?: ComplianceConsoleNotice[];
};

export type WebsiteSecurityHeaderTest = {
  id: string;
  label: string;
  pass: boolean;
  fix: string;
  core: boolean;
};

export type WebsiteCcpaCheck = {
  id: string;
  label: string;
  pass: boolean;
};

export type WebsiteScanCadence = 'off' | 'weekly' | 'daily';

export type WebsiteScanSchedule = {
  cadence: WebsiteScanCadence;
  nextDueAt?: string;
};

export type WebsiteComplianceLastScan = {
  final_url?: string;
  privacy_risk?: 'low' | 'medium' | 'high';
  ccpa_checks?: WebsiteCcpaCheck[];
  pages_crawled?: number;
  gpc_well_known?: boolean;
  gpc_well_known_url?: string;
  /** Public edge/hosting fingerprint from homepage headers (informational; agent reports its own env). */
  hosting?: {
    providers?: string[];
    evidence?: string[];
    managed_edge?: boolean;
    confidence?: number;
    note?: string;
  };
  security_header_tests?: WebsiteSecurityHeaderTest[];
  privacy_links?: string[];
  cookie_links?: string[];
  terms_links?: string[];
  dmca_links?: string[];
  do_not_sell_links?: string[];
  accessibility_links?: string[];
  consent_banner_heuristic?: boolean;
  preference_center_heuristic?: boolean;
  script_blocking_heuristic?: boolean;
  google_consent_mode_heuristic?: boolean;
  gpc_heuristic?: boolean;
  cmp_vendors?: string[];
  tracker_hosts?: string[];
  tracker_categories?: Record<string, string[]>;
  overlay_widgets?: string[];
  script_src_count?: number;
  policy_links_ok?: boolean;
  ip_links_ok?: boolean;
  link_count?: number;
  https_ok?: boolean;
  http_status?: number;
  http_ok?: boolean;
  viewport_present?: boolean;
  seo_title?: boolean;
  seo_description?: boolean;
  seo_h1_count?: number;
  contractor_identity?: {
    uei?: string[];
    cage?: string[];
    naics?: string[];
    psc?: string[];
  };
  security_headers_ok?: boolean;
  security_headers?: {
    hsts?: boolean;
    csp?: boolean;
    x_content_type_options?: boolean;
    referrer_policy?: boolean;
    x_frame_options?: boolean;
  };
  spf_present?: boolean;
  spf_aligned?: boolean;
  dmarc_present?: boolean;
  dmarc_policy?: string;
  dmarc_rua?: boolean;
  dkim_selectors?: string[];
  email_auth_ok?: boolean;
  checked_domain?: string;
  mail_hosts?: string[];
  blocklist_listed?: number;
  blocklist_checked?: number;
  scannedAt?: string;
};

export type WebsiteComplianceScanHistoryEntry = {
  scannedAt?: string;
  final_url?: string;
  policy_links_ok?: boolean;
  consent_banner_heuristic?: boolean;
  ip_links_ok?: boolean;
  cmp_vendors?: string[];
  tracker_hosts?: string[];
  tracker_categories?: Record<string, string[]>;
  overlay_widgets?: string[];
};

/** Turn a saved website name into a scan URL, or null when it is not a domain. */
export const websiteScanUrlFromName = (raw: string): string | null => {
  const value = String(raw || '').trim();
  if (!value) {
    return null;
  }
  try {
    if (/^https?:\/\//i.test(value)) {
      const parsed = new URL(value);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null;
      }
      if (parsed.pathname === '/' && !parsed.search && !parsed.hash) {
        return `${parsed.protocol}//${parsed.host}`;
      }
      return parsed.toString();
    }
  } catch {
    return null;
  }
  const host = value.replace(/\/+$/, '');
  if (/^localhost(?::\d+)?(?:\/.*)?$/i.test(host)) {
    return `http://${host}`;
  }
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(?::\d+)?(?:\/.*)?$/i.test(host)) {
    return null;
  }
  return `https://${host}`;
};

export const WEBSITE_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'website-compliance',
  entitlement: 'website-compliance',
  title: 'Website Compliance',
  subtitle:
    'Name the account and the website first. The homepage scan records whether the page answers (including a 504), HTTPS, security headers, a phone-width tag, a title and description, privacy/cookie/DMCA links, and email authentication (SPF/DKIM/DMARC). Then work the list: the site answers, it fits a phone, search basics, health data, personal data, no government files on the public site, privacy, accessibility, security, email, Zero Trust, AI/ML input guardrails when the site has a chat or assistant, a lint gate before deploy, IP, and consumer rules. Higher plans add monitoring and governance. The scan does not read private code and does not certify HIPAA.',
  storageKey: 'nextcompli.console.website-compliance.v1',
  notices: [
    {
      id: 'not-legal-advice',
      title: 'Checklist evidence, not legal advice',
      body:
        'Use this console to record owners, URLs, and review dates. It does not certify compliance or replace counsel. Map each item to the laws that actually apply to your audience and data flows.',
    },
    {
      id: 'website-law-baseline',
      title: 'Cover DMCA plus the other website-facing rules',
      body:
        'Baseline U.S./EU website obligations include DMCA 17 U.S.C. § 512, GDPR and CCPA/CPRA (plus other state privacy laws), COPPA if under-13 users are in scope, ADA/WCAG, FTC advertising and ROSCA/negative-option rules, E-SIGN clickwrap, CAN-SPAM, Gmail/Yahoo/Microsoft sender authentication (SPF/DKIM/DMARC), PCI DSS when you take cards, CIS / CISA Zero Trust hygiene for the cloud, containers, git repos, and public site, and — when the site exposes a chat or other generative AI — input and output guardrails so toxic, off-topic, or injection attempts do not reach the model unfiltered (OWASP Top 10 for LLM Applications). Local, state, and sector rules still need an applicability matrix.',
      sourceLabel: 'U.S. Copyright Office — DMCA § 512',
      sourceUrl: 'https://www.copyright.gov/512/',
    },
    {
      id: 'plan-coverage-ladder',
      title: 'Coverage deepens by subscription',
      body:
        'CompliCore is CIS Implementation Group 1 / CISA traditional-to-initial hygiene: SPF that lists the mail host, one DKIM key, DMARC at p=none with a report mailbox, an action note when mail needs a decision, HTTPS, headers, MFA, private git, no secrets in images, and a dated inventory of any public AI chat or assistant (or a note that there is none). CompliAdvantage adds CIS IG2 / CISA advanced: DMARC quarantine, one-click unsubscribe, complaint-rate watch, CIS Benchmark logging, protected branches, image/SBOM scans, live monitoring, and AI input rails that block toxic, political/off-topic, and injection prompts before the model. CompliComplete is CIS IG3 / CISA optimal: DMARC reject, BIMI, CSPM, signed images, git SSO and audit, embedded-consultant evidence reviews, and AI governance with evaluation of the guardrail layer.',
      sourceLabel: 'CIS Controls Implementation Groups',
      sourceUrl: 'https://www.cisecurity.org/controls/implementation-groups',
    },
    {
      id: 'cmp-competitive-baseline',
      title: 'CMP capabilities this console now tracks',
      body:
        'Cookiebot, CookieYes, Termly, iubenda, Osano, and similar CMPs sell cookie inventory, script blocking until consent, consent logs, Google Consent Mode v2, geo banners, and scheduled rescans. NextCompli homepage scan detects CMP vendors, tracker hosts by purpose (essential / advertising / analytics / performance / social), GPC/Do Not Sell and Limit Sensitive PI links, and overlay widgets. An accessibility overlay (UserWay/accessiBe) is not a WCAG substitute.',
      sourceLabel: 'Cookiebot cookie checker',
      sourceUrl: 'https://www.cookiebot.com/en/cookie-checker/',
    },
  ],
  pillars: [
    {
      id: 'public-site-health',
      label: 'Is the site up and safe to use',
      summary: 'The page answers, fits a phone, can be found in search, and does not hold health data, personal data, or government files it should not. The homepage scan records the status code, the phone-width tag, and the title and description. A person still looks at the page.',
      items: [
        calendarChecklistItem('website-compliance'),
        {
          id: 'site-answers-not-504',
          title: 'The page answers (not a 504)',
          action: 'Open the website. A working page loads. You do not see 504 Gateway Timeout or 502 Bad Gateway. The homepage scan records the status code. A code from 200 to 399 means the page answered.',
          evidenceHint: 'Scan status code, or a screenshot of the working page',
          actionSteps: [
            'Open the website address in a browser.',
            'A working page loads. You do not see “504 Gateway Timeout” or “502 Bad Gateway”.',
            'If you see 504, the server did not answer in time. Tell the person who hosts the site.',
            'Run the homepage scan. It writes the status code on this row. 200 to 399 means the page answered.',
          ],
        },
        {
          id: 'site-fits-a-phone',
          title: 'The page fits a phone',
          action: 'Open the site at phone width and on a wide screen. Words stay readable without sliding sideways, and buttons are big enough to tap. The scan flags a missing viewport tag. You still look with your eyes.',
          evidenceHint: 'Phone screenshot and desktop screenshot',
          dependsOn: ['site-answers-not-504'],
          dependencyNote: 'Check the layout after the page actually answers.',
          actionSteps: [
            'Open the site on a phone, or shrink the browser until it is about as wide as a phone.',
            'You can read the words without sliding sideways.',
            'Buttons are big enough to tap with a finger.',
            'Open the same page on a wide screen. The same words are still there.',
            'If the scan says the viewport tag is missing, ask the person who builds the site to add it. Then look again.',
          ],
        },
        {
          id: 'site-seo-basics',
          title: 'Search can find the page',
          action: 'Each page has a browser-tab title, one main heading, a one-sentence description, a sitemap, and a robots file. The scan marks a gap when the title, description, or single heading is missing. It does not rank the site.',
          evidenceHint: 'Homepage title, /sitemap.xml, and /robots.txt',
          dependsOn: ['site-answers-not-504'],
          dependencyNote: 'Search files are checked after the page answers.',
          sourceLabel: 'Google Search Central',
          sourceUrl: 'https://developers.google.com/search/docs/fundamentals/seo-starter-guide',
          actionSteps: [
            'Open the homepage. The browser tab names the site.',
            'The page has one main heading.',
            'Open /sitemap.xml and /robots.txt on the same site. Both should open.',
            'Each important page has a one-sentence description.',
            'The scan marks a gap when the title, description, or single heading is missing. It does not place you in Google.',
          ],
        },
        {
          id: 'site-hipaa-scope',
          title: 'Health data stays off ad tags',
          action: 'If this website does not collect health information, write “Not a health site” and mark it done. If it does, publish a Notice of Privacy Practices, and do not put ad or analytics tags on those pages unless the vendor signed a business associate agreement.',
          evidenceHint: '“Not a health site” note, or the notice URL plus the vendor agreement',
          dependsOn: ['privacy-policy-published'],
          dependencyNote: 'The privacy page is named before you decide whether health rules apply.',
          sourceLabel: 'HHS Notice of Privacy Practices',
          sourceUrl: 'https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/model-notices-privacy-practices/index.html',
          actionSteps: [
            'Ask: does this website collect health information, such as appointments, symptoms, insurance, or medical records?',
            'If no, write “Not a health site” and mark this done.',
            'If yes, publish a Notice of Privacy Practices and link it from the footer.',
            'Remove ad and analytics tags from any page that shows health information, unless that vendor signed a business associate agreement.',
            'Those forms use HTTPS and do not put health details in the web address.',
          ],
        },
        {
          id: 'site-pii-forms',
          title: 'Forms do not put personal data in the address',
          action: 'Contact, signup, and checkout forms stay on HTTPS. The address bar does not contain the name, email, or phone the visitor typed. Write down how long you keep the answers and who can see them.',
          evidenceHint: 'Screenshot of one form on HTTPS, with a blank address bar after submit',
          dependsOn: ['https-transport'],
          dependencyNote: 'Lock the lock icon (HTTPS) before you test the forms.',
          actionSteps: [
            'Open each form: contact, signup, and checkout.',
            'Type a test name and email. Look at the address bar. It must not contain that name or email.',
            'The address starts with https://.',
            'Write how long you keep the answers and who can see them.',
            'Save a screenshot of one form. Do not put a real Social Security number in the screenshot.',
          ],
        },
        {
          id: 'site-no-cui-on-public',
          title: 'No government files on the public site',
          action: 'This website is public. Do not upload contract files, system diagrams, or CUI here. Keep those in the government project. Write “Public site only” when nothing sensitive is hosted.',
          evidenceHint: '“Public site only” note, or the government-project location of the files',
          actionSteps: [
            'This website is for everyone on the internet.',
            'Do not upload contract files, government system diagrams, or CUI here.',
            'If a customer needs those files, keep them in the government project, not on this website.',
            'Write “Public site only” when nothing sensitive is hosted here.',
          ],
        },
      ],
    },
    {
      id: 'privacy-data-governance',
      label: 'Privacy & Data Governance',
      summary: 'Consent, disclosures, cookie/tracker inventory, Consent Mode, and data-rights handling stay transparent and regulator-ready under GDPR, CCPA/CPRA, COPPA, and similar rules.',
      items: [
        {
          id: 'privacy-policy-published',
          title: 'Privacy / Cookie / Terms published and linked',
          action: 'Confirm current Privacy Policy, Cookie Policy, and Terms are linked from the site footer and signup flows, with categories, purposes, and sharing described.',
          evidenceHint: 'URLs + last-reviewed date',
          sourceLabel: 'California CCPA',
          sourceUrl: 'https://oag.ca.gov/privacy/ccpa',
          docsLabel: 'GDPR Portal',
          docsUrl: 'https://gdpr.eu/',
          completeRequires: 'evidence',
          verifySteps: [
            {
              id: 'privacy-url',
              kind: 'proof',
              text: 'Keep the live privacy-policy URL and confirm the page names the data you collect, why, and who you share it with.',
              evidenceHint: 'Live privacy-policy URL',
            },
          ],
        },
        {
          id: 'cookie-consent-controls',
          title: 'Cookie / tracking consent controls',
          action: 'Verify opt-in or opt-out paths work before non-essential cookies fire; log consent choice. Honor Global Privacy Control / Do Not Sell or Share where required. Prefer a preference center over a banner-only accept/reject pair.',
          evidenceHint: 'Screenshot of banner + sample consent record',
          dependsOn: ['privacy-policy-published'],
          dependencyNote: 'Publish the privacy page before the cookie choice, so the banner can point at it.',
          sourceLabel: 'ICO cookies guidance',
          sourceUrl: 'https://ico.org.uk/for-organisations/direct-marketing-and-privacy-and-electronic-communications/guide-to-pecr/cookies-and-similar-technologies/',
        },
        {
          id: 'cookie-inventory-classified',
          title: 'Cookie / tracker inventory classified',
          action: 'Keep a categorized inventory of first- and third-party cookies and trackers (necessary, functional, analytics, advertising). Homepage scan lists detected hosts; confirm categories and providers after each rescan.',
          evidenceHint: 'Scan tracker list + cookie policy mapping',
          sourceLabel: 'Cookiebot cookie checker',
          sourceUrl: 'https://www.cookiebot.com/en/cookie-checker/',
        },
        {
          id: 'consent-proof-and-script-blocking',
          title: 'Consent logs and script blocking',
          action: 'Block non-essential scripts until consent. Store proof of consent (timestamp, version, choice, region). Re-consent when the cookie list or purposes change.',
          evidenceHint: 'Blocked-script sample + consent receipt',
          sourceLabel: 'Google Consent Mode v2',
          sourceUrl: 'https://developers.google.com/tag-platform/security/guides/consent',
        },
        {
          id: 'google-consent-mode',
          title: 'Google Consent Mode and geo banners',
          action: 'If Google tags or ads run, wire Consent Mode v2 (and IAB TCF where ads are in EU). Use geo-targeted banners so GDPR opt-in and CCPA opt-out are not one global default.',
          evidenceHint: 'Tag Manager consent config + region screenshot',
          sourceLabel: 'Google Consent Mode',
          sourceUrl: 'https://support.google.com/tagmanager/answer/13695607',
          completeRequires: 'evidence',
          verifySteps: [
            {
              id: 'gcm-scan',
              kind: 'do',
              text: 'Run the homepage scan and note whether Google tags or ads are present.',
              evidenceHint: 'Scan finding for Consent Mode',
            },
            {
              id: 'gcm-config',
              kind: 'proof',
              text: 'In Google Tag Manager, set Consent Mode so ad and analytics storage wait for a choice.',
              evidenceHint: 'Tag Manager consent screenshot',
            },
            {
              id: 'gcm-banner',
              kind: 'proof',
              text: 'Keep a screenshot of the cookie banner on the live site.',
              evidenceHint: 'Banner screenshot',
            },
            {
              id: 'gcm-pref',
              kind: 'proof',
              text: 'Confirm the preference center link lets a visitor change their choice later.',
              evidenceHint: 'Preference-center URL',
            },
            {
              id: 'gcm-rescan',
              kind: 'do',
              text: 'Rescan after the banner is live and confirm Consent Mode still shows on the finding.',
              evidenceHint: 'Second scan timestamp',
            },
          ],
        },
        {
          id: 'scheduled-rescans',
          title: 'Scheduled cookie / policy rescans',
          action: 'Choose weekly (CompliCore) or daily (CompliAdvantage) on the homepage scan. Each run is kept in scan history so a new tag cannot appear quietly.',
          evidenceHint: 'Cadence owner + last two scan timestamps',
        },
        {
          id: 'data-inventory',
          title: 'Website data collection inventory',
          action: 'Map forms, analytics, and third-party tags that collect personal data and note lawful basis / purpose, retention, and processors.',
          evidenceHint: 'Inventory spreadsheet or Confluence page',
        },
        {
          id: 'dsar-workflow',
          title: 'User-rights (DSAR) workflow',
          action: 'Confirm intake path for access, correction, deletion, portability, and opt-out requests with owner and SLA (GDPR / CCPA/CPRA and other in-scope state laws).',
          evidenceHint: 'Runbook URL + sample ticket',
          sourceLabel: 'CCPA consumer rights',
          sourceUrl: 'https://oag.ca.gov/privacy/ccpa',
          dependsOn: ['privacy-policy-published'],
          dependencyNote: 'California Do Not Sell and Limit Sensitive Personal Information stay blocked until the privacy policy is compliant.',
          completeRequires: 'evidence',
          verifySteps: [
            {
              id: 'do-not-sell',
              kind: 'proof',
              text: 'Confirm a Do Not Sell or Share My Personal Information link is on the site and works.',
              evidenceHint: 'Do Not Sell URL',
            },
            {
              id: 'limit-spi',
              kind: 'proof',
              text: 'Confirm a Limit the Use of My Sensitive Personal Information link is on the site and works.',
              evidenceHint: 'Limit Sensitive PI URL',
            },
          ],
        },
        {
          id: 'coppa-audience',
          title: 'COPPA / under-13 audience determination',
          action: 'Record whether the site is directed to children under 13 or knowingly collects their data. If yes, implement notice, parental consent, and an age-gate. If no, keep the N/A determination with audience evidence.',
          evidenceHint: 'Audience memo + age-gate screenshots or N/A sign-off',
          sourceLabel: 'FTC COPPA',
          sourceUrl: 'https://www.ftc.gov/legal-library/browse/rules/childrens-online-privacy-protection-rule-coppa',
        },
      ],
    },
    {
      id: 'accessibility-user-experience',
      label: 'Accessibility & User Experience',
      summary: 'Section 508 and WCAG 2.1 Level AA checks for the public pages. These checks are on every subscription. Record what you find. This page does not certify the site. An overlay widget is not a substitute.',
      items: [
        {
          id: 'keyboard-navigation',
          title: 'Keyboard, focus, and skip link',
          action: 'On the home page, signup, and checkout or consent: reach every control with the keyboard, leave every control, see where focus is, and use a skip link to the main content. WCAG 2.1 AA 2.1.1, 2.1.2, 2.4.1, 2.4.3, and 2.4.7. Section 508 uses this bar for web pages.',
          evidenceHint: 'Keyboard notes for each template',
          sourceLabel: 'WCAG 2.1',
          sourceUrl: 'https://www.w3.org/TR/WCAG21/',
        },
        {
          id: 'alt-text-contrast',
          title: 'Text alternatives, headings, and contrast',
          action: 'Give images, icons, and charts a text alternative. Use real headings in order. Body text contrast is at least 4.5:1, and large text and meaningful graphics at least 3:1. Do not use color as the only signal. WCAG 2.1 AA 1.1.1, 1.3.1, 1.4.1, 1.4.3, and 1.4.11.',
          evidenceHint: 'Page list, contrast readings, and gaps',
          sourceLabel: 'WCAG 2.1',
          sourceUrl: 'https://www.w3.org/TR/WCAG21/',
        },
        {
          id: 'form-labels',
          title: 'Form names, errors, and status messages',
          action: 'Every input has a visible label. Errors name the field and how to fix it. Legal, financial, and delete actions can be reviewed before they submit. Status messages are announced without moving focus. The control’s name, role, and value are available to a screen reader. WCAG 2.1 AA 3.3.1, 3.3.2, 3.3.3, 3.3.4, 4.1.2, and 4.1.3.',
          evidenceHint: 'Form notes, including one error and one success message',
          sourceLabel: 'WCAG 2.1',
          sourceUrl: 'https://www.w3.org/TR/WCAG21/',
        },
        {
          id: 'wcag-media-captions',
          title: 'Captions and transcripts',
          action: 'If a page has audio or video, add captions for speech and a transcript. Live audio has captions. If the page has no audio or video, write “No media” and mark this done. WCAG 2.1 AA 1.2.1 through 1.2.5.',
          evidenceHint: 'Caption or transcript link, or a No media note',
          sourceLabel: 'WCAG 2.1',
          sourceUrl: 'https://www.w3.org/TR/WCAG21/',
        },
        {
          id: 'wcag-reflow-zoom',
          title: 'Zoom, reflow, and text spacing',
          action: 'At 200% zoom the words stay readable. At 320 CSS pixels wide the page reflows without a sideways scroll for the words. Paragraph spacing, line height, and letter spacing can increase without cutting off text. WCAG 2.1 AA 1.4.4, 1.4.10, and 1.4.12.',
          evidenceHint: 'Screenshots at 200% and at phone width',
          sourceLabel: 'WCAG 2.1',
          sourceUrl: 'https://www.w3.org/TR/WCAG21/',
        },
        {
          id: 'wcag-motion-timing',
          title: 'Timing, motion, and hover content',
          action: 'The visitor can pause, stop, or hide anything that moves, blinks, or auto-updates. Nothing flashes more than three times in one second. Content that opens on hover or focus can be dismissed, hovered, and kept open. If a time limit exists, the visitor can turn it off, adjust it, or extend it. WCAG 2.1 AA 1.4.13, 2.2.1, 2.2.2, and 2.3.1.',
          evidenceHint: 'Notes for carousels, banners, and tooltips, or N/A',
          sourceLabel: 'WCAG 2.1',
          sourceUrl: 'https://www.w3.org/TR/WCAG21/',
        },
        {
          id: 'wcag-language-navigation',
          title: 'Page language, links, and consistent navigation',
          action: 'The page names its language, and a passage in another language is marked. Each link’s purpose is clear from its text. The same navigation and components appear in the same order. There is more than one way to find a page. WCAG 2.1 AA 2.4.4, 2.4.5, 2.4.6, 3.1.1, 3.1.2, 3.2.3, and 3.2.4.',
          evidenceHint: 'Language tag, sample links, and nav notes',
          sourceLabel: 'WCAG 2.1',
          sourceUrl: 'https://www.w3.org/TR/WCAG21/',
        },
        {
          id: 'dark-pattern-review',
          title: 'Consent / critical-flow dark-pattern review',
          action: 'Confirm consent and cancellation flows are clear; no misleading defaults or obstructed opt-outs (FTC dark-pattern / ROSCA overlap).',
          evidenceHint: 'UX review checklist sign-off',
          sourceLabel: 'FTC negative-option / ROSCA',
          sourceUrl: 'https://www.ftc.gov/legal-library/browse/rules/negative-option-rule',
        },
        {
          id: 'accessibility-statement-published',
          title: 'Accessibility statement published',
          action: 'Publish a current accessibility statement that names WCAG 2.1 Level AA, the known gaps, and a contact. The statement is a page, not an overlay screenshot.',
          evidenceHint: 'Statement URL + last-reviewed date',
          sourceLabel: 'W3C accessibility statement',
          sourceUrl: 'https://www.w3.org/WAI/planning/statements/',
        },
        {
          id: 'overlay-not-wcag-substitute',
          title: 'Overlay widgets are not WCAG compliance',
          action: 'If UserWay, accessiBe, or similar overlays are installed, record them as an assistive aid only. Keep the source-code checks on this list. Do not mark this item reviewed solely because an overlay is present.',
          evidenceHint: 'Overlay vendor + source-code check date, or N/A',
          sourceLabel: 'ADA.gov web guidance',
          sourceUrl: 'https://www.ada.gov/resources/web-guidance/',
        },
        {
          id: 'wcag-22-targets-and-focus',
          title: 'Phone layout, target size, and a non-drag path',
          action: 'At a phone width and at 200% zoom, the page reflows in one direction. Focused controls stay on screen. Buttons and links are at least 24 by 24 pixels, with room between them. Anything you can drag also has up and down buttons or another click path. Check the home page, a form, and a filter. WCAG 2.2 adds 2.4.11, 2.5.7, and 2.5.8 on top of the WCAG 2.1 checks above. Section 508 still cites the older bar. Meeting 2.2 is the practice. This row does not certify the site.',
          evidenceHint: 'Phone screenshot, zoom note, and the click alternative for any drag control',
          sourceLabel: 'WCAG 2.2',
          sourceUrl: 'https://www.w3.org/TR/WCAG22/',
        },
        {
          id: 'framework-applicability',
          title: 'Which security and privacy frameworks apply',
          action: 'Write which of these apply to this site, and which do not: SOC 2, ISO 27001, ISO 27701, HIPAA, GDPR, CCPA/CPRA, PCI DSS, Section 508, WCAG, and EN 301 549. A marketing site with no health data, no card numbers, and no European customers is not all of them. Vanta and similar tools monitor many of these for a company. This row is the applicability note for the public site. It does not certify any framework and it does not replace those tools.',
          evidenceHint: 'Dated note: in scope, out of scope, and why',
          priority: 'recommended',
          sourceLabel: 'Vanta frameworks',
          sourceUrl: 'https://www.vanta.com/products/automated-compliance',
        },
        {
          id: 'section-508-conformance-record',
          title: 'Section 508 record against WCAG 2.1 AA',
          action: 'After the checks above, write the pages you opened, the gaps still open, and who to contact. Section 508 uses WCAG 2.1 Level AA for web content. If a federal buyer asks for a VPAT or an ACR, keep that file with this note. Do not write “conformant” while a gap on those pages is still open. This check is on every subscription. NextCompli does not certify the site.',
          evidenceHint: 'Page list, open gaps, contact, and a VPAT or ACR only if a buyer asked',
          dependsOn: ['keyboard-navigation', 'alt-text-contrast', 'form-labels', 'wcag-media-captions', 'wcag-reflow-zoom', 'wcag-motion-timing', 'wcag-language-navigation', 'accessibility-statement-published'],
          dependencyNote: 'Finish the page checks and publish the accessibility statement before you write the Section 508 record.',
          sourceLabel: 'Section 508',
          sourceUrl: 'https://www.section508.gov/develop/software-websites/',
        },
      ],
    },
    {
      id: 'security-risk-protection',
      label: 'Security & Risk Protection',
      summary: 'Public-website transport, headers, patching, incident contact, and PCI. CompliAdvantage adds a WAF and recurring scans; CompliComplete adds ASVS-depth testing.',
      items: [
        {
          id: 'https-transport',
          title: 'HTTPS on public pages and critical endpoints',
          action: 'Confirm TLS 1.2+ on apex/www and that HTTP redirects to HTTPS; check mixed-content warnings. Homepage scan records whether the final URL is HTTPS.',
          evidenceHint: 'URL list + TLS expiry',
          sourceLabel: 'OWASP ASVS V12',
          sourceUrl: 'https://owasp.org/www-project-application-security-verification-standard/',
        },
        {
          id: 'security-headers',
          title: 'Security headers baseline',
          action: 'Review HSTS, CSP (or a dated plan), X-Content-Type-Options, and Referrer-Policy on primary origins. Homepage scan flags missing headers.',
          dependsOn: ['https-transport'],
          dependencyNote: 'Turn on HTTPS before the extra security headers. HSTS only works on HTTPS.',
          evidenceHint: 'Header scan output',
          sourceLabel: 'OWASP Secure Headers',
          sourceUrl: 'https://owasp.org/www-project-secure-headers/',
        },
        {
          id: 'dependency-patch-cadence',
          title: 'Vulnerability / patch cadence',
          action: 'Confirm owner and cadence for dependency and CMS/plugin patching; note last completed cycle.',
          evidenceHint: 'Last patch date + owner',
          sourceLabel: 'OWASP Top 10',
          sourceUrl: 'https://owasp.org/www-project-top-ten/',
        },
        {
          id: 'incident-contact',
          title: 'Public security / incident contact',
          action: 'Publish or internalize a security contact and escalation path for website incidents.',
          evidenceHint: 'Contact + escalation doc',
        },
        {
          id: 'pci-if-payments',
          title: 'PCI DSS scope if cards are accepted',
          action: 'If the site takes payment cards, record PCI DSS SAQ type, who stores PAN, and that checkout uses a compliant processor. If no cards, record N/A.',
          evidenceHint: 'SAQ type + processor attestation or N/A note',
          sourceLabel: 'PCI DSS',
          sourceUrl: 'https://www.pcisecuritystandards.org/standards/pci-dss/',
        },
        {
          id: 'waf-recurring-scans',
          title: 'WAF and recurring website scans',
          action: 'Put a WAF or equivalent edge filter in front of production. Schedule header/TLS/vulnerability scans and route findings into the CompliAdvantage alert workflow.',
          evidenceHint: 'WAF name + last scan job + alert owner',
          planMin: 'advanced',
          sourceLabel: 'CIS Control 16',
          sourceUrl: 'https://www.cisecurity.org/controls/v8',
        },
        {
          id: 'asvs-depth-review',
          title: 'ASVS-depth application review',
          action: 'Run a scoped application review against OWASP ASVS Level 2 or 3 (authn, session, access control, secrets). File the report and remediations in the audit workspace with an embedded consultant.',
          evidenceHint: 'ASVS report + POA&M-style remediations',
          planMin: 'enterprise',
          sourceLabel: 'OWASP ASVS',
          sourceUrl: 'https://owasp.org/www-project-application-security-verification-standard/',
        },
      ],
    },
    {
      id: 'email-authenticity-deliverability',
      label: 'Email Authenticity & Deliverability',
      summary: 'Set up the mailbox, then SPF, DKIM, and DMARC in that order. The DKIM key is one record. Splitting it, or generating it and never starting authentication, is why mail has to be fixed again.',
      items: [
        {
          id: 'email-mailbox-mx',
          title: 'Mailbox and mail exchange on the company domain',
          action: 'Create the company mailbox and point the domain’s mail exchange at that host. Edit DNS only in the panel the domain’s nameservers use. Sign the phone and mail app in with the real login, not a send-as alias.',
          evidenceHint: 'Mail-exchange record and a login that can send',
          actionSteps: [
            'Find the nameservers for the domain. Open that DNS panel. A record saved anywhere else does nothing.',
            'Set the mail exchange to the mailbox host. For Google Workspace that is priority 1 and host smtp.google.com.',
            'Remove an old mail host, such as WorkMail, so new mail does not split.',
            'Create the company mailbox. On a phone or mail app, sign in with that login. A send-as alias is not the account.',
          ],
          sourceLabel: 'Google Workspace MX records',
          sourceUrl: 'https://support.google.com/a/answer/140034',
        },
        {
          id: 'email-spf-published',
          title: 'SPF published for every sending domain',
          action: 'Publish one SPF text record per sending domain that lists every service that sends as that domain. If the mailbox is Google, the record must include _spf.google.com even when Amazon or another vendor also sends. A record that only names the other vendor fails human mail. Keep DNS lookups under 10. The homepage scan reads the domain text record and compares it to the mail exchange.',
          evidenceHint: 'One SPF text record plus the list of senders',
          dependsOn: ['email-mailbox-mx'],
          actionSteps: [
            'List every system that sends as the domain: the human mailbox, and any app or marketing tool.',
            'Publish one text record on the domain itself. It starts with v=spf1. Do not publish a second SPF record. Two SPF records make both fail.',
            'If the mailbox is Google, include _spf.google.com. A record that only names Amazon or another sender makes the human mail fail.',
            'Include each other sender the same way. Keep the lookup count under 10. End with ~all.',
          ],
          sourceLabel: 'Google Workspace SPF',
          sourceUrl: 'https://support.google.com/a/answer/33786',
        },
        {
          id: 'email-dkim-signing',
          title: 'DKIM signing on the mailbox',
          action: 'Generate the DKIM key in the mail admin, publish it as one DNS text record, then start authentication. Two text records at the same name do not join, and the signature fails. Generating the key without starting authentication does not sign mail. The scan checks common names (google, selector1, s1, default). It does not click Start authentication for you.',
          evidenceHint: 'One DKIM text record plus a signed message source',
          dependsOn: ['email-spf-published'],
          actionSteps: [
            'In Google Workspace open Admin, then Apps, Google Workspace, Gmail, Authenticate email. Pick the company domain. Click Generate new record.',
            'Write down the host name. For Google it is google._domainkey. The value is one long key.',
            'In the DNS panel the nameservers use, add one text record at that host name. Several quoted pieces inside one record join. Two separate text records with the same name do not join, and DKIM fails. Do not add a second record.',
            'If another system also sends as this domain, such as Amazon SES, add the keys that system shows, under the names it shows. Do not paste those keys onto google._domainkey.',
            'Go back to Authenticate email and click Start authentication. Wait until it says it is authenticating. DNS can take an hour. A generated key that was never started does not sign mail.',
            'Microsoft 365 is different: publish the two alias records the admin center shows (selector1 and selector2), then turn signing on. Do not invent a Google text record for a Microsoft mailbox.',
          ],
          sourceLabel: 'Turn on DKIM in Google Workspace',
          sourceUrl: 'https://support.google.com/a/answer/174124',
        },
        {
          id: 'email-dmarc-monitor',
          title: 'DMARC at p=none with aggregate reporting',
          action: 'Publish one _dmarc text record at p=none with a report mailbox someone will see. Gmail, Yahoo, and Microsoft treat mail with no DMARC policy as easier to file as spam even when SPF and DKIM pass. Do not move to quarantine or reject until a test message shows SPF and DKIM pass. The scan marks this done only when both the policy and the report mailbox are published.',
          evidenceHint: 'DMARC record plus a report mailbox a person reads',
          dependsOn: ['email-dkim-signing'],
          actionSteps: [
            'In the same DNS panel, add one text record named _dmarc.',
            'Start with v=DMARC1; p=none; rua=mailto: and a mailbox a person already reads.',
            'Do not set quarantine or reject on the first day. Wait until a test message shows SPF and DKIM pass.',
          ],
          sourceLabel: 'Google Workspace DMARC',
          sourceUrl: 'https://support.google.com/a/answer/2466580',
        },
        {
          id: 'email-auth-check',
          title: 'Read a sent message and confirm SPF and DKIM passed',
          action: 'Send from the company address to a mailbox you can open. Show the original. Both SPF and DKIM must pass for the company domain. A fail means the DNS is in the wrong panel, the DKIM key was split into two records, or authentication was never started.',
          evidenceHint: 'Message original showing spf=pass and dkim=pass',
          dependsOn: ['email-dmarc-monitor'],
          actionSteps: [
            'Send a message from the company address to a Gmail account you can open.',
            'Open the message, then Show original.',
            'Find spf=pass and dkim=pass for the company domain. fail means go back: wrong DNS panel, DKIM split into two records, or Start authentication was never clicked.',
            'Save that original. That is the proof. Do not mark the earlier steps done from a DNS screenshot alone.',
          ],
          sourceLabel: 'Gmail email sender guidelines',
          sourceUrl: 'https://support.google.com/mail/answer/81126',
        },
        {
          id: 'email-inbox-action-note',
          title: 'Action note for mail that needs a decision',
          action: 'Do not make an operator live in the sending mailbox. On a schedule that still runs when their laptop is closed: trash mail that is clearly spam, mark ordinary mail read, and send one plain-language note to a mailbox they already check when a message needs a decision (security, payment, or legal), including what to do. Keep DMARC reports in that same flow. Use fixed rules. Do not send the mailbox to a model.',
          evidenceHint: 'One sample action note plus the rule that sent spam to trash',
          dependsOn: ['email-auth-check'],
          sourceLabel: 'Gmail email sender guidelines',
          sourceUrl: 'https://support.google.com/mail/answer/81126',
        },
        {
          id: 'email-dmarc-quarantine',
          title: 'DMARC quarantine and complaint-rate watch',
          action: 'Move DMARC to p=quarantine after SPF/DKIM alignment is clean. Keep Gmail spam-complaint rate under 0.3% (aim under 0.1%). Route authentication failures into CompliAdvantage alerts.',
          evidenceHint: 'p=quarantine record + complaint-rate screenshot',
          planMin: 'advanced',
          dependsOn: ['email-auth-check'],
          sourceLabel: 'Gmail email sender guidelines',
          sourceUrl: 'https://support.google.com/mail/answer/81126',
        },
        {
          id: 'email-one-click-unsub',
          title: 'One-click unsubscribe and sending subdomains',
          action: 'Marketing mail must support RFC 8058 List-Unsubscribe one-click (required above 5,000/day to Gmail). Separate marketing vs transactional on dedicated subdomains so a campaign cannot take down password-reset mail.',
          evidenceHint: 'List-Unsubscribe header sample + subdomain map',
          planMin: 'advanced',
          sourceLabel: 'RFC 8058',
          sourceUrl: 'https://www.rfc-editor.org/rfc/rfc8058',
        },
        {
          id: 'email-dmarc-reject-bimi',
          title: 'DMARC reject and BIMI brand mark',
          action: 'Enforce p=reject on production sending domains. For consumer brands, publish BIMI with a Verified Mark Certificate after DMARC is enforced. Review rua/ruf across every domain with an embedded consultant.',
          evidenceHint: 'p=reject record + BIMI SVG/VMC or documented N/A',
          planMin: 'enterprise',
          sourceLabel: 'BIMI Group',
          sourceUrl: 'https://bimigroup.org/',
        },
      ],
    },
    {
      id: 'zero-trust-system-hardening',
      label: 'Zero Trust & System Hardening',
      summary: 'Never trust cloud, containers, websites, or git by default. CompliCore is essential hygiene; CompliAdvantage monitors IG2 controls; CompliComplete governs IG3 / CISA optimal.',
      items: [
        {
          id: 'zt-identity-mfa',
          title: 'MFA on cloud, git, and website admin',
          action: 'Require MFA on the cloud payer/root, website CMS/hosting, and git org. Disable legacy/basic auth. This is the first CISA Zero Trust identity function (verify explicitly).',
          evidenceHint: 'IdP MFA policy screenshot + exception list',
          actionSteps: [
            'Open the cloud account, the website host, and the git organization.',
            'Turn on MFA for every person who can change the site.',
            'Turn off password-only login.',
            'Save a screenshot of the MFA setting. Do not paste passwords here.',
          ],
          sourceLabel: 'CISA Zero Trust Maturity Model',
          sourceUrl: 'https://www.cisa.gov/zero-trust-maturity-model',
          accessLabel: 'Website / cloud admin',
          credentials: 'operator',
          portalId: 'cms',
        },
        {
          id: 'cloud-account-inventory',
          title: 'Cloud and website hosting inventory',
          action: 'List every cloud account, region, and public website origin that serves this customer (AWS, Azure, GCP, Vercel, DNS). Name an owner. CIS Control 1 starts with knowing the assets.',
          evidenceHint: 'Inventory sheet + owner + last review date',
          sourceLabel: 'CIS Controls Implementation Groups',
          sourceUrl: 'https://www.cisecurity.org/controls/implementation-groups',
          credentials: 'none',
        },
        {
          id: 'git-2fa-private-no-secrets',
          title: 'Git: 2FA, private repos, no secrets in history',
          action: 'Enforce 2FA on the git org. Keep product repos private unless they are intentionally public. Turn on secret scanning; rotate anything already committed. Record the org URL.',
          evidenceHint: 'Org 2FA setting + secret-scan status',
          sourceLabel: 'OpenSSF SCM best practices',
          sourceUrl: 'https://github.com/ossf/wg-best-practices-os-developers/blob/main/docs/SCM-BestPractices/README.md',
          accessLabel: 'Git organization',
          accessUrl: 'https://github.com',
          credentials: 'operator',
          portalId: 'github',
        },
        {
          id: 'code-lint-before-deploy',
          title: 'The linter must pass before a deploy',
          action: 'Turn on the project linter so a failed check stops the deploy. Save the date of the last green run. The homepage scan cannot read private code.',
          evidenceHint: 'Date of the last green lint run and the tool name',
          dependsOn: ['git-2fa-private-no-secrets'],
          dependencyNote: 'Lock the repository (2FA, private, no secrets) before you trust the lint gate.',
          actionSteps: [
            'Open the code repository for this website.',
            'Turn on the linter so a failed check stops the deploy.',
            'Run it. Fix the red lines. A green run is the proof.',
            'Write the date of that green run here. The homepage scan cannot see private code.',
          ],
        },
        {
          id: 'container-no-secrets-nonroot',
          title: 'Containers: no secrets in the image, non-root user',
          action: 'If the site ships containers, confirm images have no embedded passwords/tokens (DoD container guide 2.18) and do not run as root. If you do not use containers, file N/A with the hosting model.',
          evidenceHint: 'Dockerfile USER line + vault/secret-store note or N/A',
          sourceLabel: 'DoD container image guide',
          sourceUrl: 'https://dl.dod.cyber.mil/wp-content/uploads/devsecops/pdf/DevSecOps_Enterprise_Container_Image_Creation_and_Deployment_Guide_2.6-Public-Release.pdf',
        },
        {
          id: 'zt-cloud-cis-logging',
          title: 'Cloud CIS Benchmark and audit logs',
          action: 'Run a CIS Benchmark (or equivalent) against the production cloud account. Keep CloudTrail / Activity Log / Cloud Audit Logs immutable and alert on IAM and public-storage changes.',
          evidenceHint: 'Benchmark report + log-destination proof',
          planMin: 'advanced',
          sourceLabel: 'CIS Benchmarks',
          sourceUrl: 'https://www.cisecurity.org/cis-benchmarks',
        },
        {
          id: 'git-protected-secret-scan',
          title: 'Git: protected branches and push protection',
          action: 'Require pull-request review on default branches, CODEOWNERS where needed, and secret scanning with push protection so credentials cannot land on main.',
          evidenceHint: 'Branch-protection screenshot + secret-scan enablement',
          planMin: 'advanced',
          sourceLabel: 'OpenSSF SCM best practices',
          sourceUrl: 'https://github.com/ossf/wg-best-practices-os-developers/blob/main/docs/SCM-BestPractices/README.md',
        },
        {
          id: 'container-image-scan-sbom',
          title: 'Container image scan and SBOM',
          action: 'Scan images in CI for known vulnerabilities (Trivy/Grype/equivalent). Produce an SBOM per release. Block deploys on unfixed criticals per policy.',
          evidenceHint: 'CI scan log + SBOM artifact URL',
          planMin: 'advanced',
          sourceLabel: 'CIS Control 16.7',
          sourceUrl: 'https://www.cisecurity.org/controls/v8',
        },
        {
          id: 'cloud-cspm-workload-identity',
          title: 'CSPM, workload identity, and network segmentation',
          action: 'Run continuous CSPM. Replace long-lived cloud keys with workload identity. Segment production networks (no flat VPC). Map to CISA Zero Trust Networks + Applications pillars.',
          evidenceHint: 'CSPM dashboard + workload-identity example + network diagram',
          planMin: 'enterprise',
          sourceLabel: 'CISA Zero Trust Maturity Model',
          sourceUrl: 'https://www.cisa.gov/zero-trust-maturity-model',
        },
        {
          id: 'supply-chain-signing-sso',
          title: 'Signed images, git SSO, and immutable audit',
          action: 'Sign container images and verify at admission. Require SSO/SAML on git with SCIM offboarding. Keep immutable git and CI audit logs. Embedded consultants review quarterly against CIS IG3.',
          evidenceHint: 'Admission policy + IdP SSO + last quarterly review',
          planMin: 'enterprise',
          sourceLabel: 'CISA Zero Trust Maturity Model',
          sourceUrl: 'https://www.cisa.gov/zero-trust-maturity-model',
        },
      ],
    },
    {
      id: 'ai-ml-zero-trust-guardrails',
      label: 'AI/ML Zero Trust & Guardrails',
      summary:
        'Treat every public chat, assistant, or generative feature as untrusted input and untrusted model output. Intercept toxic, political/off-topic, and injection prompts before they reach the LLM. This pillar records owners and proof. It is not an ISO/IEC 42001 certificate.',
      items: [
        {
          id: 'web-ai-feature-inventory',
          title: 'Inventory public AI chats and assistants',
          action:
            'List every generative AI feature on this website (support chat, search assistant, form helper, voice, image). For each, write the vendor or model family, which pages expose it, whose account pays for it, and who can turn it off. If the site has no generative AI, file a dated note that says so. Do not paste customer prompts here.',
          evidenceHint: 'Inventory rows (or dated “no public AI” note) with owner and off-switch',
          actionSteps: [
            'Walk the public site and note every chat widget, assistant, or “ask AI” control.',
            'For each one, write the product name or model family and the page URL.',
            'Name the person who can disable it.',
            'If there is no generative AI, save a dated note that says none.',
          ],
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'web-ai-input-rails',
          title: 'Block unsafe prompts before they hit the LLM',
          action:
            'Put an input rail in front of every public generative feature so the user message is checked before the main model runs. Block or rewrite toxic content, clear prompt-injection attempts, and requests outside your allowed-topic list. Acceptable open-source stacks include NVIDIA NeMo Guardrails input rails with Meta Llama Guard (or Llama Prompt Guard) for safety categories, or an equivalent hosted filter. Static regex alone is not enough for CompliAdvantage. Record which layer runs first and one blocked example (no private text).',
          evidenceHint: 'Architecture note naming the input rail + one anonymized blocked sample',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['web-ai-feature-inventory'],
          dependencyNote: 'Know which public AI features exist before you prove the input rail.',
          actionSteps: [
            'Confirm every public AI path goes through a pre-LLM filter (not straight to the model API).',
            'Turn on toxicity / unsafe-category checks (Llama Guard, NeMo Guardrails, or equivalent).',
            'Turn on prompt-injection detection (rules plus a classifier such as Llama Prompt Guard or equivalent).',
            'Save a short note of the product names and one blocked sample with private text removed.',
          ],
          sourceLabel: 'NVIDIA NeMo Guardrails',
          sourceUrl: 'https://github.com/NVIDIA-NeMo/Guardrails',
          docsLabel: 'OWASP Top 10 for LLM Applications',
          docsUrl: 'https://genai.owasp.org/llm-top-10/',
        },
        {
          id: 'web-ai-topic-boundary',
          title: 'Political and off-topic requests stay out of scope',
          action:
            'Publish an allowed-topic list for each public assistant (for example: product help, pricing, account how-to). Refuse politics, religion, medical advice, legal advice, and other off-topic asks with a fixed redirect message. Topic-control models (for example NeMo Topic Control) or an equivalent classifier are fine. The refusal must happen in the input rail, not after a long model answer.',
          evidenceHint: 'Allowed-topic list + sample off-topic refusal (no private text)',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['web-ai-input-rails'],
          dependencyNote: 'The input rail must exist before you prove topic refusal.',
          sourceLabel: 'NeMo Topic Control',
          sourceUrl: 'https://docs.nvidia.com/nemo/guardrails/latest/',
        },
        {
          id: 'web-ai-output-rails',
          title: 'Filter model output before it reaches the visitor',
          action:
            'After the model answers, run an output rail that blocks private data leakage, toxic language, and instructions that would push a visitor to do something harmful. Prefer the same safety stack as the input rail (Llama Guard check output in NeMo Guardrails, or equivalent). Log refusals. A person still owns high-risk replies.',
          evidenceHint: 'Output-filter name + one blocked or rewritten sample',
          planMin: 'advanced',
          priority: 'recommended',
          dependsOn: ['web-ai-input-rails'],
          dependencyNote: 'Match the output rail to the same public AI paths as the input rail.',
          sourceLabel: 'OWASP Top 10 for LLM Applications',
          sourceUrl: 'https://genai.owasp.org/llm-top-10/',
        },
        {
          id: 'web-ai-governance-eval',
          title: 'Govern the AI path: owner, eval, and never trust alone',
          action:
            'Name an owner for each public AI feature. Keep a short acceptable-use rule. Once a quarter (or before a major model change), run a small refusal test set: toxic, political/off-topic, and “ignore previous instructions” samples. Record pass/fail and latency. Zero Trust means the model is never trusted alone — authentication, least privilege on tools, and human escalation stay on. Cross-check the AI Compliance console for company-wide inventory.',
          evidenceHint: 'Owner + dated eval note + link or pointer to AI Compliance inventory',
          planMin: 'enterprise',
          priority: 'recommended',
          dependsOn: ['web-ai-input-rails', 'web-ai-topic-boundary'],
          dependencyNote: 'Prove input and topic rails before the quarterly eval.',
          docsLabel: 'Open AI Compliance',
          docsUrl: '/console/ai-compliance',
          sourceLabel: 'CISA Zero Trust Maturity Model',
          sourceUrl: 'https://www.cisa.gov/zero-trust-maturity-model',
        },
      ],
    },
    {
      id: 'intellectual-property-dmca',
      label: 'Intellectual Property & DMCA',
      summary: 'Copyright notices, DMCA § 512 designated-agent and takedown process, and third-party license attribution.',
      items: [
        {
          id: 'copyright-notice',
          title: 'Copyright and legal notices published',
          action: 'Publish a current copyright notice and ownership statement on public pages (footer or legal). Keep year and rights-holder accurate.',
          evidenceHint: 'Footer screenshot + last-reviewed date',
          sourceLabel: 'U.S. Copyright Office',
          sourceUrl: 'https://www.copyright.gov/',
        },
        {
          id: 'dmca-designated-agent',
          title: 'DMCA designated agent registered',
          action: 'Register a designated agent with the U.S. Copyright Office and publish the agent name, postal address, phone, and email on the site (17 U.S.C. § 512).',
          evidenceHint: 'copyright.gov directory listing + on-site agent details',
          sourceLabel: 'DMCA designated agent directory',
          sourceUrl: 'https://www.copyright.gov/dmca-directory/',
          docsLabel: '17 U.S.C. § 512',
          docsUrl: 'https://www.copyright.gov/512/',
        },
        {
          id: 'dmca-intake-workflow',
          title: 'DMCA notice and counter-notice workflow',
          action: 'Publish how to send a § 512(c) takedown notice and a counter-notice. Name the owner, response SLA, and where the case log lives. Tabletop the process at least annually.',
          evidenceHint: 'DMCA page URL + runbook + last tabletop date',
          sourceLabel: 'Copyright Office — DMCA § 512',
          sourceUrl: 'https://www.copyright.gov/512/',
        },
        {
          id: 'repeat-infringer-oss',
          title: 'Repeat-infringer policy and OSS attribution',
          action: 'Adopt a repeat-infringer policy for user-generated or hosted content. Inventory shipped open-source licenses and keep required attribution/notices with the product.',
          evidenceHint: 'Policy URL + SBOM / attribution page',
          sourceLabel: 'U.S. Copyright Office — DMCA',
          sourceUrl: 'https://www.copyright.gov/512/',
        },
      ],
    },
    {
      id: 'marketing-terms-consumer-rules',
      label: 'Marketing, Terms & Consumer Rules',
      summary: 'Clickwrap enforceability, truthful advertising, auto-renewal/cancellation, email, and acceptable-use boundaries.',
      items: [
        {
          id: 'clickwrap-assent',
          title: 'Clickwrap / E-SIGN terms assent',
          action: 'Require affirmative assent to current Terms before signup. Store account, timestamp, and terms version so electronic records stay reproducible (15 U.S.C. § 7001).',
          evidenceHint: 'Signup screenshot + sample acceptance record',
          sourceLabel: '15 U.S.C. § 7001 (E-SIGN)',
          sourceUrl: 'https://www.law.cornell.edu/uscode/text/15/7001',
        },
        {
          id: 'claims-substantiation',
          title: 'FTC advertising and endorsement controls',
          action: 'Substantiate public marketing claims before publish. Disclose material connections on testimonials (16 C.F.R. Part 255). Keep a claim-to-evidence register.',
          evidenceHint: 'Claim register + last legal review date',
          sourceLabel: 'FTC Advertising and Marketing',
          sourceUrl: 'https://www.ftc.gov/business-guidance/advertising-marketing',
          docsLabel: 'FTC endorsement guides',
          docsUrl: 'https://www.ftc.gov/business-guidance/advertising-marketing/endorsements-influencers-reviews',
        },
        {
          id: 'billing-negative-option',
          title: 'Auto-renewal, cancellation, and total-cost disclosures',
          action: 'Show price, renewal, and a simple cancel path before collecting payment (ROSCA / 16 C.F.R. Part 425). Match checkout copy to the Terms billing section.',
          evidenceHint: 'Checkout + cancel-flow screenshots',
          sourceLabel: 'FTC Negative Option Rule',
          sourceUrl: 'https://www.ftc.gov/legal-library/browse/rules/negative-option-rule',
        },
        {
          id: 'canspam-aup',
          title: 'CAN-SPAM, AUP, and prohibited-use boundaries',
          action: 'Commercial email must identify the sender, include a working unsubscribe, and honor opt-outs. Publish acceptable-use / prohibited-use rules and an escalation path. Pair this legal item with the Email Authenticity pillar (SPF/DKIM/DMARC) so mail is both lawful and delivered.',
          evidenceHint: 'Email footer sample + AUP URL + last opt-out test',
          sourceLabel: 'FTC CAN-SPAM',
          sourceUrl: 'https://www.ftc.gov/business-guidance/resources/can-spam-act-compliance-guide-business',
        },
      ],
    },
  ],
};

export const DOD_CONTRACTS_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'dod-contracts-compliance',
  entitlement: 'dod-contracts-compliance',
  title: 'Government Compliance',
  subtitle:
    'Federal, state, county, and city vendor work in one binder. Classify the company, project, and contract. SOC 2, CMMC, and a GSA Schedule share one evidence library. None of them substitutes for the others. CompliCore covers FCI / CMMC Level 1 plus the vendor lists a Texas IT consultancy needs. CompliAdvantage adds the Level 2 tracker and DFARS 7012 playbook without storing CUI. CompliComplete adds SSP/POA&M, GCC High/PreVeil pointers, and an optional CUI enclave. Not a C3PAO certification engine.',
  storageKey: 'nextcompli.console.dod-contracts-compliance.v1',
  notices: [
    {
      id: 'cmmc-phase-ii-pause',
      title: 'CMMC Phase II third-party assessments are paused',
      body:
        'As of 13 July 2026, DoD suspended CMMC Phase II (C3PAO) requirements and later milestones while the program is reviewed. Phase I self-assessments remain in force when the solicitation includes them. Read each notice for DFARS 252.204-7021 level. DFARS 252.204-7012 still applies if you will handle CUI.',
      sourceLabel: 'DoD CMMC program news',
      sourceUrl: 'https://business.defense.gov/Engage/News/',
    },
    {
      id: 'taxonomy-levels',
      title: 'Classify company, project, and contract separately',
      body:
        'Company readiness, project data handling, and contract clauses can differ. Use the level workbench: Level 1 stays 15 FAR practices plus SPRS affirmation. Unlock numeric SPRS, SSP/POA&M, 110 NIST 800-171 rows, GCC High/Entra/Intune evidence pointers, and a PreVeil CUI mailbox only when CUI is in scope.',
    },
    {
      id: 'fci-first',
      title: 'First IT consulting awards are usually FCI, not classified',
      body:
        'Most small-business DoD IT consulting starts on unclassified work. Protect Federal Contract Information under FAR 52.204-21 (CMMC Level 1, 15 practices, annual SPRS affirmation, no POA&M). Do not bid CUI or classified work until NIST 800-171 / DFARS 252.204-7012 and any clearance path are actually in place.',
      sourceLabel: 'FAR 52.204-21',
      sourceUrl: 'https://www.acquisition.gov/far/52.204-21',
    },
    {
      id: 'plan-coverage-ladder',
      title: 'Coverage deepens by subscription',
      body:
        'CompliCore is FCI / CMMC Level 1: entity, SAM, insurance, the 15 FAR 52.204-21 practices, and SPRS affirmation. Do not upload CUI. CompliAdvantage adds the 110-practice Level 2 tracker, POA&M workspace, and DFARS 252.204-7012 72-hour incident playbook — CUI stays in the client enclave. CompliComplete adds SSP export, GCC High/Entra/Intune pointers, a PreVeil CUI mailbox pointer, and an optional FedRAMP Moderate enclave. NextCompli never files SPRS or certifies the customer.',
      sourceLabel: 'DoD CMMC program',
      sourceUrl: 'https://dodcio.defense.gov/cmmc/',
    },
    {
      id: 'data-impact-bar',
      title: 'Match the cloud bar to the data, not to CMMC',
      body:
        'If an agency will store records in an online product, pick the bar from the data. Low (public or nonconfidential; a breach does limited harm) is TX-RAMP Level 1 or FedRAMP Low — login names and public pages fit here; agency records do not. Normal (confidential day-to-day records; a breach does serious harm, not loss of life) is TX-RAMP Level 2 or FedRAMP Moderate — this is where most agency software sits. High (law enforcement, national security, or a breach that could be catastrophic) is FedRAMP High; Texas still uses TX-RAMP Level 2 and then adds its own extra controls. Labor consulting that does not host agency records is usually CMMC / FCI, not TX-RAMP. Cities and school districts often ask for SOC 2 instead of a cloud authorization. FedRAMP or GovRAMP reciprocity is not automatic after 30 Oct 2024 — request it in DIR SPECTRIM.',
      sourceLabel: 'Texas DIR — TX-RAMP eligibility',
      sourceUrl: 'https://dir.texas.gov/information-security/texas-risk-and-authorization-management-program-tx-ramp/tx-ramp-eligibility',
    },
    {
      id: 'vendor-lists-layers',
      title: 'Local, county, state, and federal vendor lists are separate',
      body:
        'Each buyer runs its own vendor file. SAM.gov (UEI/CAGE) is the federal award gate and must be renewed every 365 days. Texas uses the Electronic State Business Daily to post work and the Centralized Master Bidders List ($70/year, EIN required) to email notices — CMBL is not required to bid and is not a DIR contract. DIR IT contracts are won by answering an RFO, not by signing up. Register in the city and county portals for the places you will serve. San Antonio uses SAePS for goods and professional services (CIVCAST is construction/A&E). Some cities only post a finance bid board and do not mail notices. School districts use Bonfire, IonWave, PlanetBids, or similar. Cooperatives such as BuyBoard, TIPS, and HGACBuy are optional catalogs you win — do not invent a contract number. NIGP class/item codes drive city, county, and CMBL notices; SAM uses NAICS. Register once per portal. Store passwords in your password manager, not here. None of these lists replace SAM, and SAM does not register you with the city.',
      sourceLabel: 'SAM.gov entity registration',
      sourceUrl: 'https://sam.gov/entity-registration',
    },
    {
      id: 'shared-assurance',
      title: 'SOC 2, CMMC, and GSA are three programs',
      body:
        'Keep one control library and one evidence folder. A SOC 2 Type II report shows a CPA firm tested the Trust Services Criteria over a period. It does not create a CMMC status, a SPRS score, or a FedRAMP authorization. CMMC Level 2 is the 110 NIST SP 800-171 requirements. Map the two through NIST SP 800-53 (AICPA Trust Services Criteria map, then NIST SP 800-171 Appendix D) and close the gaps each buyer still requires. A GSA Multiple Award Schedule is a contract you win after SAM is Active. It is not a cyber certification. DoD still expects NIST SP 800-171 when the contract covers CUI, including while CMMC Phase II assessments are paused.',
      sourceLabel: 'AICPA Trust Services Criteria to NIST 800-53',
      sourceUrl: 'https://www.aicpa-cima.com/resources/download/mapping-2017-trust-services-criteria-to-nist-800-53',
    },
    {
      id: 'insurance-far-stack',
      title: 'Match insurance to the notice, not to a shopping list',
      body:
        'FAR 28.307-2 names workers’ compensation, CGL, and automobile liability as the usual contract floors. Aircraft and vessel coverage apply only if the work uses them — not for desk IT consulting. E&O, cyber, umbrella, crime, EPLI, D&O, and a BOP are extras: buy them when a notice, a prime, or a hire creates the risk. Miller Act bonds are for construction, not ordinary IT services. Do not invent a policy number. Do not pay until the LLC checking account stays at or above the operating floor after premiums. Personal auto and health insurance are not substitutes. Unemployment tax is not a workers’ compensation policy.',
      sourceLabel: 'FAR 28.307-2',
      sourceUrl: 'https://www.acquisition.gov/far/28.307-2',
    },
    {
      id: 'sbir-sttr-phases',
      title: 'SBIR and STTR are three phases, and Phase III is not set-aside money',
      body:
        'America’s Seed Fund (SBIR and STTR) was reauthorized on 13 April 2026 through 30 September 2031. Phase I proves the idea, usually over 6 to 12 months. Phase II builds the prototype, usually for up to two years. Phase III sells or fields the result with private capital or another federal contract. Phase III is not paid with SBIR or STTR funds, and it has no dollar, time, or count cap. As of April 2026 an agency may award a Phase I up to $323,090 and a Phase II up to $2,153,927, including modifications, without asking the SBA for a waiver. A defense topic can set a lower cap. Defense proposals go through DSIP. NextCompli does not submit the proposal and does not invent an award number.',
      sourceLabel: 'SBIR.gov — About',
      sourceUrl: 'https://www.sbir.gov/about',
    },
  ],
  pillars: [
    {
      id: 'entity-eligibility',
      label: 'Entity Eligibility',
      summary: 'Legal entity, identifiers, SAM.gov, banking, insurance, W-9, Texas franchise tax account status, and socio-economic certifications. City, county, and state vendor portals sit beside SAM — they do not replace it.',
      items: [
        {
          id: 'tx-llc-good-standing',
          title: 'Legal entity in good standing',
          action:
            'Confirm the bidder is the formed LLC (not a personal name or unfiled DBA), keep SOS and registered-agent records current, and use the EIN-letter legal name on SAM, bank, and invoices.',
          evidenceHint: 'Certificate of Formation + SOS file number',
          keepCopy: true,
          gate: 'entity',
          priority: 'blocking',
          sourceLabel: 'Texas SOS',
          sourceUrl: 'https://www.sos.state.tx.us/',
          docsLabel: 'Texas SOS — corporations',
          docsUrl: 'https://www.sos.texas.gov/corp/index.shtml',
        },
        {
          id: 'tx-franchise-account-status',
          title: 'Texas franchise tax account status',
          action:
            'Look up the LLC on the Comptroller Franchise Tax Account Status search. Texas no longer calls this a certificate of good standing. The SOS Certificate of Formation is not the same document. State and many local buyers expect the account status to show the right to transact business in Texas. Do not invent a certificate number.',
          verifySteps: [
            {
              id: 'given-identity',
              kind: 'do',
              text: 'Confirm the company legal name and EIN are on the project (Do it for me uses them automatically).',
              evidenceHint: 'Legal name + EIN on the project',
            },
            {
              id: 'when-lookup',
              kind: 'do',
              text: 'NextCompli looks up Franchise Tax Account Status (public API key or public COA search bot — no Comptroller login). Not a Certificate of Formation; do not invent a certificate number.',
              evidenceHint: 'Comptroller right-to-transact result or printout',
            },
            {
              id: 'then-vault',
              kind: 'proof',
              text: 'Keep the account-status print or screenshot in the evidence vault for the contract file (auto-staged when lookup succeeds).',
              evidenceHint: 'Comptroller account-status print or screenshot (no invented certificate)',
            },
          ],
          evidenceHint: 'Comptroller account-status print or screenshot (no invented certificate)',
          completeLabel: 'Mark complete (account status on file)',
          completeRequires: 'evidence',
          keepCopy: true,
          gate: 'entity',
          priority: 'required',
          dependsOn: ['tx-llc-good-standing'],
          dependencyNote: 'Needs the formed LLC. Independent of SAM.gov.',
          sourceLabel: 'Franchise Tax Account Status',
          sourceUrl: 'https://comptroller.texas.gov/taxes/franchise/coas-instructions.php',
          docsLabel: 'Account status search',
          docsUrl: 'https://mycpa.cpa.state.tx.us/coa/',
          accessLabel: 'Comptroller public search',
          accessUrl: 'https://mycpa.cpa.state.tx.us/coa/',
          portalId: 'tx_comptroller',
          // Public FTAS / API key lives on the NextCompli gateway — not an operator vault login.
          credentials: 'none',
        },
        {
          id: 'compliance-calendar-dates',
          title: 'Put compliance events on the calendar',
          action:
            'Open the compliance calendar and save a date for each event that applies this year: SAM renewal, the Texas franchise report, the CMMC affirmation, and the insurance certificate. Choose Not this year only when that event does not apply. Do not invent a date.',
          verifySteps: [
            {
              id: 'sam-renewal',
              kind: 'proof',
              text: 'Save the SAM.gov expiration date, or mark it not this year.',
              evidenceHint: 'Date on the compliance calendar',
            },
            {
              id: 'tx-franchise-report',
              kind: 'proof',
              text: 'Save the Texas franchise report date, or mark it not this year.',
              evidenceHint: 'Date on the compliance calendar',
            },
            {
              id: 'cmmc-affirmation',
              kind: 'proof',
              text: 'Save the CMMC affirmation date, or mark it not this year.',
              evidenceHint: 'Date on the compliance calendar',
            },
            {
              id: 'insurance-certificate',
              kind: 'proof',
              text: 'Save the insurance certificate date, or mark it not this year.',
              evidenceHint: 'Date on the compliance calendar',
            },
          ],
          evidenceHint: 'Each applicable event saved on the compliance calendar',
          completeLabel: 'Mark complete (events on the calendar)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'required',
          dependsOn: ['tx-llc-good-standing'],
          dependencyNote: 'Needs the formed company. The dates come from the records, not from a guess.',
        },
        {
          id: 'ein-irs',
          title: 'IRS EIN issued and usable',
          action:
            'Record EIN, name control, and responsible party. Bank and SAM can use the EIN immediately; wait the IRS TIN-match window before e-file.',
          evidenceHint: 'CP575 / EIN confirmation letter',
          keepCopy: true,
          gate: 'entity',
          priority: 'blocking',
          dependsOn: ['tx-llc-good-standing'],
          dependencyNote: 'Needs the formed LLC. Bank and SAM both use this EIN.',
          sourceLabel: 'Apply for an EIN',
          sourceUrl: 'https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online',
          docsLabel: 'IRS EIN documentation',
          docsUrl: 'https://www.irs.gov/businesses/small-businesses-self-employed/employer-id-numbers',
        },
        {
          id: 'business-bank',
          title: 'Business checking in the LLC name',
          action:
            'Go to a bank and open a checking account in the LLC’s legal name. Put the EIN on the application. SAM.gov cannot pay the company without this account.',
          evidenceHint: 'Account title + last-four + opened date',
          gate: 'entity',
          priority: 'blocking',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the LLC and EIN first. Does not wait on SAM.gov. SAM can still be started without this account; SAM cannot pay the company (EFT) until this account is in SAM.',
          docsLabel: 'SBA: Open a business bank account',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/open-business-bank-account',
        },
        {
          id: 'w9-on-file',
          title: 'W-9 ready for government payers',
          action:
            'Keep a signed IRS Form W-9 in the LLC legal name and EIN. Counties and cities ask for it before they pay. Do not type a Social Security Number into this console. File the W-9 with the buyer, not in NextCompli.',
          evidenceHint: 'Note that a signed W-9 exists (do not upload SSN)',
          completeLabel: 'Mark complete (W-9 ready)',
          completeRequires: 'evidence',
          keepCopy: true,
          gate: 'entity',
          priority: 'required',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the LLC and EIN. Independent of SAM.gov.',
          sourceLabel: 'IRS Form W-9',
          sourceUrl: 'https://www.irs.gov/forms-pubs/about-form-w-9',
        },
        {
          id: 'sam-uei-cage',
          title: 'SAM.gov UEI and CAGE',
          action:
            'Confirm each SAM step with proof. If SAM already shows Entity Under Review or Pending ID Assignment, wait for the GSA email — do not start a second registration. Use the Login.gov account you already have — do not create a second Login.gov for the company. Copy UEI and CAGE only when SAM marks the record Active. Do not invent those numbers. DUNS is not required.',
          keepCopy: true,
          verifySteps: [
            {
              id: 'sam-sign-in',
              kind: 'do',
              text: 'Open sam.gov and click Sign In (top right). Use the Login.gov account you already have. Login.gov is a personal sign-in, not a company account.',
              evidenceHint: 'Workspace screenshot after Sign In',
            },
            {
              id: 'sam-register-all-awards',
              kind: 'do',
              text: 'In Workspace, click Get Started, then Register Entity. Choose purpose All Awards. Do not pick Unique Entity ID only — that cannot bid as a prime.',
              evidenceHint: 'All Awards / Register Entity screenshot',
            },
            {
              id: 'sam-entity-details',
              kind: 'do',
              text: 'Fill the LLC legal name, EIN, physical address, mailing or registered-agent address, LLC type, SOS start date, and DBA if an assumed name is already filed.',
              evidenceHint: 'Entity Information screenshot',
            },
            {
              id: 'sam-submit-docs',
              kind: 'do',
              text: 'Submit the registration and required documents, then click Check Entity Status. Keep the GSA FSD incident number. Registration can take up to 10 business days.',
              evidenceHint: 'Documentation Successfully Submitted screen or INC- number',
            },
            {
              id: 'sam-gsa-validation',
              kind: 'wait',
              text: 'If SAM already shows Entity Under Review or Pending ID Assignment, wait for the GSA email. Do not click Get Started again and do not start a second registration. If GSA asks for more files, use Open Cases on the same entity.',
              evidenceHint: 'GSA / FSD email or Entity Under Review workspace screenshot',
            },
            {
              id: 'sam-naics-889',
              kind: 'do',
              text: 'After GSA closes the case, set primary NAICS 541512. Also add 541511, 541519, and 541611. Finish the FAR questionnaires, including 52.204-24 / 52.204-25 (Section 889).',
              evidenceHint: 'NAICS and reps screenshot',
            },
            {
              id: 'sam-eft',
              kind: 'do',
              text: 'When SAM asks for EFT, enter the business-checking last-four. SAM cannot pay the company without it. Do not store routing or the full account number here.',
              evidenceHint: 'EFT page showing last-four only',
            },
            {
              id: 'sam-active-ids',
              kind: 'proof',
              text: 'When status is Active, copy the UEI and CAGE SAM assigned. Do not type guessed IDs.',
              evidenceHint: 'SAM Active record screenshot (UEI + CAGE)',
            },
          ],
          actionSteps: [
            'If SAM already shows Entity Under Review or Pending ID Assignment, stop. Do not click Get Started again and do not start a second registration.',
            'Open sam.gov and click Sign In (top right). Use the Login.gov account you already have. Login.gov is a personal sign-in, not a company account.',
            'In Workspace, click Get Started, then Register Entity. Do not pick Unique Entity ID only — that cannot bid as a prime.',
            'Choose purpose All Awards.',
            'Fill the LLC legal name, EIN, physical address, mailing or registered-agent address, LLC type, and the SOS start date.',
            'Set primary NAICS 541512. Also add 541511, 541519, and 541611. Finish the FAR questionnaires, including 52.204-24 / 52.204-25 (Section 889).',
            'When SAM asks for EFT, enter the business-checking last-four. SAM cannot pay the company without it.',
            'Submit, then click Check Entity Status on the SAM home card. Registration can take up to 10 business days.',
            'When status is Active, copy the UEI and CAGE SAM assigned. Do not type guessed IDs.',
          ],
          waitingSteps: [
            'Do not click Get Started or Register Entity again while SAM shows Entity Under Review or Pending ID Assignment.',
            'Watch the Login.gov email for the GSA / FSD reply (usually 1.5–3.5 business days; weekends and federal holidays do not count).',
            'If GSA asks for more files, use Open Cases on the same entity. Do not open a new registration.',
            'When the email says the entity was validated, return to Workspace, continue NAICS, 889, and EFT, and copy UEI and CAGE only when status is Active.',
          ],
          evidenceHint: 'SAM Active record screenshot (UEI + CAGE)',
          completeLabel: 'Mark complete (UEI + CAGE)',
          completeRequires: 'uei-cage',
          gate: 'entity',
          priority: 'blocking',
          sourceLabel: 'Open SAM.gov',
          sourceUrl: 'https://sam.gov/',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the LLC and EIN. You can start SAM and get a UEI/CAGE without the bank account. You need the bank before SAM can pay you (EFT). Use the personal Login.gov account you already have. Do not create a second Login.gov for the company.',
          docsLabel: 'SAM.gov entity registration guide',
          docsUrl: 'https://sam.gov/content/entity-registration',
        },
        {
          id: 'naics-reps-certs',
          title: 'NAICS, size, and representations',
          action:
            'In that same SAM.gov registration, set primary NAICS 541512 (computer systems design). Also add 541511, 541519, and 541611. Finish the size and FAR questionnaires SAM asks for.',
          evidenceHint: 'SAM NAICS + Reps & Certs date',
          gate: 'entity',
          priority: 'blocking',
          sourceLabel: 'Open SAM.gov',
          sourceUrl: 'https://sam.gov/',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Do this in the same SAM.gov registration as UEI/CAGE. You can fill NAICS and reps while SAM is in progress. This step does not wait on the bank account.',
          docsLabel: 'Census NAICS search',
          docsUrl: 'https://www.census.gov/naics/',
        },
        {
          id: 'psc-codes',
          title: 'PSC codes on the public page',
          action:
            'Pick the Product or Service Codes that match how a buyer would purchase the work, then show them in public text next to the legal name, UEI, CAGE, and NAICS. For custom application work sold as labor, the usual code is DA01. For that same software sold as a subscription, the usual code is DA10. Do not copy another company’s codes. PSC is not stored the way NAICS is stored in SAM. The buyer puts the main PSC on the contract line and in FPDS. Use the PSC manual. Do not invent a code.',
          evidenceHint: 'Public page or capability statement showing the PSC codes',
          gate: 'pursuit',
          priority: 'required',
          dependsOn: ['naics-reps-certs'],
          dependencyNote: 'Needs the NAICS list first so the PSC codes describe the same work.',
          sourceLabel: 'PSC manual (Acquisition.gov)',
          sourceUrl: 'https://www.acquisition.gov/psc-manual',
          docsLabel: 'GSA guide to picking a PSC',
          docsUrl: 'https://itvmo.gsa.gov/assets/files/A-Guide-to-Finding-PSC-codes-508dk.pdf',
        },
        {
          id: 'sam-renewal-365',
          title: 'SAM.gov annual renewal',
          action:
            'SAM.gov entity registrations expire every 365 days. Calendar the renewal date from the Active record. An expired SAM cannot receive federal awards. Renew in the same entity — do not start a second registration.',
          evidenceHint: 'SAM expiration date on the calendar, or Active record showing the next due date',
          completeLabel: 'Mark complete (renewal dated)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'required',
          dependsOn: ['sam-uei-cage'],
          dependencyNote: 'Needs an Active SAM record. Do not start a second registration to renew.',
          sourceLabel: 'SAM.gov entity registration',
          sourceUrl: 'https://sam.gov/entity-registration',
        },
        {
          id: 'sam-exclusions-self',
          title: 'SAM exclusions / responsibility check',
          action:
            'After SAM is Active, search SAM Exclusions (FAR 9.4) and the entity’s Responsibility/Qualification record (formerly FAPIIS) for the LLC legal name. You should not appear on the excluded-parties list. Do not invent an exclusion record. Recheck after any ownership or name change.',
          evidenceHint: 'Exclusions search showing the LLC legal name is not listed',
          completeLabel: 'Mark complete (not excluded)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'required',
          dependsOn: ['sam-uei-cage'],
          dependencyNote: 'Needs an Active SAM record. Do not invent an exclusion or FAPIIS finding.',
          sourceLabel: 'SAM.gov exclusions',
          sourceUrl: 'https://sam.gov/content/exclusions',
        },
        {
          id: 'tx-hub-certification',
          title: 'Texas HUB certification',
          action:
            'Apply for Texas Historically Underutilized Business (HUB) certification at the Comptroller after the LLC and EIN exist. Do not invent a HUB number. Do not mark complete until the Comptroller certificate is on file. HUB helps on state and some local work; it does not replace SAM.gov.',
          verifySteps: [
            {
              id: 'hub-eligibility',
              kind: 'do',
              text: 'Confirm Texas-based ownership and control against the Comptroller HUB rules before applying. Do not claim HUB on the capability statement until the certificate exists.',
              evidenceHint: 'Eligibility notes (ownership / control) — no invented cert number',
            },
            {
              id: 'hub-apply',
              kind: 'do',
              text: 'Submit the HUB application in the Comptroller CMBL / HUB portal using the LLC legal name and EIN. Keep the confirmation number.',
              evidenceHint: 'Portal confirmation or application ID',
            },
            {
              id: 'hub-certificate',
              kind: 'proof',
              text: 'When the Comptroller issues the certificate, file it and copy the HUB number here. Do not type a guessed number.',
              evidenceHint: 'HUB certificate PDF or Comptroller screenshot',
            },
          ],
          evidenceHint: 'HUB certificate PDF (Comptroller) — do not invent the number',
          completeLabel: 'Mark complete (HUB certificate on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'required',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the formed LLC and EIN. Independent of SAM.gov. Do not invent a HUB number.',
          sourceLabel: 'Texas Comptroller HUB',
          sourceUrl: 'https://comptroller.texas.gov/purchasing/vendor/hub/',
          docsLabel: 'HUB certification guide',
          docsUrl: 'https://comptroller.texas.gov/purchasing/vendor/hub/certification.php',
        },
        {
          id: 'sba-certifications',
          title: 'SBA certifications (8(a), WOSB, HUBZone, small)',
          action:
            'After SAM is Active, confirm the small-business size standard in SAM, then apply only for SBA certifications the LLC actually qualifies for (8(a), WOSB/EDWOSB, HUBZone, SDVOSB). WOSB/EDWOSB is the women-owned set-aside when women own and control at least 51 percent. 8(a) usually needs two years in business. An 8(a) joint venture is a separate company. Do not claim a partner’s 8(a) or a partner’s contract vehicle. Do not invent certifications. Do not claim 8(a) until SBA awards it. SBA Small Business Search (SBS, formerly DSBS) is a later step once SAM is Active.',
          verifySteps: [
            {
              id: 'sba-sam-size',
              kind: 'wait',
              text: 'Wait until SAM.gov status is Active, then confirm the NAICS 541512 small-business size representation in SAM. Do not start a second SAM registration.',
              evidenceHint: 'SAM Active reps screenshot showing small-business size',
            },
            {
              id: 'sba-eligibility',
              kind: 'do',
              text: 'Screen 8(a), WOSB/EDWOSB, HUBZone, and SDVOSB against real ownership, location, and time-in-business. 8(a) usually needs two years in business. An 8(a) joint venture is a separate company with its own SAM record. Do not apply for a program the LLC does not qualify for.',
              evidenceHint: 'Eligibility notes per program (qualified / not yet / N/A)',
            },
            {
              id: 'sba-certify',
              kind: 'do',
              text: 'If eligible, file in SBA Certify with the LLC legal name and EIN. Keep the application ID. Do not mark complete until SBA issues the certificate.',
              evidenceHint: 'SBA Certify confirmation or application ID',
            },
            {
              id: 'sba-certificate',
              kind: 'proof',
              text: 'File each SBA certificate. Copy only numbers SBA issued. Then complete the separate SBA Small Business Search (SBS) profile step.',
              evidenceHint: 'SBA certificate PDF or Certify screenshot',
            },
          ],
          evidenceHint: 'SBA certificate(s) or documented N/A — do not invent program status',
          completeLabel: 'Mark complete (SBA certs on file or N/A)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'required',
          dependsOn: ['tx-llc-good-standing', 'ein-irs', 'sam-uei-cage'],
          dependencyNote: 'Needs LLC, EIN, and an Active SAM record before SBA Certify. Do not invent 8(a), WOSB, or HUBZone status. Do not start a second SAM registration.',
          sourceLabel: 'SBA Certify',
          sourceUrl: 'https://certify.sba.gov/',
          docsLabel: 'SBA federal contracting guide',
          docsUrl: 'https://www.sba.gov/federal-contracting/contracting-guide',
        },
        {
          id: 'insurance-eo-cgl',
          title: 'E&O and CGL insurance',
          action:
            'Bind professional liability (E&O) and commercial general liability (CGL) in the LLC legal name after current quotes are on file. FAR 28.307-2 sets a CGL floor of $500,000 per occurrence; the bid-ready target is $1 million each. E&O is not a FAR minimum but primes and IT notices routinely require it. Before performance, keep a Certificate of Insurance in the file as proof (FAR 52.228-5, FAR 4.803, DAFFARS 5328.310). A COI evidences coverage; it does not replace the policy. Do not mark complete until the COI is on file.',
          verifySteps: [
            {
              id: 'ins-quote',
              kind: 'do',
              text: 'Get current quotes in the LLC legal name for $1M commercial general liability and $1M professional liability (E&O). The CGL quote often excludes E&O — bind both. FAR 28.307-2 CGL minimum is $500,000; $1M is the bid-ready target. Do not shop a second market unless the on-file quote cannot be bound.',
              evidenceHint: 'Quote PDF or broker email with named insured and limits',
            },
            {
              id: 'ins-bind',
              kind: 'do',
              text: 'Bind both policies. Named insured must be the LLC legal name, not only the DBA. Keep the binder or policy declarations.',
              evidenceHint: 'Binder or declarations page showing this company\'s legal name',
            },
            {
              id: 'ins-coi',
              kind: 'proof',
              text: 'File the Certificate of Insurance (ACORD 25 or equivalent) showing named insured, limits, and effective dates. Contracting officers request this as proof of insurance and keep it in the contract file. Upload the COI or note the policy numbers here. Do not mark complete until this proof is on file.',
              evidenceHint: 'COI PDF / screenshot, or policy numbers and effective dates',
            },
          ],
          evidenceHint: 'Certificate of Insurance (named insured = LLC legal name, $1M E&O + $1M CGL)',
          completeLabel: 'Mark complete (COI on file)',
          completeRequires: 'evidence',
          keepCopy: true,
          gate: 'entity',
          priority: 'blocking',
          dependsOn: ['tx-llc-good-standing'],
          dependencyNote: 'Needs the LLC legal name on the policy and COI. Independent of SAM.gov and the bank account.',
          sourceLabel: 'FAR 52.228-5',
          sourceUrl: 'https://www.acquisition.gov/far/52.228-5',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
        {
          id: 'insurance-cyber',
          title: 'Cyber liability insurance',
          action:
            'Cyber insurance is separate from E&O and CGL. First-party coverage pays the LLC’s own incident costs (forensics, restore, ransomware, business interruption, notices). Third-party coverage pays if a client claims the LLC’s work caused their breach. Technology E&O may bundle some third-party cyber; it does not replace first-party costs on the LLC’s own systems. FAR 28.307 and CMMC do not require a cyber policy, but a solicitation or prime subcontract often does. Write down the cyber limit the notice names. Many notices ask for about $2 million. That figure is the notice’s number, not a FAR minimum. Buying a policy does not satisfy DFARS 252.204-7012 72-hour reporting, SPRS, or CMMC. Do not invent a policy number. Do not mark complete until a COI or a written decline is on file.',
          verifySteps: [
            {
              id: 'cyber-need',
              kind: 'do',
              text: 'Decide whether the LLC hosts client or agency data, or only advises on the client’s systems. Hosting or processing PII/FCI on LLC tenants is a first-party cyber need. Advising or implementing on a client network is mainly third-party / tech E&O. If a notice names a cyber limit, write that number next to the limit on the quote. Ask the broker after E&O and CGL quotes are current.',
              evidenceHint: 'Broker email or notes of first-party vs third-party need',
            },
            {
              id: 'cyber-quote',
              kind: 'do',
              text: 'Get a named-insured quote in the LLC legal name. Carriers commonly ask about MFA, endpoint detection, backups, and (for defense work) CMMC/SPRS status. Those answers must match the DoD console — misrepresenting CMMC on an application can void coverage and is a False Claims Act risk the policy usually will not pay.',
              evidenceHint: 'Quote PDF or broker email with named insured and limits',
            },
            {
              id: 'cyber-bind-or-decline',
              kind: 'proof',
              text: 'Bind when a contract requires it and cash can stay above the operating floor after premiums, or file a written decline until a notice asks. File the COI when bound. Do not mark complete without that proof or the decline note.',
              evidenceHint: 'COI PDF, policy number and dates, or written decline',
            },
          ],
          evidenceHint: 'Cyber COI (named insured = LLC legal name) or written decline',
          completeLabel: 'Mark complete (cyber COI or decline on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'recommended',
          dependsOn: ['insurance-eo-cgl'],
          dependencyNote: 'Ask for cyber after E&O and CGL quotes exist. Independent of SAM.gov. Do not invent a policy.',
          sourceLabel: 'SBA: Get business insurance',
          sourceUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
        {
          id: 'insurance-workers-comp',
          title: 'Workers’ compensation and employer’s liability',
          action:
            'FAR 28.307-2 wants workers’ compensation plus employer’s liability of at least $100,000. Texas does not require workers’ compensation for most private employers, but federal, DIR, and city notices often do. Members who own 25 percent or more can usually elect to be excluded. Health insurance is not a substitute. If the LLC has no W-2 employees and a notice does not ask, file a written N/A. Do not invent a policy. Do not mark complete until a COI or the N/A note is on file.',
          verifySteps: [
            {
              id: 'wc-need',
              kind: 'do',
              text: 'Count W-2 employees (not 1099 contractors). If a solicitation or DIR/city clause requires workers’ compensation, buy it even when Texas would otherwise let you skip it. Ask the broker after E&O and CGL quotes exist.',
              evidenceHint: 'Headcount note or clause extract',
            },
            {
              id: 'wc-bind-or-na',
              kind: 'proof',
              text: 'Bind statutory Texas workers’ compensation with employer’s liability at least $100,000 (bid-ready $1 million) in the LLC legal name, or file a written N/A / member-exclusion until a notice asks. File the COI when bound. Do not mark complete without that proof.',
              evidenceHint: 'WC COI, exclusion election, or written N/A',
            },
          ],
          evidenceHint: 'Workers’ compensation COI or written N/A (named insured = LLC legal name)',
          completeLabel: 'Mark complete (WC COI or N/A on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'required',
          dependsOn: ['insurance-eo-cgl'],
          dependencyNote: 'Ask after E&O and CGL quotes exist. Independent of SAM.gov. Do not invent a policy.',
          sourceLabel: 'FAR 28.307-2',
          sourceUrl: 'https://www.acquisition.gov/far/28.307-2',
          docsLabel: 'Texas DWC — coverage',
          docsUrl: 'https://www.tdi.texas.gov/wc/',
        },
        {
          id: 'insurance-auto',
          title: 'Automobile liability / HNOA',
          action:
            'FAR 28.307-2 wants automobile liability when cars are used on the contract. US floors are $200,000 per person / $500,000 per occurrence bodily injury and $20,000 property damage; the bid-ready target is $1 million combined single limit. Personal auto is not a substitute. If the LLC owns no vehicle, add HNOA for personal or rental cars used for work. Aircraft and vessel liability are N/A for desk IT consulting. Do not invent a policy. Do not mark complete until a COI or a written N/A is on file.',
          verifySteps: [
            {
              id: 'auto-need',
              kind: 'do',
              text: 'Record whether anyone drives to a client site, a base, or a city office for LLC work. If yes, HNOA or business auto is in play. If nobody drives for the company, write N/A.',
              evidenceHint: 'Driving-use note or N/A',
            },
            {
              id: 'auto-bind-or-na',
              kind: 'proof',
              text: 'Bind HNOA or business auto in the LLC legal name, or file a written N/A. File the COI when bound. Do not mark complete without that proof.',
              evidenceHint: 'Auto/HNOA COI or written N/A',
            },
          ],
          evidenceHint: 'Auto or HNOA COI (named insured = LLC legal name) or written N/A',
          completeLabel: 'Mark complete (auto COI or N/A on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'required',
          dependsOn: ['insurance-eo-cgl'],
          dependencyNote: 'Ask after E&O and CGL quotes exist. Independent of SAM.gov. Do not invent a policy.',
          sourceLabel: 'FAR 28.307-2',
          sourceUrl: 'https://www.acquisition.gov/far/28.307-2',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
        {
          id: 'insurance-umbrella',
          title: 'Umbrella / excess liability',
          action:
            'An umbrella stacks extra limits on top of CGL, auto, and employer’s liability when a notice wants $2 million to $5 million instead of $1 million. It does not sit over E&O, cyber, D&O, or EPLI — those need their own excess. Skip the purchase until a solicitation or prime asks. Do not invent a policy. Do not mark complete until a COI or a written decline is on file.',
          verifySteps: [
            {
              id: 'umbrella-need',
              kind: 'do',
              text: 'Read the insurance clause. If it asks for more than the primary CGL/auto limits, quote umbrella. If it does not, write that no contract asks for higher limits.',
              evidenceHint: 'Clause extract or “no higher limits” note',
            },
            {
              id: 'umbrella-bind-or-decline',
              kind: 'proof',
              text: 'Bind when a notice requires it and cash can stay above the operating floor, or file a written decline. File the COI when bound. Do not mark complete without that proof.',
              evidenceHint: 'Umbrella COI or written decline',
            },
          ],
          evidenceHint: 'Umbrella COI (named insured = LLC legal name) or written decline',
          completeLabel: 'Mark complete (umbrella COI or decline on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'recommended',
          dependsOn: ['insurance-eo-cgl'],
          dependencyNote: 'Quote after CGL exists. Independent of SAM.gov. Do not invent a policy.',
          sourceLabel: 'SBA: Get business insurance',
          sourceUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
        {
          id: 'insurance-crime',
          title: 'Crime / fidelity coverage',
          action:
            'Crime or fidelity pays employee theft and often third-party fidelity when the company handles government money, cards, or securities. Wire-fraud and fake-invoice losses are often outside a basic cyber policy — ask whether social-engineering and funds-transfer fraud are included. Skip unless a notice or treasury role creates the risk. Do not invent a policy. Do not mark complete until a COI, endorsement, or written N/A is on file.',
          verifySteps: [
            {
              id: 'crime-need',
              kind: 'do',
              text: 'Record whether the LLC will hold agency funds, process payments, or have access to a buyer’s accounts. If the cyber quote already includes social-engineering and funds-transfer fraud, note that instead of buying a second policy.',
              evidenceHint: 'Role note or cyber-quote extract',
            },
            {
              id: 'crime-bind-or-na',
              kind: 'proof',
              text: 'Bind crime/fidelity in the LLC legal name, attach a cyber endorsement, or file a written N/A. File the COI when bound. Do not mark complete without that proof.',
              evidenceHint: 'Crime COI, cyber endorsement, or written N/A',
            },
          ],
          evidenceHint: 'Crime COI, cyber endorsement, or written N/A',
          completeLabel: 'Mark complete (crime COI or N/A on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'recommended',
          dependsOn: ['insurance-cyber'],
          dependencyNote: 'Ask after the cyber quote so you do not buy the same fraud coverage twice. Do not invent a policy.',
          sourceLabel: 'SBA: Get business insurance',
          sourceUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
        {
          id: 'insurance-bonds',
          title: 'Performance and payment bonds',
          action:
            'The Miller Act requires performance and payment bonds on most federal construction over $150,000. FAR 28.103-1 says agencies generally shall not require those bonds on non-construction work. Ordinary IT consulting records N/A unless the solicitation asks. Bid guarantees follow the same rule. Do not buy a bond package that the notice does not require. Do not invent a bond number.',
          verifySteps: [
            {
              id: 'bonds-clause',
              kind: 'do',
              text: 'Read FAR 52.228-15 / 52.228-16 on the notice. Construction or dismantling clauses mean bonds. A services IT notice without those clauses is N/A.',
              evidenceHint: 'Clause extract or N/A note',
            },
            {
              id: 'bonds-proof',
              kind: 'proof',
              text: 'File the executed bond or a written N/A. Do not mark complete without that proof.',
              evidenceHint: 'Bond PDF or written N/A',
            },
          ],
          evidenceHint: 'Executed bond or written N/A (IT consulting default is N/A)',
          completeLabel: 'Mark complete (bond or N/A on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'recommended',
          sourceLabel: 'FAR 28.103-1',
          sourceUrl: 'https://www.acquisition.gov/far/28.103-1',
          docsLabel: 'FAR Subpart 28.1 — Bonds',
          docsUrl: 'https://www.acquisition.gov/far/subpart-28.1',
        },
        {
          id: 'insurance-property',
          title: 'Business property / BOP',
          action:
            'Homeowners and renters policies often exclude business equipment. If the LLC owns laptops or other gear, ask for business property. A BOP often packages that with CGL — do not buy a second CGL if the package already includes it. Not a FAR 28.307 item. Do not invent a policy. Do not mark complete until a COI, BOP declarations, or a written N/A is on file.',
          verifySteps: [
            {
              id: 'property-need',
              kind: 'do',
              text: 'List company-owned equipment (laptops, servers, phones). If none, write N/A. If CGL will sit inside a BOP, tell the broker so you do not double-buy general liability.',
              evidenceHint: 'Equipment list or N/A',
            },
            {
              id: 'property-bind-or-na',
              kind: 'proof',
              text: 'Bind business property or a BOP in the LLC legal name, or file a written N/A. File the declarations when bound. Do not mark complete without that proof.',
              evidenceHint: 'BOP/property declarations or written N/A',
            },
          ],
          evidenceHint: 'BOP or property declarations (named insured = LLC legal name) or written N/A',
          completeLabel: 'Mark complete (property proof or N/A on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'recommended',
          dependsOn: ['insurance-eo-cgl'],
          dependencyNote: 'Coordinate with CGL so a BOP does not duplicate general liability. Do not invent a policy.',
          sourceLabel: 'SBA: Get business insurance',
          sourceUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
        {
          id: 'insurance-epli',
          title: 'Employment practices liability (EPLI)',
          action:
            'EPLI pays claims such as wrongful firing, discrimination, harassment, or retaliation. It does not replace workers’ compensation. Quote before the first W-2 hire. Skip the purchase until that hire (or a contract) exists. Do not invent a policy. Do not mark complete until a COI or a written “no employees yet” note is on file.',
          verifySteps: [
            {
              id: 'epli-need',
              kind: 'do',
              text: 'If the LLC has W-2 employees, quote EPLI. Managing members only and no employees is a written wait note, not a gap you must buy today.',
              evidenceHint: 'Headcount note',
            },
            {
              id: 'epli-bind-or-wait',
              kind: 'proof',
              text: 'Bind in the LLC legal name, or file a written wait note until the first employee. File the COI when bound. Do not mark complete without that proof.',
              evidenceHint: 'EPLI COI or written wait note',
            },
          ],
          evidenceHint: 'EPLI COI (named insured = LLC legal name) or written wait note',
          completeLabel: 'Mark complete (EPLI COI or wait note on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'recommended',
          sourceLabel: 'SBA: Get business insurance',
          sourceUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
        {
          id: 'insurance-do',
          title: 'Directors and officers (D&O)',
          action:
            'D&O pays if an owner or manager is sued over how the company is run. It is not CGL and not E&O. A two-member LLC can wait until a contract, lender, or outside investor requires it. Do not invent a policy. Do not mark complete until a COI or a written wait note is on file.',
          verifySteps: [
            {
              id: 'do-need',
              kind: 'do',
              text: 'Quote D&O if a notice, bank, or investor asks. Otherwise write that you are waiting.',
              evidenceHint: 'Ask note or wait note',
            },
            {
              id: 'do-bind-or-wait',
              kind: 'proof',
              text: 'Bind in the LLC legal name, or file a written wait note. File the COI when bound. Do not mark complete without that proof.',
              evidenceHint: 'D&O COI or written wait note',
            },
          ],
          evidenceHint: 'D&O COI (named insured = LLC legal name) or written wait note',
          completeLabel: 'Mark complete (D&O COI or wait note on file)',
          completeRequires: 'evidence',
          gate: 'entity',
          priority: 'recommended',
          sourceLabel: 'SBA: Get business insurance',
          sourceUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
          docsLabel: 'SBA: Get business insurance',
          docsUrl: 'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
        },
      ],
    },
    {
      id: 'cybersecurity',
      label: 'Cybersecurity',
      summary: 'FCI first (CMMC Level 1 self-assessment in SPRS). The level workbench unlocks NIST 800-171, SSP/POA&M, and CUI enclave tools only when a contract actually handles CUI.',
      items: [
        {
          id: 'fci-cui-determination',
          title: 'FCI vs CUI determination',
          action:
            'For each pursuit, read the job paper and record one label: FCI (everyday contract information → CMMC Level 1), CUI (more sensitive unclassified → NIST 800-171 / CMMC Level 2), or Classified (Secret / Top Secret / facility clearance). Do not assume CMMC Level 2 or a facility clearance. Use Help me decide if you are unsure.',
          actionSteps: [
            'Open the solicitation, the task order, or the staffing request for this job.',
            'Search that document for these exact words: CUI, Controlled Unclassified Information, DFARS 252.204-7012, NIST SP 800-171, and CMMC Level 2.',
            'If you find any of those words, write CUI. That includes a staffing job where the person will use a government computer that holds CUI. CUI needs NIST 800-171 / CMMC Level 2 later. It is still not classified.',
            'If those words are not there, and the work is unclassified IT help or staffing, write FCI. FCI is everyday contract information that is not public. The first bar is CMMC Level 1. That label alone does not need CMMC Level 2 or a facility clearance.',
            'Write Classified only when the notice says Secret, Top Secret, or a facility clearance. An owner’s personal clearance is not the company’s FCL. Do not guess.',
            'Save the page where you found the words, or a note that you searched and they were not there.',
          ],
          evidenceHint: 'Solicitation clause extract + determination note',
          gate: 'fci',
          priority: 'blocking',
          sourceLabel: 'CMMC scoping (Level 1)',
          sourceUrl: 'https://dodcio.defense.gov/cmmc/',
        },
        {
          id: 'data-impact-cloud-bar',
          title: 'Data-impact bar (TX-RAMP / FedRAMP / SOC 2)',
          action:
            'Classify the data an online product would store before picking a cloud authorization. Low: public or nonconfidential; a breach does limited harm — TX-RAMP Level 1 or FedRAMP Low. Login names and public pages fit here; agency records do not. Normal: confidential day-to-day records; a breach does serious harm, not loss of life — TX-RAMP Level 2 or FedRAMP Moderate (about 80 percent of authorized federal cloud products; the usual Texas agency bar). High: law enforcement, national security, or a breach that could be catastrophic — FedRAMP High. Texas still uses TX-RAMP Level 2 and then adds extra controls. TX-RAMP applies only to cloud computing services (Texas Government Code 2054.0593), not to labor consulting on an agency’s own systems. Cities and school districts often ask for SOC 2 Type II instead. FedRAMP or GovRAMP reciprocity is not automatic after 30 Oct 2024 — request it in DIR SPECTRIM. Do not claim an authorization you do not have. Consulting-only companies record that no agency stores records in their product.',
          verifySteps: [
            {
              id: 'impact-classify',
              kind: 'do',
              text: 'For each product or pursuit, write Low / Normal / High (or “consulting only — no hosted agency records”). Agency records are never Low.',
              evidenceHint: 'Classification note per product or pursuit',
            },
            {
              id: 'impact-bar',
              kind: 'do',
              text: 'Map the class to TX-RAMP Level 1 / FedRAMP Low, TX-RAMP Level 2 / FedRAMP Moderate, or FedRAMP High (Texas stays Level 2 plus extra controls). If a city or school is the buyer, record whether they asked for SOC 2 instead.',
              evidenceHint: 'Required bar, or N/A for labor-only consulting',
            },
            {
              id: 'impact-reciprocity',
              kind: 'do',
              text: 'If you will use FedRAMP or GovRAMP to satisfy Texas, submit a TX-RAMP request in SPECTRIM. Do not assume the product is on the Texas certified list. Start the package when a buyer is ready; Texas can issue 18-month provisional status during an active purchase.',
              evidenceHint: 'SPECTRIM request id, or N/A',
            },
          ],
          evidenceHint: 'Low/Normal/High (or consulting-only) note + required bar',
          completeLabel: 'Mark complete (classification on file)',
          completeRequires: 'evidence',
          gate: 'fci',
          priority: 'required',
          sourceLabel: 'Texas DIR — TX-RAMP eligibility',
          sourceUrl: 'https://dir.texas.gov/information-security/texas-risk-and-authorization-management-program-tx-ramp/tx-ramp-eligibility',
          docsLabel: 'TX-RAMP FAQs',
          docsUrl: 'https://dir.texas.gov/information-security/texas-risk-and-authorization-management-program-tx-ramp/tx-ramp-frequently',
        },
        {
          id: 'cmmc-l1-practices',
          title: 'CMMC Level 1 / FAR 52.204-21 practices',
          action:
            'Implement all 15 basic safeguarding requirements for FCI (access control, identification, media, physical, communications, integrity). Some newer notices cite FAR 52.240-93 for the same 15 requirements. Level 1 is pass/fail — no POA&M credit.',
          evidenceHint: 'Practice-by-practice evidence binder',
          gate: 'fci',
          priority: 'blocking',
          sourceLabel: 'FAR 52.204-21',
          sourceUrl: 'https://www.acquisition.gov/far/52.204-21',
        },
        {
          id: 'cmmc-l1-scope',
          title: 'CMMC Level 1 assessment scope',
          action:
            'Document which laptops, cloud tenants, email, and collaboration tools process, store, or transmit FCI. Isolate DoD FCI from personal devices and from any non-US-person access.',
          evidenceHint: 'Scope diagram + asset list',
          gate: 'fci',
          priority: 'blocking',
          sourceLabel: '32 CFR 170.19',
          sourceUrl: 'https://www.ecfr.gov/current/title-32/part-170',
        },
        {
          id: 'piee-sprs-access',
          title: 'PIEE account and SPRS Cyber Vendor role',
          action:
            'After CAGE is assigned, register in PIEE and request SPRS Cyber Vendor User so Level 1 results can be entered.',
          evidenceHint: 'PIEE username + SPRS role confirmation',
          gate: 'fci',
          priority: 'blocking',
          sourceLabel: 'PIEE',
          sourceUrl: 'https://piee.eb.mil',
          accessLabel: 'PIEE',
          accessUrl: 'https://piee.eb.mil',
          portalId: 'piee',
          credentials: 'vault',
          dependsOn: ['sam-uei-cage'],
          dependencyNote: 'Needs an Active SAM record with a CAGE. Bank is not required for the PIEE account itself.',
        },
        {
          id: 'sprs-attestation',
          title: 'SPRS Level 1 entry and affirmation',
          action:
            'Enter Final Level 1 (Self) in SPRS and have a senior official (managing member) affirm. Renew annually before expiry. Do not treat a numeric NIST 800-171 score as a substitute for Level 1 FCI work.',
          evidenceHint: 'SPRS CMMC assessment ID + affirmation date',
          gate: 'fci',
          priority: 'blocking',
          sourceLabel: 'SPRS CMMC entry guide',
          sourceUrl: 'https://www.sprs.csd.disa.mil',
          accessLabel: 'SPRS',
          accessUrl: 'https://www.sprs.csd.disa.mil',
          portalId: 'sprs',
          credentials: 'vault',
        },
        {
          id: 'cmmc-uid-proposal',
          title: 'CMMC unique identifier on proposals',
          action:
            'When DFARS 252.204-7025 is in the solicitation, put the SPRS CMMC unique identifier (UID) for every in-scope system in the proposal. A UID exists only after you post the assessment. Contracting officers shall not award without a current CMMC status at the required level and a current annual affirmation. Level 1 has no Conditional / POA&M path — it must be Final. CMMC Phase II (C3PAO) is paused; NIST SP 800-171 still applies under DFARS 252.204-7012 if CUI is in scope.',
          evidenceHint: 'Proposal clause extract + SPRS CMMC UID(s)',
          gate: 'pursuit',
          priority: 'required',
          dependsOn: ['sprs-attestation'],
          dependencyNote: 'Needs a posted SPRS Level 1 (or higher) assessment so a UID exists. Blocked on CAGE.',
          sourceLabel: 'DFARS 252.204-7025',
          sourceUrl: 'https://www.acquisition.gov/dfars/252.204-7025-cybersecurity-maturity-model-certification-requirements',
        },
        {
          id: 'sprs-annual-renewal',
          title: 'Annual SPRS reaffirmation reminder',
          action:
            'Calendar the annual CMMC Level 1 affirmation before SPRS expiry. A managing member reaffirms. Missing the window can block new awards even if the original assessment was Final.',
          evidenceHint: 'Calendar reminder + last affirmation date',
          gate: 'fci',
          priority: 'required',
          sourceLabel: 'SPRS CMMC entry guide',
          sourceUrl: 'https://www.sprs.csd.disa.mil',
        },
        {
          id: 'assessment-record-retention',
          title: 'Six-year assessment record retention',
          action:
            'Keep the Level 1 self-assessment package (scope, 15-practice dispositions, affirmation, and artifacts) for six years. Do not store CUI in commercial NextCompli.',
          evidenceHint: 'Retention location + destruction date',
          gate: 'fci',
          priority: 'required',
          sourceLabel: '32 CFR 170',
          sourceUrl: 'https://www.ecfr.gov/current/title-32/part-170',
        },
        {
          id: 'dfars-incident',
          title: 'DFARS cyber incident reporting readiness',
          action:
            'Confirm 72-hour cyber incident reporting contacts, DIBNet access path, and tabletop date for DFARS 252.204-7012 when CUI or covered defense information is in scope.',
          evidenceHint: 'Runbook + last tabletop date',
          gate: 'cui',
          planMin: 'advanced',
          priority: 'required',
          sourceLabel: 'DFARS 252.204-7012',
          sourceUrl: 'https://www.acquisition.gov/dfars/252.204-7012-safeguarding-covered-defense-information-and-cyber-incident-reporting',
        },
        {
          id: 'cmmc-gap',
          title: 'NIST 800-171 / CMMC Level 2 gap review',
          action:
            'If a target solicitation requires CUI protections, map NIST SP 800-171 controls, owners, and open POA&M items. Phase II C3PAO is paused; self-assessment may still be required under 7021 — read the notice.',
          evidenceHint: 'Gap matrix or SSP excerpt',
          gate: 'cui',
          planMin: 'advanced',
          priority: 'required',
          sourceLabel: 'NIST SP 800-171 Rev. 2',
          sourceUrl: 'https://csrc.nist.gov/pubs/sp/800/171/r2/final',
        },
        {
          id: 'ssp-poam-cui',
          title: 'System security plan and POA&M (CUI path)',
          action:
            'Maintain an SSP for the enclave that would store CUI and a POA&M for unmet 800-171 requirements. Not required to claim CMMC Level 1, but required before bidding CUI work.',
          evidenceHint: 'SSP + POA&M dated copies',
          gate: 'cui',
          planMin: 'enterprise',
          priority: 'required',
          sourceLabel: 'NIST SP 800-171',
          sourceUrl: 'https://csrc.nist.gov/pubs/sp/800/171/r2/final',
        },
        {
          id: 'gcc-high-entra-intune',
          title: 'GCC High / Entra / Intune evidence pointers',
          action:
            'When CUI lives in a Microsoft government tenant, record tenant IDs and export Entra/Intune control evidence into the binder. Do not ingest CUI into commercial NextCompli.',
          evidenceHint: 'Tenant ID + evidence export path (pointers only)',
          gate: 'cui',
          planMin: 'enterprise',
          priority: 'required',
          sourceLabel: 'Microsoft GCC High',
          sourceUrl: 'https://learn.microsoft.com/en-us/office365/servicedescriptions/office-365-platform-service-description/office-365-us-government/gcc-high-and-dod',
        },
        {
          id: 'preveil-cui-enclave',
          title: 'Encrypted CUI mailbox (PreVeil or GCC High)',
          action:
            'Stand up an encrypted CUI mailbox/Drive when CUI will be emailed or shared. PreVeil free accounts are collaboration-only; CMMC Pass is paid. FCI-only work does not need this mailbox.',
          evidenceHint: 'Enclave domain + access list (US persons)',
          gate: 'cui',
          planMin: 'enterprise',
          priority: 'required',
          sourceLabel: 'PreVeil',
          sourceUrl: 'https://www.preveil.com/',
        },
      ],
    },
    {
      id: 'shared-assurance',
      label: 'SOC 2, CMMC, and GSA',
      summary:
        'One evidence library for the auditor report, the DoD cyber program, and a GSA Schedule. A SOC 2 report does not create a CMMC status. A Schedule is a contract, not a certification.',
      items: [
        {
          id: 'buyer-assurance-path',
          title: 'Which assurance the buyer actually asked for',
          action:
            'For each pursuit, write the buyer type and the bar they named. DoD work uses CMMC and SPRS. A federal cloud subscription uses FedRAMP. A city, county, school, or commercial prime often asks for a SOC 2 Type II report. A GSA Schedule is a separate contract. Record N/A when nobody has asked yet. Do not answer a SOC 2 request with a CMMC self-assessment, or a CMMC clause with a SOC 2 report.',
          evidenceHint: 'Buyer, bar requested, and the system in scope',
          completeLabel: 'Mark complete (buyer bar on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'required',
          sourceLabel: 'DoD CMMC program',
          sourceUrl: 'https://dodcio.defense.gov/cmmc/',
          docsLabel: 'FAR 52.204-21',
          docsUrl: 'https://www.acquisition.gov/far/52.204-21',
        },
        {
          id: 'soc2-scope',
          title: 'SOC 2 system and Trust Services Criteria',
          action:
            'Name the system a SOC 2 report would cover, the period, and whether the buyer wants Type I (design at a date) or Type II (operating effectiveness over months). Security is the baseline Trust Services Criteria. Add Availability, Confidentiality, Processing Integrity, or Privacy only when a contract asks. A readiness letter from a consultant is not the CPA report. File N/A if no buyer has asked for SOC 2.',
          evidenceHint: 'System boundary, criteria in scope, Type I or Type II, or a dated N/A',
          completeLabel: 'Mark complete (scope or N/A on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          sourceLabel: 'AICPA SOC 2',
          sourceUrl: 'https://www.aicpa-cima.com/topic/audit-assurance/audit-and-assurance-greater-than-soc-2',
        },
        {
          id: 'shared-evidence-library',
          title: 'One evidence library across SOC 2 and CMMC',
          action:
            'Store access, logging, change, incident response, and risk artifacts once. Tag each file with the Trust Services Criteria it supports and, when FCI or CUI is in scope, with the matching CMMC practice. Crosswalk through NIST SP 800-53: use the AICPA Trust Services Criteria map, then NIST SP 800-171 Appendix D. Do not keep a second folder of the same screenshots. The Phase II pause does not retire the NIST SP 800-171 work a CUI contract already requires.',
          evidenceHint: 'Index of artifacts with the criterion and the CMMC practice each one supports',
          completeLabel: 'Mark complete (index on file)',
          completeRequires: 'evidence',
          gate: 'fci',
          priority: 'required',
          sourceLabel: 'NIST SP 800-171 Rev. 2',
          sourceUrl: 'https://csrc.nist.gov/pubs/sp/800/171/r2/final',
          docsLabel: 'AICPA map to NIST 800-53',
          docsUrl: 'https://www.aicpa-cima.com/resources/download/mapping-2017-trust-services-criteria-to-nist-800-53',
        },
        {
          id: 'soc2-cmmc-gaps',
          title: 'Gaps that do not travel between SOC 2 and CMMC',
          action:
            'Write the gaps for the current buyer. When CUI is in scope, a SOC 2 report still leaves FIPS-validated encryption, CUI media markings, a system security plan, session lock, and visitor escorts to close under NIST SP 800-171. CMMC does not cover the SOC 2 Availability, Processing Integrity, or Privacy criteria. FCI-only work can mark the CUI gaps N/A. Do not treat a clean SOC 2 opinion as a CMMC status under 32 CFR Part 170.',
          evidenceHint: 'Gap list with owner, or a dated FCI-only N/A',
          completeLabel: 'Mark complete (gaps or N/A on file)',
          completeRequires: 'evidence',
          gate: 'fci',
          priority: 'required',
          sourceLabel: '32 CFR Part 170',
          sourceUrl: 'https://www.ecfr.gov/current/title-32/part-170',
        },
        {
          id: 'soc2-report-custody',
          title: 'SOC 2 report custody',
          action:
            'When a CPA firm finishes the examination, record the firm, the opinion, the period covered, and where the PDF is stored. Note each subservice organization (cloud host, email, identity provider) and whether its report is inclusive or carved out. Do not upload the full report here if it describes the system in detail. A bridge letter covers only the months after the period, and only when the firm writes one.',
          evidenceHint: 'Firm, period, opinion, storage path, and subservice note — or N/A',
          completeLabel: 'Mark complete (custody note on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['soc2-scope'],
          dependencyNote: 'Needs a named system scope, or a dated note that no buyer asked for SOC 2.',
        },
        {
          id: 'gsa-mas-lifecycle',
          title: 'GSA Schedule administration after award',
          action:
            'After GSA awards a Multiple Award Schedule, name a contract administrator and keep SAM Active through final payment (FAR 52.204-7). The sales floor in GSAR 552.238-79 is $100,000 in the first five years and $125,000 in each five-year period after that. Report services by 31 October under FAR 52.204-15. Update responsibility information in SAM on the FAR 52.209-9 cadence. Re-represent size before an option under FAR 52.219-28. Accept mass modifications in eMod. Report sales the way the contract names them. Do not invent a contract number. If GSA has not awarded a Schedule, file that wait on the Schedule task and mark this N/A.',
          evidenceHint: 'Contract number GSA assigned, administrator, next report date — or N/A',
          completeLabel: 'Mark complete (admin calendar or N/A on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['gsa-schedule', 'sam-uei-cage'],
          dependencyNote: 'Needs an Active SAM record. Starts only after GSA awards the Schedule.',
          sourceLabel: 'Requirements after a MAS contract',
          sourceUrl: 'https://www.gsa.gov/sell-to-government/step-1-learn-about-government-contracting/how-to-access-contract-opportunities/help-with-mas-contracts-to-sell-to-government/requirements-after-getting-a-mas-contract',
          docsLabel: 'MAS modification guide',
          docsUrl: 'https://www.gsa.gov/system/files/MULTIPLE%20AWARD%20SCHEDULE%20%28MAS%29%20MODIFICATION%20GUIDE%20_%20REFRESH%2033_October%202026_508.pdf',
        },
        {
          id: 'gsa-taa-supply',
          title: 'Trade Agreements Act on a GSA Schedule',
          action:
            'For every product offered on a Schedule, keep the country of origin and show it is a U.S.-made or designated-country end product under FAR 52.225-5. Services follow the same Trade Agreements Act rule. Refresh the list when a supplier changes the plant. Section 889 representations stay in SAM. Labor-only offers with no products can file N/A for country of origin and still keep the 889 representation current.',
          evidenceHint: 'Country-of-origin list, or a labor-only N/A',
          completeLabel: 'Mark complete (origin list or N/A on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['gsa-schedule', 'sam-uei-cage'],
          dependencyNote: 'Applies when a Schedule offer includes products. Section 889 still lives on the SAM task.',
          sourceLabel: 'TAA on MAS',
          sourceUrl: 'https://www.gsa.gov/buy-through-us/purchasing-programs/multiple-award-schedule/help-with-mas-buying/trade-agreements-act-compliance-and-supply-chain-security-on-mas',
          docsLabel: 'FAR 52.225-5',
          docsUrl: 'https://www.acquisition.gov/far/52.225-5',
        },
      ],
    },
    {
      id: 'contract-compliance',
      label: 'Contract Compliance',
      summary: 'FAR/DFARS obligations, 889, FOCI / foreign-person access, flow-downs, and export controls.',
      items: [
        {
          id: 'clause-register',
          title: 'FAR/DFARS clause register',
          action:
            'Maintain an actionable register of clauses from each solicitation and award (cyber, 889, reporting, IP, OCI) with owner and due dates.',
          evidenceHint: 'Clause register link',
          gate: 'pursuit',
          priority: 'required',
          sourceLabel: 'Acquisition.gov DFARS',
          sourceUrl: 'https://www.acquisition.gov/dfars',
        },
        {
          id: 'section-889',
          title: 'Section 889 covered telecommunications',
          action:
            'Prohibit Huawei, ZTE, Hytera, Hikvision, Dahua, and successor covered equipment in the LLC environment and in any DoD delivery. Complete the SAM/FAR 52.204-24 / 52.204-25 representations.',
          evidenceHint: '889 representation + asset attestation',
          gate: 'entity',
          priority: 'blocking',
          sourceLabel: 'FAR 52.204-25',
          sourceUrl: 'https://www.acquisition.gov/far/52.204-25',
        },
        {
          id: 'foci-foreign-persons',
          title: 'US-person-only FCI/CUI access (FOCI)',
          action:
            'Adopt a written rule: only US persons may access DoD FCI/CUI. Foreign ICs, including any Venezuela remote staffing plan, stay off DoD systems, mailboxes, and repositories. Disclose foreign ownership, control, or influence honestly in SAM.',
          evidenceHint: 'Adopted access policy + org chart',
          gate: 'entity',
          priority: 'blocking',
          sourceLabel: 'DCSA FOCI',
          sourceUrl: 'https://www.dcsa.mil/Industrial-Security/Entity-Vetting-FOCI/',
        },
        {
          id: 'flow-down',
          title: 'Subcontractor flow-down verification',
          action:
            'Verify required cyber, 889, and ethics clauses are flowed down before any sub touches FCI. Do not use non-US-person labor on in-scope DoD work.',
          evidenceHint: 'Sample subcontract exhibit',
          gate: 'cui',
          planMin: 'advanced',
          priority: 'required',
        },
        {
          id: 'export-gov-property',
          title: 'Export control & government property checkpoints',
          action:
            'Confirm ITAR/EAR screening for technical data and government-property handling. Unclassified IT consulting still needs a "no export of technical data" default.',
          evidenceHint: 'SOP links + last review',
          gate: 'cui',
          planMin: 'enterprise',
          priority: 'required',
          sourceLabel: 'DDTC / ITAR',
          sourceUrl: 'https://www.pmddtc.state.gov/',
        },
        {
          id: 'facility-clearance',
          title: 'Facility clearance (only if classified)',
          action:
            'Write the company’s facility clearance (FCL) status in plain language: none, in process, or cleared and to what level. An owner’s personal clearance is not the company’s FCL. A personal Top Secret clearance can be tied to one contract at a time. Some notices need only a lower personnel investigation. A DD Form 254 states the security level of that contract. Do not sponsor an FCL unless a classified solicitation requires it. Unclassified DoD IT consulting files a written N/A.',
          evidenceHint: 'N/A determination or FCL package',
          gate: 'classified',
          priority: 'recommended',
          sourceLabel: 'NISS / DCSA',
          sourceUrl: 'https://www.dcsa.mil/',
        },
      ],
    },
    {
      id: 'supply-chain-assurance',
      label: 'Supply Chain Assurance',
      summary: 'Vendor risk, prohibited sources, and traceability for primes, tools, and subcontractors.',
      items: [
        {
          id: 'vendor-compliance',
          title: 'Subcontractor compliance tracking',
          action:
            'Classify key suppliers (cloud, MSP, subcontractors) by whether they will touch FCI/CUI and confirm required attestations are on file.',
          evidenceHint: 'Supplier risk list',
          gate: 'fci',
          priority: 'required',
        },
        {
          id: 'prohibited-source',
          title: 'Prohibited-source screening',
          action:
            'Run OFAC / SAM exclusions / 889 checks for in-scope suppliers and beneficial owners before onboarding.',
          evidenceHint: 'Screening log date',
          gate: 'fci',
          priority: 'required',
          sourceLabel: 'SAM exclusions',
          sourceUrl: 'https://sam.gov/content/exclusions',
        },
        {
          id: 'traceability',
          title: 'Traceability records',
          action:
            'Confirm vendor, license, and deliverable traceability for software and consulting artifacts on active contracts.',
          evidenceHint: 'Sample lot/trace or repo provenance record',
          gate: 'pursuit',
          priority: 'recommended',
        },
      ],
    },
    {
      id: 'financial-ethics',
      label: 'Financial & Ethics Controls',
      summary: 'Prefer FFP/T&M commercial items for first awards; keep timekeeping and disclosure ready.',
      items: [
        {
          id: 'dcaa-accounting',
          title: 'DCAA-aligned accounting readiness',
          action:
            'For first DoD IT awards, prefer firm-fixed-price or commercial T&M so a full DCAA-adequate cost system is not a gate. Document the decision. Do not bid cost-reimbursement until the books support it.',
          evidenceHint: 'Accounting system notes + contract-type policy',
          gate: 'pursuit',
          priority: 'required',
          sourceLabel: 'DCAA',
          sourceUrl: 'https://www.dcaa.mil/',
        },
        {
          id: 'timekeeping-billing',
          title: 'Timekeeping and billing controls',
          action:
            'Daily timekeeping by contract/CLIN, no rounding games, invoice review before submission. Required even on T&M.',
          evidenceHint: 'Control checklist sign-off',
          gate: 'pursuit',
          priority: 'required',
        },
        {
          id: 'ethics-disclosure',
          title: 'Ethics program & mandatory disclosure',
          action:
            'Confirm conflict-of-interest intake and FAR 52.203-13 mandatory disclosure path (fraud, bribery, gratuities) even as a small LLC.',
          evidenceHint: 'Disclosure SOP + training log',
          gate: 'entity',
          priority: 'required',
          sourceLabel: 'FAR 52.203-13',
          sourceUrl: 'https://www.acquisition.gov/far/52.203-13',
        },
      ],
    },
    {
      id: 'pursuit-readiness',
      label: 'Pursuit Readiness',
      summary:
        'Capability statement, SBA Small Business Search, SAM.gov and GSA, SBIR and STTR research awards, Texas ESBD and CMBL, DIR RFOs, cooperatives, city and county portals (San Antonio / Bexar examples), home-city bid boards, school-district portals, NIGP vs NAICS, APEX help, and a first-award path via subcontracting.',
      items: [
        {
          id: 'capability-statement',
          title: 'Capability statement',
          action:
            'Publish the same capability statement as a text page and as a 1-page PDF. The page is what buyers and search tools read. The PDF is the leave-behind. Put the legal name, UEI, CAGE, NAICS, PSC, phone, and email where the eye lands first. Then list only the work the company can perform, in plain language: websites, accessible sites (WCAG and Section 508), web and cloud applications, integrations, data and dashboards, automation, and AI governance when that is real work. Split past performance into two labeled lists: work this company performed, and work an owner performed before the company existed. Commercial work can be on either list when there is no government award, and the page must say which list it is. Add any set-aside status you actually hold. Match the codes to SAM. Do not copy another company’s UEI, CAGE, GSA MAS number, or OASIS+ number. Keep the PDF under 10 MB and tagged so a screen reader can read it. When DFARS 252.204-7025 is in a notice, include the SPRS CMMC UID. Army OSBP offices also accept the file through the APG eKiosk.',
          evidenceHint: 'PDF + last-reviewed date',
          gate: 'pursuit',
          priority: 'blocking',
          dependsOn: ['psc-codes'],
          dependencyNote: 'The statement should show the same PSC codes you chose for the public page.',
        },
        {
          id: 'dsbs-profile',
          title: 'SBA Small Business Search (SBS, formerly DSBS)',
          action:
            'Once SAM is Active, complete the SBA Small Business Search profile (SBS, formerly Dynamic Small Business Search) so primes can find the LLC for set-asides and subcontracting. Do not invent a profile URL.',
          evidenceHint: 'SBS profile URL',
          gate: 'pursuit',
          priority: 'required',
          dependsOn: ['sam-uei-cage'],
          sourceLabel: 'SBA Small Business Search',
          sourceUrl: 'https://search.certifications.sba.gov/',
        },
        {
          id: 'sam-opportunity-watch',
          title: 'SAM.gov opportunity watch',
          action:
            'NextCompli already polls the official Get Opportunities public API for NAICS 541512 (solicitations, combined, sources sought). Also save the same search on sam.gov so you get SAM’s own emails. Review weekly; do not bid expired or CUI-only notices until Gate 2 is ready. Do not scrape sam.gov.',
          evidenceHint: 'Console opportunity list + saved-search names',
          gate: 'pursuit',
          priority: 'required',
          sourceLabel: 'SAM.gov opportunities',
          sourceUrl: 'https://sam.gov/content/opportunities',
        },
        {
          id: 'apex-accelerator',
          title: 'APEX Accelerator counseling',
          action:
            'Book a Texas APEX Accelerator appointment for SAM registration review, capability statement critique, and first-bid strategy. No-cost government resource.',
          evidenceHint: 'Counseling date + notes',
          gate: 'pursuit',
          priority: 'recommended',
          sourceLabel: 'SBA APEX Accelerators',
          sourceUrl: 'https://www.sba.gov/counseling/local-assistance/targeted-support-and-services/#find-an-apex-accelerator',
          docsLabel: 'Find an APEX Accelerator',
          docsUrl: 'https://www.apexaccelerators.us/',
        },
        {
          id: 'saeps-vendor',
          title: 'City of San Antonio SAePS vendor',
          action:
            'Register once in the city e-procurement portal for each city you will bid. San Antonio uses SAePS for City goods and professional-services bids, including IT. One account per vendor and one notification email. Vendor Support: vendors@sanantonio.gov, 210-207-0118. Construction, architecture, and engineering use CIVCAST — IT and professional services stay on SAePS. If this company will not bid San Antonio work, file a written N/A. This does not replace SAM.gov and is not a reason to start a second SAM registration.',
          verifySteps: [
            {
              id: 'saeps-preregister',
              kind: 'do',
              text: 'Open Become a Vendor, use Chrome, and submit New Vendor Registration once. Use the LLC legal name, EIN as Employer Identification Number (not SSN), and one notification email. If Dun & Bradstreet already issued a DUNS, enter it; do not type the EIN into D-U-N-S. Fax is required: use the LLC voice number. Physical address is the company’s operating address, not a registered-agent mail drop. Do not create a second SAePS account.',
              evidenceHint: 'Vendor Submission Confirmation screenshot',
            },
            {
              id: 'saeps-credentials',
              kind: 'wait',
              text: 'Wait up to 48 business hours for Vendor Support to email the SAePS User ID and temporary password after IRS Tax ID validation. Store the User ID and password in your password manager under the LLC legal name / SAePS. Do not put the password in NextCompli, Confluence, Jira, or git. Call 210-207-0118 or email vendors@sanantonio.gov if the mail does not arrive.',
              evidenceHint: 'SAePS credentials email (password lives in the password manager, not here)',
            },
            {
              id: 'saeps-profile-nigp',
              kind: 'do',
              text: 'Log in, change the password, set the security question, finish the Vendor Information tab in one sitting, and add NIGP codes that match the work (use the NAICS crosswalk). Do not register on CIVCAST for IT or professional-services work.',
              evidenceHint: 'SAePS profile + NIGP save confirmation',
            },
          ],
          evidenceHint: 'SAePS confirmation + User ID (not the password)',
          completeLabel: 'Mark complete (SAePS profile saved)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'required',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the LLC and EIN. Independent of SAM.gov. One SAePS account per vendor. Do not use CIVCAST for IT.',
          sourceLabel: 'Become a Vendor (SAePS)',
          sourceUrl: 'https://www.sa.gov/Directory/Departments/Finance/About/Divisions/Procurement/Become-a-Vendor',
          docsLabel: 'Register as a COSA vendor (PDF)',
          docsUrl: 'https://www.sa.gov/files/assets/main/v/1/finance/documents/register-as-a-cosa-vendor.pdf',
        },
        {
          id: 'nigp-code-alignment',
          title: 'NIGP codes vs NAICS',
          action:
            'Pick NIGP class/item codes on every city, county, and CMBL profile so bid notices match IT consulting. SAM.gov uses NAICS, not NIGP. Do not pick construction or public-works codes the company cannot perform. Finish SAePS NIGP after the City User ID arrives.',
          evidenceHint: 'NIGP list saved on SAePS, CMBL, and Bexar (or a dated wait note)',
          completeLabel: 'Mark complete (NIGP codes saved)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'required',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the LLC and EIN. Independent of SAM.gov. Do not invent codes.',
          sourceLabel: 'NIGP codes (City of San Antonio)',
          sourceUrl: 'https://www.sa.gov/Directory/Departments/Finance/About/Divisions/Procurement/Become-a-Vendor',
        },
        {
          id: 'gsa-schedule',
          title: 'GSA Multiple Award Schedule',
          action:
            'A GSA Multiple Award Schedule is a contract you win after SAM.gov is Active. It is not a signup, it is not SAM registration, and it is not a SOC 2 or CMMC certification. OASIS+ is a different government-wide professional-services vehicle. It is also won after SAM is Active, and it is not a signup. Read the current solicitation and answer only when the work matches. After a Schedule award, keep the administration calendar and the Trade Agreements Act list in the SOC 2, CMMC, and GSA section. Do not say the company holds a Schedule or OASIS+ until that agency awards it. Do not copy a prime’s number onto your statement.',
          evidenceHint: 'Solicitation you will answer, GSA award, or a note that you are waiting on SAM Active',
          completeLabel: 'Mark complete (award on file or documented wait)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['sam-uei-cage'],
          dependencyNote: 'Needs an Active SAM record. Do not invent a Schedule number.',
          sourceLabel: 'Sell to government (GSA)',
          sourceUrl: 'https://www.gsa.gov/sell-to-government',
        },
        {
          id: 'esbd-watch',
          title: 'Texas ESBD bid notices',
          action:
            'Search the Electronic State Business Daily for IT consulting and professional-services solicitations. You can search and bid without paying for CMBL. Watching ESBD is not a DIR contract, not a HUB certificate, and not a SAM registration.',
          evidenceHint: 'The ESBD search you are watching, or a dated note that none match',
          completeLabel: 'Mark complete (watched search on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['tx-llc-good-standing'],
          dependencyNote: 'Independent of SAM.gov. Do not invent DIR status.',
          sourceLabel: 'Texas ESBD',
          sourceUrl: 'https://www.txsmartbuy.gov/esbd',
        },
        {
          id: 'cmbl-registration',
          title: 'Texas CMBL bidder list',
          action:
            'Register on the Comptroller Centralized Master Bidders List after the EIN if you want emailed bid notices for the class codes you pick. The published annual fee is $70. EIN is required; the Comptroller does not take an SSN. CMBL is not required to bid on ESBD and is not a DIR contract. Skip the $70 until cash can stay above the operating floor. Check VPTS (Vendor Performance Tracking System) after the first state award.',
          evidenceHint: 'CMBL receipt, or a dated note that you are skipping the $70 for now',
          completeLabel: 'Mark complete (CMBL or skip note on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['ein-irs'],
          dependencyNote: 'Needs the EIN. Independent of SAM.gov. Do not invent a CMBL vendor number.',
          sourceLabel: 'Comptroller vendor registration',
          sourceUrl: 'https://comptroller.texas.gov/purchasing/vendor/registration/',
          docsLabel: 'CMBL / HUB directory',
          docsUrl: 'https://comptroller.texas.gov/purchasing/vendor/cmbl/',
        },
        {
          id: 'tx-dir-rfo',
          title: 'Texas DIR IT contracts',
          action:
            'DIR cooperative IT contracts are won by answering a Request for Offer on ESBD / Tech4TX. CMBL email helps you see the RFO but is not required. You cannot sign up for DIR the way you register in SAM. Do not invent DIR vendor status. Answer only when the work matches.',
          evidenceHint: 'RFO you will answer, DIR award, or a note that you are watching ESBD first',
          completeLabel: 'Mark complete (award on file or documented wait)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the LLC and EIN. Do not invent a DIR contract number.',
          sourceLabel: 'How to become a DIR vendor',
          sourceUrl: 'https://dir.texas.gov/how-become-vendor',
        },
        {
          id: 'cooperative-purchasing',
          title: 'BuyBoard, TIPS, and HGACBuy',
          action:
            'BuyBoard, TIPS (Region 8 ESC), and HGACBuy are optional cooperative catalogs that cities, counties, and schools buy from. You win a seat by answering their solicitation. Registration is not an award. Do not invent a cooperative contract number. Skip until cash and capacity support a response.',
          evidenceHint: 'Coop solicitation you will answer, award, or a dated skip note',
          completeLabel: 'Mark complete (award or skip note on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          dependencyNote: 'Needs the LLC and EIN. Do not invent a BuyBoard, TIPS, or HGACBuy contract number.',
          sourceLabel: 'TIPS cooperative',
          sourceUrl: 'https://www.tips-usa.com/',
          docsLabel: 'BuyBoard',
          docsUrl: 'https://www.buyboard.com/',
        },
        {
          id: 'school-isd-portals',
          title: 'School-district vendor portals',
          action:
            'Texas independent school districts each run a vendor portal (Bonfire Interactive, IonWave, PlanetBids, or similar). Register only for districts you will serve. Registration is not an award. Store each login in your password manager. Do not put passwords here. File a written N/A if no ISD is in scope.',
          evidenceHint: 'District portal User IDs, or a dated note that no ISD is in scope yet',
          completeLabel: 'Mark complete (portals or skip note on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          sourceLabel: 'Texas school districts',
          sourceUrl: 'https://tea.texas.gov/',
        },
        {
          id: 'neighbor-county-portals',
          title: 'Neighboring county bid portals',
          action:
            'Counties next to a home county may use BidNet Direct, CivicPlus, or another board instead of that county’s own portal. Example: Hays County uses BidNet Direct beside Bexar’s Supplier Portal. Register once per portal for counties you will serve. Store logins in your password manager. Do not put passwords here. File a written N/A if only one county is in scope.',
          evidenceHint: 'Portal User IDs, or a dated note that only Bexar is in scope',
          completeLabel: 'Mark complete (portals or skip note on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          sourceLabel: 'BidNet Direct',
          sourceUrl: 'https://www.bidnetdirect.com/',
        },
        {
          id: 'bexar-bids',
          title: 'Bexar County supplier portal',
          action:
            'Register in the county supplier portal for each county you will serve. Bexar County is the South Texas example: attach only commodity codes the company can perform and the county emails bid notices for those codes. Have a W-9 ready before the county pays you. Store the portal login in your password manager. Do not put the password here. File a written N/A if this company will not serve that county.',
          evidenceHint: 'Supplier registration complete (User ID only)',
          completeLabel: 'Mark complete (Bexar registration saved)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['tx-llc-good-standing', 'ein-irs'],
          sourceLabel: 'Bexar County supplier registration',
          sourceUrl: 'https://www.bexar.org/616/Supplier-PortalVendor-Registration',
        },
        {
          id: 'helotes-bids',
          title: 'Home-city bid notices',
          action:
            'Check the home city’s finance bid-notices page. Some cities (Helotes is the example) do not mail bid notices and do not use a city e-procurement portal. Answer only when a notice matches the work. No separate vendor account is required to read a watch-only board. File a written N/A if this company will not serve that city.',
          evidenceHint: 'Notice you are watching, or a dated note that none match',
          completeLabel: 'Mark complete (watch note on file)',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['tx-llc-good-standing'],
          sourceLabel: 'City of Helotes bid notices',
          sourceUrl: 'https://helotes-tx.gov/departments/finance/bid-notices/',
        },
        {
          id: 'sbir-sttr-fit',
          title: 'SBIR or STTR fit',
          action:
            'Write whether this pursuit is SBIR, STTR, or neither. The bidder must be a for-profit U.S. small business with fewer than 500 employees. SBIR does not require a research partner. STTR requires one nonprofit research institution: the company does at least 40 percent of the work and the institution does at least 30 percent. The applicant is always the small business. A university cannot apply. Do not mark this finished for a company that is not a small business.',
          evidenceHint: 'Written fit note (SBIR, STTR, or not a fit) and employee count',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          sourceLabel: 'SBIR.gov — About',
          sourceUrl: 'https://www.sbir.gov/about',
        },
        {
          id: 'sbir-phase-i',
          title: 'SBIR or STTR Phase I proposal',
          action:
            'Phase I proves the idea can work and has a commercial path. It is usually 6 to 12 months. As of April 2026 the SBA ceiling without a waiver is $323,090, including modifications. Read the defense topic: it can set a lower cap. Submit defense proposals in DSIP. These awards are contracts, not grants. The company keeps the invention. NextCompli does not submit the proposal. Do not invent an award number. If no topic fits, file a dated note and leave the award blank.',
          evidenceHint: 'Topic number and DSIP receipt, or a dated note that no topic is open',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['sbir-sttr-fit'],
          dependencyNote: 'Write the SBIR or STTR fit before you spend a proposal on a topic.',
          sourceLabel: 'Defense SBIR/STTR Innovation Portal',
          sourceUrl: 'https://www.dodsbirsttr.mil/',
        },
        {
          id: 'sbir-phase-ii',
          title: 'SBIR or STTR Phase II prototype',
          action:
            'Phase II continues the Phase I result and builds a prototype, usually for up to two years. As of April 2026 the SBA ceiling without a waiver is $2,153,927, including modifications. A topic can set a lower cap. Only a Phase I winner is eligible unless that topic allows a direct-to-Phase II because the feasibility work is already done with other money. Defense topics often allow one follow-on Phase II per company per topic. Do not invent an award number.',
          evidenceHint: 'Phase II award or topic number, or a note that Phase I is not finished',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['sbir-phase-i'],
          dependencyNote: 'Finish the Phase I fit and proposal path before you record a Phase II.',
          sourceLabel: 'SBIR.gov — About',
          sourceUrl: 'https://www.sbir.gov/about',
        },
        {
          id: 'sbir-phase-iii',
          title: 'SBIR or STTR Phase III follow-on',
          action:
            'Phase III sells or fields the result. It is not paid with SBIR or STTR set-aside money. There is no cap on dollars, time, or how many awards. The agency should award the follow-on to the company that developed the technology, to the greatest extent practicable, including a sole-source award, when the work derives from, extends, or completes the earlier effort. Keep the data-rights clause DFARS 252.227-7018. The current protection period runs 20 years from the award date unless the parties later agree to a different period. Do not invent a contract number.',
          evidenceHint: 'Follow-on contract number from the buyer, or a note that no Phase III is open',
          completeRequires: 'evidence',
          gate: 'pursuit',
          priority: 'recommended',
          dependsOn: ['sbir-phase-i'],
          dependencyNote: 'A Phase III has to derive from a real Phase I or Phase II award. Do not open one from a blank topic.',
          sourceLabel: 'SBA SBIR/STTR policy',
          sourceUrl: 'https://www.sbir.gov/about/policies',
        },
        {
          id: 'first-subcontract-path',
          title: 'First award via subcontracting',
          action:
            'Build the first awards as a subcontractor or team member, starting with city and county work, before bidding as a prime. Many notices expect about two years of company history before a firm can prime. Read each notice for a gap this company does not hold: CMMC Level 2, an authority to operate (ATO), a facility clearance (FCL), a DD Form 254, NAICS 54151 HACS, a certification, or a cyber-insurance limit. A teaming agreement names who covers each gap. Do not put the prime’s GSA MAS or OASIS+ number on your capability statement. Read the prime’s CMMC flow-down before signing.',
          evidenceHint: 'Target-prime list + outreach log',
          gate: 'pursuit',
          priority: 'recommended',
          sourceLabel: 'DoD OSBP',
          sourceUrl: 'https://business.defense.gov/',
        },
      ],
    },
  ],
};

/** Item IDs that existed in the CompliCore DoD MVP — keep so persisted console state still maps. */
export const LEGACY_DOD_CHECKLIST_ITEM_IDS = [
  'cmmc-gap',
  'dfars-incident',
  'sprs-attestation',
  'clause-register',
  'flow-down',
  'export-gov-property',
  'vendor-compliance',
  'prohibited-source',
  'traceability',
  'dcaa-accounting',
  'timekeeping-billing',
  'ethics-disclosure',
] as const;

export const EDUCATION_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'education-compliance',
  entitlement: 'education-compliance',
  title: 'Education Compliance',
  subtitle:
    'Campus program owners for sanctions, research export, vendor trust, student privacy, campus safety, Title IX, accessibility, research integrity, and the federal reporting calendar. Case files and education records stay in the campus systems that already hold them.',
  storageKey: 'nextcompli.console.education-compliance.v1',
  notices: [
    {
      id: 'not-maxient',
      title: 'Not a student-affairs case system',
      body: 'Name the program owner, the policy, and the calendar. Conduct cases, Title IX investigations, the Clery crime log, and education records stay in the tools those offices already run.',
    },
    {
      id: 'hecvat-license',
      title: 'HECVAT stays on EDUCAUSE',
      body: 'Link the official HECVAT 4 toolkit. Do not paste EDUCAUSE questions into NextCompli. Third-party GRC engines that score HECVAT still need an EDUCAUSE license.',
      sourceLabel: 'EDUCAUSE HECVAT 4',
      sourceUrl: 'https://www.educause.edu/higher-education-community-vendor-assessment-toolkit',
    },
  ],
  pillars: [
    {
      id: 'restricted-party-screening',
      label: 'Campus Restricted-Party Screening',
      summary: 'Screen visiting scholars, MOUs, foreign universities, and shipments before the collaboration proceeds; keep cleared parties on monitoring.',
      items: [
        calendarChecklistItem('education-compliance'),
        {
          id: 'edu-rps-scholars',
          title: 'Screen visiting scholars, employees, and guests',
          action:
            'Run OFAC SDN / CAPTA screening on named persons before campus access, appointment, or stipend. Record the purpose (scholar, employee, guest lecturer).',
          evidenceHint: 'OFAC case id + purpose + decision date',
          docsLabel: 'Open OFAC screening',
          docsUrl: '/console/ofac-screening',
          sourceLabel: 'OFAC compliance',
          sourceUrl: 'https://ofac.treasury.gov/compliance',
        },
        {
          id: 'edu-rps-mous',
          title: 'Screen MOUs, foreign universities, and research partners',
          action:
            'Screen the legal entity and known aliases on every new MOU, dual-degree, or sponsored-research partnership before signature.',
          evidenceHint: 'Entity screen packet + MOU file URL',
          docsLabel: 'Open OFAC screening',
          docsUrl: '/console/ofac-screening',
          sourceLabel: 'OFAC SDN list',
          sourceUrl: 'https://ofac.treasury.gov/specially-designated-nationals-and-blocked-persons-list-sdn-human-readable-lists',
        },
        {
          id: 'edu-rps-shipments',
          title: 'Screen export shipments and research equipment',
          action:
            'Screen consignees, freight forwarders, and end users before instruments, samples, or software leave campus. Escalate possible BIS / DDTC hits.',
          evidenceHint: 'Shipment screen + ECCN / license note if any',
          docsLabel: 'Open OFAC screening',
          docsUrl: '/console/ofac-screening',
        },
        {
          id: 'edu-rps-monitoring',
          title: 'Enroll cleared parties in ongoing monitoring',
          action:
            'Subscribe scholars and partners who remain on campus or under an active MOU to list-change alerts. Do not treat a one-time clear as permanent.',
          evidenceHint: 'Monitoring subscription ids + review cadence',
          dependsOn: ['edu-rps-scholars', 'edu-rps-mous', 'edu-rps-shipments'],
          dependencyNote: 'Screen the person, the partner, and the shipment before you put them on the watch list.',
          docsLabel: 'Open OFAC live feed',
          docsUrl: '/console/ofac-live-feed',
        },
      ],
    },
    {
      id: 'research-export-cui',
      label: 'Research Export and CUI',
      summary: 'Classify FCI vs CUI vs export-controlled work and reuse the DoD console for 800-171 / CMMC evidence when a grant or contract requires it.',
      items: [
        {
          id: 'edu-cui-classify',
          title: 'Classify the research activity',
          action:
            'Record whether the project is public, FCI-only, CUI, or export-controlled before foreign-national access or cloud hosting is approved.',
          evidenceHint: 'Classification decision + contract / grant clause cites',
          docsLabel: 'Open Government Compliance',
          docsUrl: '/console/government-compliance',
          sourceLabel: 'DoD CMMC program',
          sourceUrl: 'https://dodcio.defense.gov/cmmc/',
        },
        {
          id: 'edu-cui-deemed-export',
          title: 'Document foreign-national and deemed-export access',
          action:
            'List who can reach the lab, data enclave, or source code. Record citizenship / visa status only as the export-control office already collects it.',
          evidenceHint: 'Access roster + export-control office sign-off',
          docsLabel: 'Open Government Compliance',
          docsUrl: '/console/government-compliance',
        },
        {
          id: 'edu-cui-800-171',
          title: 'Keep 800-171 / CMMC evidence when CUI is in scope',
          action:
            'If DFARS 252.204-7012 or a CMMC clause applies, complete the DoD console SPRS / SSP path. Do not invent a second CUI workbench here.',
          evidenceHint: 'DoD console company + SPRS or SSP pointer',
          docsLabel: 'Open Government Compliance',
          docsUrl: '/console/government-compliance',
          sourceLabel: 'NIST SP 800-171',
          sourceUrl: 'https://csrc.nist.gov/pubs/sp/800/171/r3/final',
        },
        {
          id: 'edu-cui-country-alerts',
          title: 'Watch comprehensively sanctioned destinations',
          action:
            'Flag collaborations, travel, and shipments involving comprehensively sanctioned countries before the research office commits.',
          evidenceHint: 'Country-alert log + OFAC program page',
          docsLabel: 'Open OFAC CAPTA lists',
          docsUrl: '/console/ofac-capta',
        },
      ],
    },
    {
      id: 'hecvat-vendor-trust',
      label: 'HECVAT and Vendor Trust',
      summary: 'Collect campus HECVAT packets from EdTech vendors and keep NextCompli’s own campus assurance pack current. Do not embed the EDUCAUSE workbook.',
      items: [
        {
          id: 'edu-hecvat-toolkit',
          title: 'Use the official EDUCAUSE HECVAT 4 toolkit',
          action:
            'Download Full / Lite from EDUCAUSE. Store completed vendor workbooks in the institution’s evidence store; do not copy questions into this product.',
          evidenceHint: 'Toolkit version (e.g. 4.1.6) + storage location',
          docsLabel: 'EDUCAUSE HECVAT toolkit',
          docsUrl: 'https://www.educause.edu/higher-education-community-vendor-assessment-toolkit',
          sourceLabel: 'EDUCAUSE HECVAT',
          sourceUrl: 'https://www.educause.edu/higher-education-community-vendor-assessment-toolkit',
        },
        {
          id: 'edu-hecvat-inventory',
          title: 'Inventory EdTech vendors that touch student, aid, or research data',
          action:
            'Track whether each vendor has a current HECVAT, SOC 2, DPA, and data-flow description. Mark gaps before renewal.',
          evidenceHint: 'Vendor inventory URL + last-reviewed date',
        },
        {
          id: 'edu-hecvat-nextcompli-pack',
          title: 'Keep the NextCompli campus assurance pack current',
          action:
            'Review the public Trust Center and campus pack before an RFP. Completed HECVAT for a specific campus deal stays sales-assisted in v1.',
          evidenceHint: 'Pack URL + last-reviewed date',
          docsLabel: 'Campus assurance pack',
          docsUrl: '/trust/nextcompli-campus-assurance-pack.md',
        },
        {
          id: 'edu-hecvat-trust-center',
          title: 'Point campus reviewers at /trust',
          action:
            'Share the public Trust Center (frameworks, security contact, campus pack) instead of ad-hoc PDF dumps.',
          evidenceHint: 'Trust Center URL in the RFP response',
          docsLabel: 'Trust Center',
          docsUrl: '/trust',
        },
      ],
    },
    {
      id: 'ferpa-glba-program',
      label: 'FERPA and GLBA Program',
      summary: 'Name owners for FERPA notice, 45-day access, disclosure log, and GLBA Safeguards. This is program hygiene, not a registrar or aid portal.',
      items: [
        {
          id: 'edu-ferpa-notice',
          title: 'Assign the FERPA annual-notice owner',
          action:
            'Confirm who publishes the annual FERPA notice and where students find it. NextCompli does not host education records.',
          evidenceHint: 'Owner name + notice URL',
          sourceLabel: 'Student Privacy Policy Office',
          sourceUrl: 'https://studentprivacy.ed.gov/',
        },
        {
          id: 'edu-ferpa-access',
          title: 'Document the 45-day records-access path',
          action:
            'Record intake, identity check, and SLA for eligible-student / parent inspection requests under 34 CFR Part 99.',
          evidenceHint: 'Runbook URL + sample ticket (no student PII in this console)',
        },
        {
          id: 'edu-ferpa-disclosure-log',
          title: 'Keep a 34 CFR 99.31 disclosure log owner',
          action:
            'Name the office that logs non-consensual FERPA disclosures. Capture the log location, not the log contents.',
          evidenceHint: 'Owner + log system name',
        },
        {
          id: 'edu-glba-wisp',
          title: 'Map GLBA Safeguards for Title IV schools',
          action:
            'Title IV institutions need a written information-security program under 16 CFR 314. Point at the WISP and vendor-oversight owner (HECVAT helps; it is not the whole rule).',
          evidenceHint: 'WISP URL + FSA cyber contact',
          sourceLabel: 'FSA GLBA cybersecurity update',
          sourceUrl:
            'https://fsapartners.ed.gov/knowledge-center/library/electronic-announcements/2023-02-09/updates-gramm-leach-bliley-act-cybersecurity-requirements',
        },
        {
          id: 'edu-ferpa-breach',
          title: 'Name the student-record breach owner',
          action:
            'Record who decides a FERPA or state breach notice, and where the incident log lives. Do not paste student names into this console.',
          evidenceHint: 'Owner + incident-log system name',
          sourceLabel: 'Student Privacy Policy Office',
          sourceUrl: 'https://studentprivacy.ed.gov/security',
        },
      ],
    },
    {
      id: 'campus-safety-program',
      label: 'Campus Safety Program',
      summary: 'Name owners for the Clery report, timely warnings, and VAWA prevention. The crime log stays in the campus safety system.',
      items: [
        {
          id: 'edu-clery-asr',
          title: 'Assign the Clery annual security report owner',
          action:
            'Confirm who publishes the annual security report and the fire-safety report, and the date students and employees can find them.',
          evidenceHint: 'Owner + report URL + publish date',
          sourceLabel: 'Clery Act appendix',
          sourceUrl: 'https://www2.ed.gov/admins/lead/safety/campus.html',
        },
        {
          id: 'edu-clery-warnings',
          title: 'Separate timely warnings from emergency notifications',
          action:
            'Name who issues a Clery timely warning and who issues an emergency notification. Record the geography the campus uses. Do not copy the crime log here.',
          evidenceHint: 'Owner names + geography note',
        },
        {
          id: 'edu-vawa-training',
          title: 'Keep the VAWA prevention-training calendar',
          action:
            'Record the annual training for dating violence, domestic violence, sexual assault, and stalking, and who receives it.',
          evidenceHint: 'Training calendar URL + last completion date',
          sourceLabel: 'Clery and VAWA',
          sourceUrl: 'https://www2.ed.gov/admins/lead/safety/campus.html',
        },
        {
          id: 'edu-hazing-report',
          title: 'Name the hazing-transparency owner',
          action:
            'The Stop Campus Hazing Act adds a hazing report to the Clery package. Name the office that publishes it and where the report will live.',
          evidenceHint: 'Owner + report location',
        },
      ],
    },
    {
      id: 'title-ix-program',
      label: 'Title IX Program',
      summary: 'Name the coordinator, the policy, and the training calendar. Investigations stay in the campus case system.',
      items: [
        {
          id: 'edu-tix-coordinator',
          title: 'Publish the Title IX coordinator',
          action:
            'Record the coordinator’s name, office, and the public policy URL. Do not store complaints or party names here.',
          evidenceHint: 'Coordinator + policy URL',
          sourceLabel: 'Title IX',
          sourceUrl: 'https://www.ed.gov/laws-and-policy/civil-rights-laws/sex-discrimination/title-ix-and-sex-discrimination',
        },
        {
          id: 'edu-tix-training',
          title: 'Keep Title IX training current',
          action:
            'Record the training year for the coordinator, investigators, and decision-makers, and where the materials are filed.',
          evidenceHint: 'Training year + materials location',
        },
        {
          id: 'edu-tix-athletics',
          title: 'Name the athletics equity owner',
          action:
            'Confirm who files the equity-in-athletics disclosure and who reviews participation and aid. This is a calendar, not a roster.',
          evidenceHint: 'Owner + last filing year',
          sourceLabel: 'Equity in Athletics',
          sourceUrl: 'https://ope.ed.gov/athletics/',
        },
      ],
    },
    {
      id: 'accessibility-civil-rights',
      label: 'Accessibility and Civil Rights',
      summary: 'Name owners for ADA, Section 504, and public digital access. Accommodation files stay in the disability office.',
      items: [
        {
          id: 'edu-504-owner',
          title: 'Name the Section 504 and ADA coordinator',
          action:
            'Record who receives accommodation requests and where the public notice is posted. Do not store medical or accommodation files here.',
          evidenceHint: 'Coordinator + notice URL',
          sourceLabel: 'ADA and Section 504',
          sourceUrl: 'https://www.ed.gov/laws-and-policy/civil-rights-laws/disability-discrimination',
        },
        {
          id: 'edu-508-digital',
          title: 'Assign a public-website accessibility owner',
          action:
            'Name who checks new public pages and course platforms against WCAG, and where the last review is filed.',
          evidenceHint: 'Owner + last review date',
        },
        {
          id: 'edu-minors-program',
          title: 'Name the minors-on-campus program owner',
          action:
            'Record who approves camps and programs that host minors, and where the background-check and supervision rules live.',
          evidenceHint: 'Owner + policy URL',
        },
      ],
    },
    {
      id: 'research-integrity',
      label: 'Research Integrity',
      summary: 'Name owners for human-subjects review, conflicts, and misconduct policy. Participant files stay with the IRB.',
      items: [
        {
          id: 'edu-irb-owner',
          title: 'Name the IRB or human-subjects owner',
          action:
            'Confirm which board reviews research with people and where the policy is published. Do not paste protocols or participant lists here.',
          evidenceHint: 'Board name + policy URL',
          sourceLabel: 'HHS human subjects',
          sourceUrl: 'https://www.hhs.gov/ohrp/regulations-and-policy/regulations/common-rule/index.html',
        },
        {
          id: 'edu-coi-research',
          title: 'Keep the research conflict-of-interest disclosure',
          action:
            'Name who collects investigator disclosures before a federal award, and the review cadence.',
          evidenceHint: 'Owner + last review date',
          sourceLabel: 'NIH financial conflict of interest',
          sourceUrl: 'https://grants.nih.gov/grants/policy/coi/index.htm',
        },
        {
          id: 'edu-misconduct-policy',
          title: 'Point at the research-misconduct policy',
          action:
            'Record the policy URL and the research-integrity officer. Allegations stay in that office’s file.',
          evidenceHint: 'Policy URL + officer name',
        },
      ],
    },
    {
      id: 'federal-reporting-calendar',
      label: 'Federal Reporting Calendar',
      summary: 'One calendar for IPEDS, foreign-gift reporting, consumer information, and distance-education authorization.',
      items: [
        {
          id: 'edu-ipeds',
          title: 'Assign the IPEDS filing owner',
          action:
            'Name who files each IPEDS survey and where the lock dates are tracked.',
          evidenceHint: 'Owner + current collection calendar',
          sourceLabel: 'IPEDS',
          sourceUrl: 'https://nces.ed.gov/ipeds/',
        },
        {
          id: 'edu-section-117',
          title: 'Name the HEA foreign-gift reporting owner',
          action:
            'Section 117 requires reporting of certain foreign gifts and contracts. Record who totals them and the last filing.',
          evidenceHint: 'Owner + last filing period',
          sourceLabel: 'HEA Section 117',
          sourceUrl: 'https://fsapartners.ed.gov/knowledge-center/topics/section-117-foreign-gift-and-contract-reporting',
        },
        {
          id: 'edu-consumer-info',
          title: 'Publish the consumer-information owner',
          action:
            'Confirm who keeps the HEA consumer-information page current: prices, outcomes, and required notices.',
          evidenceHint: 'Owner + page URL',
        },
        {
          id: 'edu-distance-auth',
          title: 'Record distance-education authorization',
          action:
            'If the institution teaches students outside its home state, name who tracks state authorization or NC-SARA participation.',
          evidenceHint: 'Home state + authorization or SARA note',
          sourceLabel: 'NC-SARA',
          sourceUrl: 'https://nc-sara.org/',
        },
      ],
    },
    {
      id: 'program-governance',
      label: 'Program Governance',
      summary: 'A named committee, role-based training, and a board report. This is how a campus shows the program is operating.',
      items: [
        {
          id: 'edu-committee',
          title: 'Name the compliance committee and its cadence',
          action:
            'Record who sits on the committee, how often it meets, and where minutes are kept. Minutes should not contain student education records.',
          evidenceHint: 'Chair + meeting cadence + minutes location',
        },
        {
          id: 'edu-training-matrix',
          title: 'Keep a role-based training matrix',
          action:
            'Map FERPA, Clery, Title IX, GLBA, and research-export training to the roles that need them, with a renewal date.',
          evidenceHint: 'Matrix URL + renewal year',
        },
        {
          id: 'edu-board-report',
          title: 'Schedule the board or audit-committee report',
          action:
            'Name who reports open gaps, overdue filings, and training completion. The report is counts and owners, not case files.',
          evidenceHint: 'Report owner + last report date',
        },
        {
          id: 'edu-hipaa-clinic',
          title: 'Say whether a campus clinic is in HIPAA scope',
          action:
            'If a clinic or health plan holds treatment records, name the privacy officer and the policy. If the campus has no covered clinic, record that the scope is none.',
          evidenceHint: 'In scope or not + privacy officer if yes',
          sourceLabel: 'HIPAA',
          sourceUrl: 'https://www.hhs.gov/hipaa/for-professionals/privacy/index.html',
        },
      ],
    },
  ],
};

export const BUSINESS_CREATION_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'business-creation-compliance',
  entitlement: 'business-creation-compliance',
  title: 'Organization',
  subtitle:
    'Name the organization first. A business, a nonprofit, and a campus all start here. Then answer one question at a time. The answers become the filing, tax, license, and report steps.',
  storageKey: 'nextcompli.console.business-creation-compliance.v1',
  notices: [
    {
      id: 'not-a-filing-service',
      title: 'A checklist, not a filing desk',
      body: 'The questions write the steps for this company. NextCompli does not send articles, an EIN application, or a license to a government office.',
    },
    {
      id: 'not-legal-advice',
      title: 'Confirm the state rule before you file',
      body: 'Fees, forms, and deadlines differ by state. Check the Secretary of State site, or ask counsel, before you treat a step as finished.',
    },
  ],
  pillars: [
    {
      id: 'formation-calendar',
      label: 'Dates',
      summary: 'Put the state report and the registered-agent renewal on this calendar. The interview still writes the filing steps.',
      items: [calendarChecklistItem('business-creation-compliance')],
    },
  ],
};

export const WILLS_TRUSTS_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'wills-trusts-compliance',
  entitlement: 'wills-trusts-compliance',
  title: 'Wills & Trusts',
  subtitle:
    'Name the person first. Then answer one question at a time. The answers become the checklist for a will or a revocable trust. A lawyer licensed in that state writes and reviews the papers. NextCompli does not.',
  storageKey: 'nextcompli.console.wills-trusts-compliance.v1',
  notices: [
    {
      id: 'not-a-law-firm',
      title: 'A checklist, not a law firm',
      body: 'The questions write the steps for this person. NextCompli does not write a will or a trust, notarize a signature, or file anything with a probate court.',
    },
    {
      id: 'lawyer-reviews',
      title: 'A lawyer in that state reviews the papers before anyone signs',
      body: 'Signing rules differ by state. Do not copy another state’s witness rule. Do not sign until a lawyer licensed in the person’s state has reviewed the papers.',
    },
  ],
  pillars: [
    {
      id: 'wills-calendar',
      label: 'Dates',
      summary: 'Put the signing appointment and the next review on this calendar. The interview still writes the plan steps.',
      items: [calendarChecklistItem('wills-trusts-compliance')],
    },
  ],
};

const cloudCore = (
  id: string,
  title: string,
  action: string,
  evidenceHint: string,
  extra: Partial<ComplianceChecklistItemDef> = {},
): ComplianceChecklistItemDef => ({
  id,
  title,
  action,
  evidenceHint,
  priority: 'required',
  ...extra,
});

export const CLOUD_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'cloud-compliance',
  entitlement: 'cloud-compliance',
  title: 'Cloud Compliance',
  subtitle:
    'CompliCore is the written checklist for the client account. CompliAdvantage adds a once-a-day read-only review after the client grants a narrow audit role. This console does not use the NextCompli operations account, and it does not crawl the public website.',
  storageKey: 'nextcompli.console.cloud-compliance.v1',
  notices: [
    {
      id: 'client-cloud-not-ops',
      title: 'The client cloud account is not the NextCompli operations account',
      body:
        'Website Compliance reads public pages only. Cloud Compliance needs access the client grants to their own cloud account. Do not point this console at the account that runs NextCompli.',
    },
    {
      id: 'checklist-then-agent',
      title: 'The checklist is CompliCore. The daily review is CompliAdvantage.',
      body:
        'Mark each step finished yourself. The daily review writes findings. It does not change the account, and it does not mark a step done. A resource the review cannot see is not a pass.',
    },
  ],
  pillars: [
    {
      id: 'connect-cloud',
      label: 'Connect the client account',
      summary: 'Name the client account and record the read-only access they granted.',
      items: [
        calendarChecklistItem('cloud-compliance'),
        cloudCore(
          'name-client-cloud',
          'Name the client cloud account',
          'Record the client account name and the cloud it runs in: Amazon Web Services, Microsoft Azure, or Google Cloud. One client, one account. Do not reuse the NextCompli operations account.',
          'Account name and cloud, saved in the company file',
          { priority: 'blocking' },
        ),
        cloudCore(
          'grant-read-only',
          'Record the read-only access the client granted',
          'Ask the client for read-only access to that account. On AWS, a customer-managed read role trusted only by the reviewer, with an external id. On Azure, Reader plus Security Reader. On Google Cloud, Security Reviewer, not Owner. Save who granted it and the date. Do not store passwords or secret keys in this checklist.',
          'Who granted access, and the date',
          { priority: 'blocking', dependsOn: ['name-client-cloud'] },
        ),
        cloudCore(
          'review-boundary',
          'Confirm what the access can see',
          'Write down which resources the read-only access can see, and which it cannot. A review that cannot see a resource is not a pass for that resource.',
          'List of what the access can and cannot see',
          { dependsOn: ['grant-read-only'] },
        ),
      ],
    },
    {
      id: 'identity-access',
      label: 'Sign-in and permissions',
      summary: 'Only the people who need it can change the account, and they sign in with a second factor.',
      items: [
        cloudCore(
          'mfa-privileged',
          'MFA on every administrator',
          'Every person who can change the account signs in with MFA. On AWS that includes the root user and every console user. On Azure it includes every privileged role. On Google Cloud it includes every account that can change the project. A password alone is not enough.',
          'Date MFA was confirmed for administrators',
        ),
        cloudCore(
          'root-unused',
          'The account owner is not used for daily work',
          'The AWS root user, the Azure account owner, and the Google Cloud organization owner are not used for everyday changes. Daily work uses a named person with a smaller role. File the date the owner last signed in.',
          'Date the owner last signed in, and who does daily work',
        ),
        cloudCore(
          'no-long-lived-keys',
          'People do not keep long-lived access keys',
          'People sign in as themselves. Workloads use a role, a managed identity, or a service account. Any access key older than 90 days is removed or replaced. Do not paste a key into this checklist.',
          'Count of user access keys older than 90 days',
        ),
        cloudCore(
          'least-privilege-review',
          'Review who can change the account',
          'At least once a year, list who can administer the account and remove anyone who no longer needs it. On AWS look at users, groups, and roles. On Azure look at role assignments. On Google Cloud look at project and organization bindings.',
          'Review date and the list of administrators',
        ),
        cloudCore(
          'admin-sign-in-restricted',
          'Administrator sign-in is not open to the whole internet',
          'Console and API administration is limited to the company network, a known identity provider, or a private endpoint. A password policy requires length and rotation where the cloud still uses passwords.',
          'How administrator sign-in is limited',
        ),
      ],
    },
    {
      id: 'audit-logging',
      label: 'Audit logging',
      summary: 'Admin changes are recorded, kept, and alarmed.',
      items: [
        cloudCore(
          'audit-log-on',
          'An audit log records admin changes',
          'Turn on a log of management changes in every region the account uses. On AWS that is CloudTrail. On Azure it is the Activity Log. On Google Cloud it is Cloud Audit Logs, including data access where the workload handles customer data.',
          'Where the audit log is on, and the regions it covers',
        ),
        cloudCore(
          'audit-log-retained',
          'Audit logs are kept and protected',
          'Keep the audit log at least a year. The people the log records cannot delete it. On AWS, log-file validation is on and the bucket blocks public access. On Azure, the log workspace is locked. On Google Cloud, the log bucket is retained and not public.',
          'Retention period and where the log is stored',
        ),
        cloudCore(
          'audit-alarms',
          'An alarm fires when logging or sign-in changes',
          'An alarm reaches a person when someone turns logging off, changes the network, or changes who can sign in. A silent log is not a control.',
          'Alarm name and who receives it',
        ),
      ],
    },
    {
      id: 'network-exposure',
      label: 'Network exposure',
      summary: 'Admin ports and default networks are not left open.',
      items: [
        cloudCore(
          'no-open-admin-ports',
          'Remote admin ports are not open to the whole internet',
          'Ports 22 and 3389 are not open to 0.0.0.0/0 or ::/0. Administration uses a private network, a bastion with MFA, or a managed identity-aware proxy.',
          'Security-group or firewall rule that still allows those ports, or a note that none do',
        ),
        cloudCore(
          'flow-logs',
          'Network flow logs are on',
          'Flow logs are on for the networks that hold the workload. On AWS that is VPC flow logs. On Azure it is Network Watcher. On Google Cloud it is VPC Flow Logs.',
          'Networks that have flow logs, and where the logs land',
        ),
        cloudCore(
          'default-network-closed',
          'The default network is not left open',
          'The cloud default network, default security group, and default storage are not used for the workload, or they are locked so they accept no public traffic.',
          'What was done with the default network and default storage',
        ),
      ],
    },
    {
      id: 'data-protection',
      label: 'Data protection',
      summary: 'Storage is encrypted, private, backed up, and restorable.',
      items: [
        cloudCore(
          'encryption-at-rest',
          'Storage and databases encrypt data at rest',
          'Object storage, disks, and databases encrypt data at rest. Record whether the key is the cloud default or a customer-managed key, and who can use that key.',
          'What is encrypted, and who controls the key',
        ),
        cloudCore(
          'no-public-storage',
          'Storage is not public',
          'Buckets, blobs, and disks are not public. Public access blocks are on. A file that must be public is named, owned, and reviewed.',
          'Public-access setting, and any exception with an owner',
        ),
        cloudCore(
          'storage-in-transit',
          'Storage requires an encrypted connection',
          'Clients reach storage over TLS. On AWS, the bucket denies requests that are not secure. On Azure, secure transfer is required. On Google Cloud, the bucket is not served over plain HTTP.',
          'Setting that refuses a plain connection',
        ),
        cloudCore(
          'backups-restore-tested',
          'Backups exist and a restore was tested',
          'Databases and the data the business cannot lose have a backup. A restore was tested in the last year. A backup that has never been restored is not proof.',
          'Backup schedule and the date of the last restore test',
        ),
        cloudCore(
          'key-rotation',
          'Customer-managed keys rotate',
          'If the client uses their own encryption key, it rotates on a schedule and the previous key is retired. Record the last rotation date.',
          'Key name and last rotation date, or a note that the cloud default key is used',
        ),
      ],
    },
    {
      id: 'findings-inventory',
      label: 'Findings and inventory',
      summary: 'A findings service is on, critical findings have an owner, and the inventory matches the account.',
      items: [
        cloudCore(
          'findings-service-on',
          'A findings service is on',
          'Turn on the cloud findings service for this account. On AWS that is Security Hub. On Azure it is Microsoft Defender for Cloud. On Google Cloud it is Security Command Center. These are the places operators compare against the CIS Foundations Benchmark for that cloud. NextCompli does not copy those benchmark texts.',
          'Service name and the date it was last checked',
        ),
        cloudCore(
          'critical-finding-owner',
          'Each critical finding has an owner and a date',
          'Every critical or high finding has a person and a due date, or a written reason it is accepted. An unread findings page is not a pass.',
          'Open critical findings, each with an owner',
        ),
        cloudCore(
          'inventory-current',
          'The inventory matches the account',
          'A current list of accounts, projects, and subscriptions exists. A resource in a region or project that the review cannot see is named as unseen, not as passed.',
          'Inventory date and any account the review cannot see',
        ),
      ],
    },
    {
      id: 'cloud-agent',
      label: 'Daily read-only review',
      summary: 'CompliAdvantage. The agent looks. A person decides.',
      items: [
        cloudCore(
          'record-readonly-role',
          'Record the read-only role',
          'Save the role name the client granted. AWS: a read role trusted only by the reviewer, with an external id, not a stored access key. Azure: Reader and Security Reader. Google Cloud: Security Reviewer. Do not paste a password or a secret key.',
          'Role name, cloud, and the date it was granted',
          { planMin: 'advanced', priority: 'blocking', dependsOn: ['grant-read-only'] },
        ),
        cloudCore(
          'daily-readonly-review',
          'Queue the read-only review once a day',
          'Queue one review a day for that role. The review reads identity, logging, network, storage, encryption, backup, and findings. It does not change the account. It does not use the NextCompli operations account. It does not mark these steps done.',
          'Date of the last queued review',
          { planMin: 'advanced', dependsOn: ['record-readonly-role'] },
        ),
        cloudCore(
          'person-reviews-findings',
          'A person reads the day’s findings',
          'A person reads what the review found and files proof on the matching CompliCore step. Low-confidence and unseen resources stay open. The agent does not send a change to the cloud.',
          'Who reviewed the findings, and the date',
          { planMin: 'advanced', dependsOn: ['daily-readonly-review'] },
        ),
      ],
    },
  ],
};

export const OFAC_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'ofac-compliance',
  entitlement: 'ofac-compliance',
  title: 'OFAC Compliance',
  subtitle:
    'One checklist for sanctions screening. Refresh the SDN and CAPTA lists, import names, run Screen now, then decide each possible match. Investigations and the whitelist wait on that decision. This page does not replace the screening tools.',
  storageKey: 'nextcompli.console.ofac-compliance.v1',
  notices: [
    {
      id: 'ofac-not-counsel',
      title: 'A checklist, not a sanctions opinion',
      body:
        'NextCompli screens names against OFAC lists and keeps the decision on file. It does not tell you that a payment, a shipment, or a hire is lawful. Ask counsel when a match is real.',
    },
    {
      id: 'ofac-tools-stay',
      title: 'The tools stay one click from the task',
      body:
        'Screening, CAPTA lists, Investigations, the whitelist, and Live Feed are the places you do the work. This page shows what is done, what is blocked, and what is still open.',
    },
  ],
  pillars: [
    {
      id: 'ofac-lists',
      label: 'List coverage',
      summary: 'Confirm the SDN list and the CAPTA list refreshed before any name is screened.',
      items: [
        calendarChecklistItem('ofac-compliance'),
        {
          id: 'ofac-refresh-lists',
          title: 'Refresh the SDN and CAPTA lists',
          action:
            'Open Live Feed and confirm the SDN list and the CAPTA list both show a successful refresh date. Screening waits on that date.',
          evidenceHint: 'Refresh date for the SDN list and the CAPTA list',
          priority: 'blocking',
          sourceLabel: 'Open Live Feed',
          sourceUrl: '/console/ofac-live-feed',
          docsLabel: 'Open CAPTA lists',
          docsUrl: '/console/ofac-capta',
        },
      ],
    },
    {
      id: 'ofac-screening',
      label: 'Name screening',
      summary: 'Import the names for this account, then run Screen now.',
      items: [
        {
          id: 'ofac-import-subjects',
          title: 'Import the names to screen',
          action:
            'On Screening, import the people and companies this account must check. Use the legal name. Leave unknown dates and addresses blank.',
          evidenceHint: 'Imported subject count on Screening',
          priority: 'blocking',
          dependsOn: ['ofac-refresh-lists'],
          dependencyNote: 'The SDN and CAPTA refresh dates come first.',
          sourceLabel: 'Open Screening',
          sourceUrl: '/console/ofac-screening',
        },
        {
          id: 'ofac-screen-now',
          title: 'Run Screen now',
          action:
            'On Screening, run Screen now for the imported names. Read each result before you clear a name.',
          evidenceHint: 'Screen now completion for this account',
          priority: 'required',
          dependsOn: ['ofac-import-subjects'],
          dependencyNote: 'Import the names before Screen now.',
          sourceLabel: 'Open Screening',
          sourceUrl: '/console/ofac-screening',
        },
      ],
    },
    {
      id: 'ofac-review',
      label: 'Review queue',
      summary: 'Decide every possible match before an investigation or a whitelist entry.',
      items: [
        {
          id: 'ofac-review-matches',
          title: 'Decide each possible match',
          action:
            'On Screening, clear each possible match with a written reason, or escalate it. An open possible match stays blocked.',
          evidenceHint: 'Decision note for each possible match',
          priority: 'required',
          dependsOn: ['ofac-screen-now'],
          dependencyNote: 'Screen now has to finish before the queue can be decided.',
          sourceLabel: 'Open Screening',
          sourceUrl: '/console/ofac-screening',
        },
      ],
    },
    {
      id: 'ofac-investigations',
      label: 'Investigations',
      summary: 'Apply the 50% rule when a review is escalated.',
      items: [
        {
          id: 'ofac-investigation',
          title: 'Investigate an escalated match',
          action:
            'When a review is escalated, open Investigations and apply the 50% rule to owners and related companies. Record the decision in the case. Skip this step when nothing was escalated and note that.',
          evidenceHint: 'Investigation decision, or a note that nothing was escalated',
          priority: 'required',
          dependsOn: ['ofac-review-matches'],
          dependencyNote: 'The review decision comes first.',
          sourceLabel: 'Open Investigations',
          sourceUrl: '/console/ofac-investigations',
        },
      ],
    },
    {
      id: 'ofac-whitelist',
      label: 'Whitelist',
      summary: 'Record a confirmed false positive after the review.',
      items: [
        {
          id: 'ofac-whitelist-false-positive',
          title: 'Whitelist a confirmed false positive',
          action:
            'When a review confirms a false positive, add that name on the whitelist with the reason and the date. Skip this step when there is no false positive and note that. The next screen still runs.',
          evidenceHint: 'Whitelist entry with reason and date, or a note that none applied',
          priority: 'required',
          dependsOn: ['ofac-review-matches'],
          dependencyNote: 'The review has to confirm the false positive first.',
          sourceLabel: 'Open whitelist',
          sourceUrl: '/console/ofac-whitelist',
        },
      ],
    },
    {
      id: 'ofac-live-feed',
      label: 'Live feed',
      summary: 'Keep official OFAC sources on a schedule after the lists are refreshing.',
      items: [
        {
          id: 'ofac-live-feed-schedule',
          title: 'Schedule the official OFAC feed',
          action:
            'On Live Feed, keep the official OFAC sources on a schedule so the next SDN and CAPTA refresh is not a one-time check. CompliAdvantage includes this step.',
          evidenceHint: 'Live Feed schedule, or the last automatic refresh time',
          priority: 'recommended',
          planMin: 'advanced',
          dependsOn: ['ofac-refresh-lists'],
          dependencyNote: 'Confirm one successful SDN and CAPTA refresh before you rely on the schedule.',
          sourceLabel: 'Open Live Feed',
          sourceUrl: '/console/ofac-live-feed',
        },
      ],
    },
  ],
};

export const AI_COMPLIANCE_CONSOLE: ComplianceConsoleDefinition = {
  kind: 'ai-compliance',
  entitlement: 'ai-compliance',
  title: 'AI Compliance',
  subtitle:
    'An inventory, a written use rule, and a named owner for the AI tools a company already runs. CompliCore keeps the inventory and the rule. CompliAdvantage adds vendor terms, human review, and a quarterly check. CompliComplete adds an impact note when a system affects a person. This is not an ISO/IEC 42001 certificate and it is not a NIST AI RMF score.',
  storageKey: 'nextcompli.console.ai-compliance.v1',
  notices: [
    {
      id: 'not-a-certificate',
      title: 'A record, not a certificate',
      body:
        'NIST AI RMF 1.0 is a voluntary guide: govern, map, measure, and manage. ISO/IEC 42001 is a management-system standard a company can seek later. The European Union AI Act is law for some uses whose output is used in the European Union. This console stores the inventory, the rule, and the owner. NextCompli does not certify any of those, and it does not decide that a tool is allowed.',
      sourceLabel: 'NIST AI RMF 1.0',
      sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
    },
    {
      id: 'ai-plan-ladder',
      title: 'Coverage deepens by subscription',
      body:
        'CompliCore is the inventory, the one-page use rule, the data that never goes into a public model, and a named owner. CompliAdvantage adds the vendor terms, a person reviewing the output before a decision, a way to turn the system off, and a quarterly review. CompliComplete adds an impact note for systems that affect a person, plus the federal and European Union scope questions. None of the plans mark the company certified.',
      sourceLabel: 'NIST AI RMF to ISO/IEC 42001 crosswalk',
      sourceUrl: 'https://airc.nist.gov/docs/NIST_AI_RMF_to_ISO_IEC_42001_Crosswalk.pdf',
    },
  ],
  pillars: [
    {
      id: 'ai-inventory-rule',
      label: 'Inventory and use rule',
      summary: 'List every AI tool, name who owns the list, and write what data never goes into a public model.',
      items: [
        calendarChecklistItem('ai-compliance'),
        {
          id: 'ai-inventory',
          title: 'AI tool inventory',
          action:
            'List every AI tool, including the AI features inside software you already own. For each one, write what it drafts or decides, what data it can see, whose account it uses, and the vendor. NIST AI RMF asks for this inventory. Do not paste customer text, prompts, or model output into this row. A name and a link are enough.',
          evidenceHint: 'Inventory with tool, owner account, data it can see, and vendor',
          completeRequires: 'evidence',
          priority: 'required',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'ai-owner',
          title: 'Named owner',
          action:
            'Name one person who keeps the inventory and the use rule. A small company can name the same person who already owns security. Write how the team reports a new tool before someone starts using it.',
          evidenceHint: 'Owner name and the date the team was told',
          completeRequires: 'evidence',
          priority: 'required',
          dependsOn: ['ai-inventory'],
          dependencyNote: 'Start the inventory so the owner has a list to keep.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'ai-acceptable-use',
          title: 'One-page acceptable-use rule',
          action:
            'Write which tools are allowed and which data never goes into an outside model: customer personal data, health records, payment cards, government contract files, and passwords. Say when a person must read the output before it is used. Keep the rule to one page.',
          evidenceHint: 'Dated use-rule page',
          completeRequires: 'evidence',
          priority: 'required',
          dependsOn: ['ai-owner'],
          dependencyNote: 'Name the owner before you publish the rule they have to keep.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
      ],
    },
    {
      id: 'ai-vendor-review',
      label: 'Vendor review and human check',
      summary: 'On CompliAdvantage, keep vendor terms, a human check before a decision, and a way to stop a bad system.',
      items: [
        {
          id: 'ai-vendor-terms',
          title: 'Vendor terms for tools that see private data',
          action:
            'For each tool that can see customer or employee data, keep the vendor page that says whether they train on that data, how long they keep it, and who they share it with. If the vendor cannot say, do not put private data in that tool.',
          evidenceHint: 'Saved vendor page or contract clause, dated',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['ai-inventory'],
          dependencyNote: 'The inventory names the vendors this review has to cover.',
          sourceLabel: 'NIST AI RMF to ISO/IEC 42001 crosswalk',
          sourceUrl: 'https://airc.nist.gov/docs/NIST_AI_RMF_to_ISO_IEC_42001_Crosswalk.pdf',
        },
        {
          id: 'ai-human-review',
          title: 'A person reviews the output before a decision',
          action:
            'A person reads the output before it is used for a decision about a person, a payment, a hire, or a customer. Keep a short log: the tool, the date, and who reviewed it. Do not store the private text that was reviewed.',
          evidenceHint: 'Review log with tool, date, and reviewer — no private prompt text',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['ai-acceptable-use'],
          dependencyNote: 'The use rule says when a person has to review. This row is the log.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'ai-tell-people',
          title: 'Say when a person is dealing with an AI system',
          action:
            'When a customer, student, or employee is dealing with an AI system, say so in plain language on that screen or message. Do not claim the European Union AI Act is met. This row only records that people are told.',
          evidenceHint: 'Screenshot or page link that tells the person',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          sourceLabel: 'European Union AI Act',
          sourceUrl: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32024R1689',
        },
        {
          id: 'ai-stop-path',
          title: 'A way to stop a system that is wrong',
          action:
            'Write who can turn the tool off, and who to tell, if the output is wrong or harmful. NIST AI RMF Manage covers stopping a system that is not doing what it was meant to do. Test the path once and write the date.',
          evidenceHint: 'Stop-path note and the date it was tried',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['ai-owner'],
          dependencyNote: 'The owner is the person the stop path has to reach.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'ai-quarterly-review',
          title: 'Quarterly inventory review',
          action:
            'Every quarter, add tools the team started and remove tools they stopped. The owner signs the date. Do not treat last quarter’s list as current.',
          evidenceHint: 'Review date and owner initials',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'recommended',
          dependsOn: ['ai-inventory', 'ai-owner'],
          dependencyNote: 'Review the inventory the owner already keeps.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
      ],
    },
    {
      id: 'ai-impact',
      label: 'Impact and scope',
      summary: 'On CompliComplete, write what happens if a system that affects a person is wrong, and whether a federal or European Union rule even applies.',
      items: [
        {
          id: 'ai-impact-note',
          title: 'Impact note for systems that affect a person',
          action:
            'For each tool that can affect a customer, employee, student, or payment, write what happens if it is wrong, who reviews it, and how a person can ask for a second look. Half a page is enough. This is not an ISO/IEC 42001 certificate.',
          evidenceHint: 'Impact note per tool that affects a person',
          completeRequires: 'evidence',
          planMin: 'enterprise',
          priority: 'required',
          dependsOn: ['ai-human-review'],
          dependencyNote: 'The human-review log shows which tools need an impact note.',
          sourceLabel: 'ISO/IEC 42001',
          sourceUrl: 'https://www.iso.org/standard/81230.html',
        },
        {
          id: 'ai-federal-scope',
          title: 'Federal agency use, if any',
          action:
            'If a federal agency will use the system, ask whether that agency’s AI inventory under OMB Memorandum M-24-10 applies to this work. That memo binds agencies. It does not by itself bind every contractor. Do not mark this finished just because a commercial tool exists. If no agency use is in scope, file a dated note that says so.',
          evidenceHint: 'Agency-use note, or a dated note that no federal use is in scope',
          completeRequires: 'evidence',
          planMin: 'enterprise',
          priority: 'recommended',
          sourceLabel: 'OMB Memorandum M-24-10',
          sourceUrl: 'https://www.whitehouse.gov/wp-content/uploads/2024/03/M-24-10-Advancing-Governance-Innovation-and-Risk-Management-for-Agency-Use-of-Artificial-Intelligence.pdf',
        },
        {
          id: 'ai-eu-scope',
          title: 'European Union output, if any',
          action:
            'If the output is used in the European Union, write whether the company provides the system or only uses a vendor tool. A U.S.-only internal draft tool is not automatically in scope. Do not claim the European Union AI Act is met.',
          evidenceHint: 'Scope note: in the Union, or not',
          completeRequires: 'evidence',
          planMin: 'enterprise',
          priority: 'recommended',
          sourceLabel: 'European Union AI Act',
          sourceUrl: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32024R1689',
        },
        {
          id: 'ai-defense-use',
          title: 'Defense use stays on Government Compliance',
          action:
            'If the work is for the Department of Defense, keep it next to the department’s responsible AI principles: responsible, equitable, traceable, reliable, and governable. That does not replace CMMC, and it does not replace SBIR or STTR data rights. Open Government Compliance for those records. Do not mark this finished from a commercial chatbot alone.',
          evidenceHint: 'Note of the defense use, or a dated note that there is none',
          completeRequires: 'evidence',
          planMin: 'enterprise',
          priority: 'recommended',
          docsLabel: 'Open Government Compliance',
          docsUrl: '/console/government-compliance',
          sourceLabel: 'DoD responsible AI pathway',
          sourceUrl: 'https://media.defense.gov/2024/Oct/26/2003571790/-1/-1/0/2024-06-RAI-STRATEGY-IMPLEMENTATION-PATHWAY.PDF',
        },
      ],
    },
    {
      id: 'ai-agent-security',
      label: 'Agent security',
      summary: 'When a tool can read outside text and then act, keep a record of the guardrails. This is not an ISO/IEC 42001 certificate and it is not a score.',
      items: [
        {
          id: 'ai-agent-inventory',
          title: 'List tools that can act, not only tools that draft',
          action: 'On the inventory, mark which tools can call other systems, send a message, change a file, or spend money. A chat box that only drafts text is not the same as a tool that acts. Name the person who can turn each acting tool off.',
          evidenceHint: 'Inventory rows marked draft-only or can-act, with an owner',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['ai-inventory'],
          dependencyNote: 'Start the inventory before you mark which tools can act.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'ai-untrusted-content',
          title: 'Outside text cannot give the tool its orders',
          action: 'Treat web pages, email, attachments, and retrieved documents as untrusted. They must not override the system instructions or start an action by themselves. A separate rule decides whether the tool may act. Record the products where this split is on, and the ones where it is not.',
          evidenceHint: 'Note of where untrusted text is blocked from giving orders',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['ai-agent-inventory'],
          dependencyNote: 'Know which tools can act before you test whether outside text can steer them.',
          sourceLabel: 'Securing AI agents (2026)',
          sourceUrl: 'https://www.isaca.org/resources/white-papers/2026/cybersecurity-recommendations-for-securing-ai-agents',
        },
        {
          id: 'ai-input-prefilter',
          title: 'Stop toxic, political, and off-topic prompts before the model',
          action:
            'For every chat or assistant people use, run an input filter before the main LLM. Block toxic language, political or other off-topic asks outside your written topic list, and common prompt-injection phrases. Open-source options include NVIDIA NeMo Guardrails input rails with Meta Llama Guard (safety categories) and topic-control models; hosted equivalents are fine. Website-facing proof also lives under Website Compliance → AI/ML Zero Trust & Guardrails. Do not paste private prompts into this row.',
          evidenceHint: 'Filter product name + topic list pointer + one anonymized blocked sample',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['ai-agent-inventory'],
          dependencyNote: 'List the tools before you prove which filter sits in front of them.',
          docsLabel: 'Website AI/ML guardrails',
          docsUrl: '/console/website-compliance',
          sourceLabel: 'NVIDIA NeMo Guardrails',
          sourceUrl: 'https://github.com/NVIDIA-NeMo/Guardrails',
        },
        {
          id: 'ai-tool-least-privilege',
          title: 'Each acting tool gets only the access it needs',
          action: 'List the accounts, files, and actions each acting tool can reach. Remove broad access. Do not put a long-lived password in the tool. A person approves an action that cannot be undone, such as a payment, a filing, or a message to a customer.',
          evidenceHint: 'Access list per tool, and the actions that wait for a person',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'required',
          dependsOn: ['ai-agent-inventory'],
          dependencyNote: 'The acting-tool list comes first.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'ai-output-check',
          title: 'Check what the tool sends out',
          action: 'Before output is used, check it for private data, made-up claims presented as fact, and instructions that would push a person or another tool to do something harmful. Record who reviews that output, or which product filter does.',
          evidenceHint: 'Reviewer or filter name, and one example of a blocked output',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'recommended',
          dependsOn: ['ai-human-review'],
          dependencyNote: 'The human-review step shows which outputs need this check.',
          sourceLabel: 'Top risks for language-model apps',
          sourceUrl: 'https://genai.owasp.org/llm-top-10/',
        },
        {
          id: 'ai-supply-and-data',
          title: 'Know the model and the documents it trusts',
          action: 'Write which model or vendor each acting tool uses, and which document set it searches. Do not let an unknown file become a trusted source. If a vendor model changes, note the date. This is the supply check and the poisoning check. It is not a lab test.',
          evidenceHint: 'Model or vendor name, document source, and the date you last checked',
          completeRequires: 'evidence',
          planMin: 'advanced',
          priority: 'recommended',
          dependsOn: ['ai-vendor-terms'],
          dependencyNote: 'Vendor terms come before you rely on that vendor’s model.',
          sourceLabel: 'NIST AI RMF 1.0',
          sourceUrl: 'https://doi.org/10.6028/NIST.AI.100-1',
        },
        {
          id: 'ai-red-team-and-rollback',
          title: 'Try to misuse it, and keep a way to undo it',
          action: 'Once a year, or before a tool starts acting on customer data, try to steer it with a hostile page, a hostile email, and a request it should refuse. Write what happened. Confirm you can turn the tool off and undo the last action. ai-stop-path is the off switch. This row is the test and the undo.',
          evidenceHint: 'Dated test note and the undo or off switch you used',
          completeRequires: 'evidence',
          planMin: 'enterprise',
          priority: 'recommended',
          dependsOn: ['ai-stop-path', 'ai-untrusted-content'],
          dependencyNote: 'The off switch and the untrusted-text rule come before the misuse test.',
          sourceLabel: 'Securing AI agents (2026)',
          sourceUrl: 'https://www.isaca.org/resources/white-papers/2026/cybersecurity-recommendations-for-securing-ai-agents',
        },
      ],
    },
  ],
};

export const COMPLIANCE_CONSOLES: Record<ComplianceConsoleKind, ComplianceConsoleDefinition> = {
  'website-compliance': WEBSITE_COMPLIANCE_CONSOLE,
  'dod-contracts-compliance': DOD_CONTRACTS_COMPLIANCE_CONSOLE,
  'education-compliance': EDUCATION_COMPLIANCE_CONSOLE,
  'ai-compliance': AI_COMPLIANCE_CONSOLE,
  'business-creation-compliance': BUSINESS_CREATION_COMPLIANCE_CONSOLE,
  'wills-trusts-compliance': WILLS_TRUSTS_COMPLIANCE_CONSOLE,
  'cloud-compliance': CLOUD_COMPLIANCE_CONSOLE,
  'ofac-compliance': OFAC_COMPLIANCE_CONSOLE,
};

const snapshotCompanyCheck = (state: ComplianceConsoleState): DodCompanyCheckState => ({
  items: state.items || {},
  practiceDispositions: state.practiceDispositions || {},
  evidenceConnectors: state.evidenceConnectors || emptyDodEvidenceConnectors(),
  levelAssessment: state.levelAssessment || null,
  businessCreation: state.businessCreation,
  willsTrusts: state.willsTrusts,
  lastScan: state.lastScan ?? null,
  lastScanHistory: state.lastScanHistory || [],
  calendarEvents: state.calendarEvents || [],
});

export const syncActiveCompanyCheck = (state: ComplianceConsoleState): ComplianceConsoleState => {
  const companyId = state.taxonomy?.activeCompanyId;
  if (!companyId) {
    return state;
  }
  return {
    ...state,
    companyChecks: {
      ...(state.companyChecks || {}),
      [companyId]: snapshotCompanyCheck(state),
    },
  };
};

const formationAnswerCount = (answers: BusinessCreationAnswers | undefined): number => {
  if (!answers) {
    return 0;
  }
  return Object.entries(answers).filter(([key, value]) => (
    key !== 'legalName' && value !== undefined && value !== null && String(value).trim() !== ''
  )).length;
};

export const hydrateActiveCompanyCheck = (state: ComplianceConsoleState): ComplianceConsoleState => {
  const companyId = state.taxonomy?.activeCompanyId;
  if (!companyId) {
    return state;
  }
  const snapshot = (state.companyChecks || {})[companyId];
  if (!snapshot) {
    return state;
  }
  const hasItems = Boolean(snapshot.items && Object.keys(snapshot.items).length > 0);
  if (!hasItems && snapshot.businessCreation === undefined && snapshot.willsTrusts === undefined) {
    return state;
  }
  const snapshotAnswers = snapshot.businessCreation;
  const rootAnswers = state.businessCreation;
  const businessCreation = formationAnswerCount(rootAnswers) > formationAnswerCount(snapshotAnswers)
    ? rootAnswers
    : (snapshotAnswers ?? rootAnswers);
  return {
    ...state,
    ...(hasItems
      ? {
        items: snapshot.items,
        practiceDispositions: snapshot.practiceDispositions || {},
        evidenceConnectors: snapshot.evidenceConnectors || emptyDodEvidenceConnectors(),
        levelAssessment: snapshot.levelAssessment ?? null,
        ...(Object.prototype.hasOwnProperty.call(snapshot, 'lastScan')
          ? { lastScan: snapshot.lastScan ?? null, lastScanHistory: snapshot.lastScanHistory || [] }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(snapshot, 'calendarEvents')
          ? { calendarEvents: snapshot.calendarEvents || [] }
          : {}),
      }
      : {}),
    businessCreation,
    willsTrusts: parseWillsTrustsAnswers(snapshot.willsTrusts ?? state.willsTrusts),
    companyChecks: businessCreation === snapshotAnswers
      ? state.companyChecks
      : {
        ...(state.companyChecks || {}),
        [companyId]: {
          ...snapshot,
          businessCreation,
        },
      },
  };
};

/** A new company starts its own formation interview. It does not keep the previous company's answers. */
const businessCreationForCompany = (
  state: ComplianceConsoleState,
  incoming: DodCompanyCheckState,
  company: { name?: string } | undefined,
): BusinessCreationAnswers | undefined => {
  if (incoming.businessCreation) {
    return incoming.businessCreation;
  }
  if (!state.businessCreation) {
    return undefined;
  }
  const name = String(company?.name || '').trim();
  if (name && state.businessCreation.legalName === name) {
    return state.businessCreation;
  }
  return name ? { legalName: name } : {};
};

export const selectDodCompany = (
  state: ComplianceConsoleState,
  companyId: string,
): ComplianceConsoleState => {
  const taxonomy = state.taxonomy || emptyDodTaxonomy();
  const previousId = taxonomy.activeCompanyId;
  const checks = { ...(state.companyChecks || {}) };
  if (previousId) {
    const previousCompany = taxonomy.companies.find((row) => row.id === previousId);
    const previousName = String(previousCompany?.name || '').trim();
    const snap = snapshotCompanyCheck(state);
    const answersName = String(snap.businessCreation?.legalName || '').trim();
    if (previousName && answersName && answersName !== previousName) {
      const prior = checks[previousId]?.businessCreation;
      snap.businessCreation = prior && prior.legalName === previousName
        ? prior
        : { legalName: previousName };
    }
    checks[previousId] = snap;
  }
  if (!companyId) {
    return {
      ...state,
      updatedAt: new Date().toISOString(),
      targetCompanyName: '',
      items: {},
      practiceDispositions: {},
      evidenceConnectors: emptyDodEvidenceConnectors(),
      levelAssessment: null,
      companyChecks: checks,
      businessCreation: undefined,
      willsTrusts: undefined,
      lastScan: null,
      lastScanHistory: [],
      taxonomy: {
        ...taxonomy,
        activeCompanyId: '',
        activeProjectId: '',
        activeContractId: '',
      },
    };
  }
  const company = taxonomy.companies.find((row) => row.id === companyId);
  const project = taxonomy.projects.find((row) => row.companyId === companyId);
  const incoming = checks[companyId] || {
    items: {},
    practiceDispositions: {},
    evidenceConnectors: emptyDodEvidenceConnectors(),
    levelAssessment: null,
  };
  const hasOwnScan = Object.prototype.hasOwnProperty.call(incoming, 'lastScan');
  return {
    ...state,
    updatedAt: new Date().toISOString(),
    targetCompanyName: company?.name || '',
    items: incoming.items,
    practiceDispositions: incoming.practiceDispositions,
    evidenceConnectors: incoming.evidenceConnectors,
    levelAssessment: incoming.levelAssessment,
    businessCreation: businessCreationForCompany(state, incoming, company),
    willsTrusts: incoming.willsTrusts || {},
    lastScan: hasOwnScan ? (incoming.lastScan ?? null) : null,
    lastScanHistory: hasOwnScan ? (incoming.lastScanHistory || []) : [],
    companyChecks: checks,
    taxonomy: {
      ...taxonomy,
      activeCompanyId: companyId,
      activeProjectId: project?.id || '',
      activeContractId: '',
    },
  };
};

export const DOD_GATE_LABELS: Record<DodComplianceGate, { en: string; es: string }> = {
  entity: { en: 'Gate 0 — Entity', es: 'Puerta 0 — Entidad' },
  fci: { en: 'Gate 1 — FCI / CMMC L1', es: 'Puerta 1 — FCI / CMMC N1' },
  cui: { en: 'Gate 2 — CUI', es: 'Puerta 2 — CUI' },
  classified: { en: 'Gate 3 — Classified (optional)', es: 'Puerta 3 — Clasificado (opcional)' },
  pursuit: { en: 'Pursuit', es: 'Captación' },
};

export const dodGateLabel = (gate: DodComplianceGate | undefined, language: 'en' | 'es'): string => {
  if (!gate) {
    return '';
  }
  return language === 'es' ? DOD_GATE_LABELS[gate].es : DOD_GATE_LABELS[gate].en;
};

export const dodItemsForGate = (
  definition: ComplianceConsoleDefinition,
  gate: DodComplianceGate,
): ComplianceChecklistItemDef[] => (
  definition.pillars.flatMap((pillar) => pillar.items).filter((item) => item.gate === gate)
);

export const findChecklistItem = (
  definition: ComplianceConsoleDefinition,
  itemId: string,
): ComplianceChecklistItemDef | undefined => (
  definition.pillars.flatMap((pillar) => pillar.items).find((item) => item.id === itemId)
);

export const unmetChecklistDependencies = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
  item: ComplianceChecklistItemDef,
): ComplianceChecklistItemDef[] => (
  checklistDependencyStates(definition, state, item)
    .filter((row) => !row.done)
    .map((row) => row.item)
);

export const checklistDependencyStates = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
  item: ComplianceChecklistItemDef,
): ChecklistDependencyState[] => (
  (item.dependsOn || [])
    .map((id) => findChecklistItem(definition, id))
    .filter((row): row is ComplianceChecklistItemDef => Boolean(row))
    .map((row) => ({
      item: row,
      done: (state.items[row.id]?.status || 'not_started') === 'reviewed',
    }))
);

/** How many predecessor steps sit before this one. A cycle counts as zero extra depth. */
export const checklistDependencyDepth = (
  definition: ComplianceConsoleDefinition,
  itemId: string,
  seen: ReadonlySet<string> = new Set(),
): number => {
  if (seen.has(itemId)) {
    return 0;
  }
  const deps = findChecklistItem(definition, itemId)?.dependsOn || [];
  if (!deps.length) {
    return 0;
  }
  const next = new Set(seen);
  next.add(itemId);
  return Math.min(
    5,
    1 + Math.max(...deps.map((id) => checklistDependencyDepth(definition, id, next))),
  );
};

export type RecommendedWorkInput = {
  status: string;
  priority?: string;
  dueDate?: string;
  openDeps?: number;
  unlocks?: number;
  waitsOutside?: boolean;
  today?: string;
};

/** Due dates, then work you can do now, then blocking, then steps that free the most other work. */
export const recommendedWorkRank = (row: RecommendedWorkInput): string => {
  const today = row.today || new Date().toISOString().slice(0, 10);
  const due = row.dueDate || '';
  let dateBucket = '3';
  if (due && due < today) dateBucket = '0';
  else if (due) {
    const days = Math.round((Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
    dateBucket = days <= 30 ? '1' : '2';
  }
  const openDeps = Math.max(0, row.openDeps || 0);
  const ability = openDeps > 0 ? '2' : row.waitsOutside ? '1' : '0';
  const priority = row.priority === 'blocking' ? '0' : row.priority === 'required' ? '1' : row.priority === 'recommended' ? '2' : '3';
  const momentum = row.status === 'in_progress' ? '0' : row.status === 'not_started' ? '1' : row.status === 'gap' ? '2' : '3';
  const unlocks = String(1000 - Math.min(999, row.unlocks || 0)).padStart(4, '0');
  const deps = String(Math.min(99, openDeps)).padStart(2, '0');
  return `${dateBucket}|${due || '9999-99-99'}|${ability}|${priority}|${deps}|${unlocks}|${momentum}`;
};

export const shortDependencyLabel = (
  item: ComplianceChecklistItemDef,
  language: 'en' | 'es',
  facts?: { companyName?: string; ein?: string },
): string => {
  if (item.id === 'tx-llc-good-standing') {
    const name = (facts?.companyName || '').trim();
    if (language === 'es') {
      return name ? `LLC en archivo (${name})` : 'LLC en archivo';
    }
    return name ? `LLC on file (${name})` : 'LLC on file';
  }
  if (item.id === 'ein-irs') {
    const ein = (facts?.ein || '').trim();
    return ein ? `EIN ${ein}` : 'EIN';
  }
  if (item.id === 'sam-uei-cage') {
    return 'SAM';
  }
  if (item.id === 'business-bank') {
    return language === 'es' ? 'Banco' : 'Bank';
  }
  return item.title;
};

const SAM_SUBMITTED_NOTE = /INC-|Documentation Successfully Submitted|Entity Under Review|Pending ID Assignment|open validation case/i;
const SAM_PRE_VALIDATION_STEP_IDS = [
  'sam-sign-in',
  'sam-register-all-awards',
  'sam-entity-details',
  'sam-submit-docs',
] as const;

export const inferItemStepProgress = (
  item: ComplianceChecklistItemDef,
  itemState: ComplianceItemState | undefined,
): Record<string, ComplianceStepEvidence> => {
  const stored: Record<string, ComplianceStepEvidence> = { ...(itemState?.stepProgress || {}) };
  const notes = String(itemState?.notes || '');
  if (item.id === 'sam-uei-cage' && SAM_SUBMITTED_NOTE.test(notes)) {
    const incident = notes.match(/INC-[A-Z0-9]+/i)?.[0];
    SAM_PRE_VALIDATION_STEP_IDS.forEach((stepId) => {
      if (stored[stepId]?.done === false) {
        return;
      }
      stored[stepId] = {
        inferred: stored[stepId]?.inferred ?? !stored[stepId]?.done,
        evidenceNote: stored[stepId]?.evidenceNote || (
          stepId === 'sam-submit-docs' && incident
            ? incident
            : 'Confirmed from SAM case notes'
        ),
        ...(stored[stepId] || {}),
        done: true,
      };
    });
  }
  if (itemState?.status === 'reviewed') {
    (item.verifySteps || []).forEach((step) => {
      if (stored[step.id]?.done === false) {
        return;
      }
      stored[step.id] = {
        evidenceNote: stored[step.id]?.evidenceNote || 'Marked complete',
        ...(stored[step.id] || {}),
        done: true,
      };
    });
  }
  return stored;
};

export const verifiedStepCounts = (
  item: ComplianceChecklistItemDef,
  itemState: ComplianceItemState | undefined,
): { done: number; total: number; current?: ComplianceVerifyStepDef } => {
  const steps = item.verifySteps || [];
  const progress = inferItemStepProgress(item, itemState);
  const done = steps.filter((step) => progress[step.id]?.done).length;
  return {
    done,
    total: steps.length,
    current: steps.find((step) => !progress[step.id]?.done),
  };
};

export const checklistItemWaiting = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
  item: ComplianceChecklistItemDef,
): boolean => (
  isDodWaitingOnExternal(item, state)
  || (item.id === 'formation-filing' && state.businessCreation?.stage === 'pending')
  || unmetChecklistDependencies(definition, state, item).length > 0
);

export const isDodWaitingOnExternal = (
  item: ComplianceChecklistItemDef,
  state: ComplianceConsoleState,
): boolean => {
  const samState = state.items['sam-uei-cage'];
  const samNotesWaiting = /Entity Under Review|Pending ID Assignment|GSA FSD INC-|open validation case/i.test(
    String(samState?.notes || ''),
  );
  const submitted = Boolean(inferItemStepProgress(
    { id: 'sam-uei-cage', title: '', action: '', evidenceHint: '' },
    samState,
  )['sam-submit-docs']?.done);
  const gsaDone = Boolean(samState?.stepProgress?.['sam-gsa-validation']?.done);
  const samWaiting = samNotesWaiting || (submitted && !gsaDone);
  if (item.id === 'sam-uei-cage' || item.id === 'naics-reps-certs' || item.id === 'sba-certifications') {
    return samWaiting;
  }
  return false;
};

/** A step whose next move belongs to someone outside the account. */
export type OutsideWait = {
  party: { en: string; es: string };
  /** Calendar days between checks. A missed check stays at the top of Home. */
  checkDays: number;
};

export const outsideWaitFor = (
  item: ComplianceChecklistItemDef,
  state: ComplianceConsoleState,
): OutsideWait | null => {
  const status = state.items[item.id]?.status || 'not_started';
  if (status === 'reviewed') {
    return null;
  }
  if (item.id === 'sam-uei-cage' && (isDodWaitingOnExternal(item, state) || status === 'in_progress' || status === 'gap')) {
    return { party: { en: 'GSA', es: 'GSA' }, checkDays: 3 };
  }
  if (item.id === 'formation-filing' && state.businessCreation?.stage === 'pending') {
    return {
      party: { en: 'the state filing office', es: 'la oficina estatal de presentaciones' },
      checkDays: 3,
    };
  }
  if (item.id === 'saeps-vendor' && status !== 'not_started') {
    if (state.items[item.id]?.stepProgress?.['saeps-preregister']?.done === false) {
      return null;
    }
    return {
      party: { en: 'the San Antonio vendor desk', es: 'la mesa de proveedores de San Antonio' },
      checkDays: 2,
    };
  }
  if (item.id === 'apex-accelerator' && (status === 'in_progress' || status === 'gap')) {
    return {
      party: { en: 'the procurement counseling office', es: 'la oficina de asesoría de compras' },
      checkDays: 7,
    };
  }
  return null;
};

/** never: no saved check. due: the interval has elapsed. fresh: still inside the interval. */
export const outsideCheckStatus = (
  lastCheck: string | undefined,
  checkDays: number,
  today: string,
): 'never' | 'due' | 'fresh' => {
  if (!lastCheck || !/^\d{4}-\d{2}-\d{2}$/.test(lastCheck)) {
    return 'never';
  }
  const due = new Date(`${lastCheck}T00:00:00Z`);
  if (Number.isNaN(due.getTime())) {
    return 'never';
  }
  due.setUTCDate(due.getUTCDate() + Math.max(1, checkDays));
  return due.toISOString().slice(0, 10) <= today ? 'due' : 'fresh';
};

export const dodBlockingGaps = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
): number => (
  definition.pillars
    .flatMap((pillar) => pillar.items)
    .filter((item) => item.priority === 'blocking' && (state.items[item.id]?.status || 'not_started') !== 'reviewed')
    .length
);

export const nextDodDecisions = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
  limit = 3,
  plan: PricingTierId = 'enterprise',
): Array<{ item: ComplianceChecklistItemDef; pillarId: string; pillarLabel: string }> => {
  const rank = (
    item: ComplianceChecklistItemDef,
    status: ComplianceItemStatus,
    priority?: ComplianceItemPriority,
  ): number => {
    if (checklistItemWaiting(definition, state, item)) {
      return 4;
    }
    if (priority === 'blocking' && status !== 'reviewed') {
      return 0;
    }
    if (status === 'gap') {
      return 1;
    }
    if (status === 'in_progress') {
      return 2;
    }
    if (status === 'not_started') {
      return 3;
    }
    return 9;
  };
  const openRows = definition.pillars
    .flatMap((pillar) => pillar.items.map((item) => ({
      item,
      pillarId: pillar.id,
      pillarLabel: pillar.label,
      status: (state.items[item.id]?.status || 'not_started') as ComplianceItemStatus,
    })))
    .filter((row) => row.status !== 'reviewed')
    .filter((row) => itemIncludedInPlan(row.item, plan))
    .sort((left, right) => rank(left.item, left.status, left.item.priority) - rank(right.item, right.status, right.item.priority));
  const waiting = openRows.filter((row) => checklistItemWaiting(definition, state, row.item));
  const actionable = openRows.filter((row) => !checklistItemWaiting(definition, state, row.item));
  const waitingCap = waiting.length ? 1 : 0;
  return [...actionable.slice(0, Math.max(0, limit - waitingCap)), ...waiting.slice(0, waitingCap)]
    .map(({ item, pillarId, pillarLabel }) => ({ item, pillarId, pillarLabel }));
};

export type HomeworkRow = {
  consoleKind: ComplianceConsoleKind;
  itemId: string;
  title: string;
  category: string;
  status: ComplianceItemStatus;
  priority: ComplianceItemPriority;
};

export const collectHomeworkRows = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
  plan: PricingTierId = 'enterprise',
): HomeworkRow[] => (
  definition.pillars.flatMap((pillar) => (
    pillar.items
      .filter((item) => itemIncludedInPlan(item, plan))
      .map((item) => ({
        consoleKind: definition.kind,
        itemId: item.id,
        title: item.title,
        category: pillar.label,
        status: (state.items[item.id]?.status || 'not_started') as ComplianceItemStatus,
        priority: item.priority || 'required',
      }))
  ))
);

export const homeworkStatusTally = (
  rows: HomeworkRow[],
): Record<ComplianceItemStatus, number> => (
  rows.reduce((tally, row) => {
    tally[row.status] += 1;
    return tally;
  }, {
    not_started: 0,
    in_progress: 0,
    reviewed: 0,
    gap: 0,
  } as Record<ComplianceItemStatus, number>)
);

const DOD_GATE_SORT: Record<DodComplianceGate, number> = {
  entity: 0,
  fci: 1,
  cui: 2,
  classified: 3,
  pursuit: 4,
};

/** Reviewed checklist rows for the DoD home “Done” list (entity IDs first). */
export const recentDodCompleted = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
  limit = 8,
): Array<{ item: ComplianceChecklistItemDef; pillarId: string; pillarLabel: string }> => (
  definition.pillars
    .flatMap((pillar) => pillar.items.map((item) => ({
      item,
      pillarId: pillar.id,
      pillarLabel: pillar.label,
      status: (state.items[item.id]?.status || 'not_started') as ComplianceItemStatus,
    })))
    .filter((row) => row.status === 'reviewed')
    .sort((left, right) => {
      const leftGate = left.item.gate ? DOD_GATE_SORT[left.item.gate] : 9;
      const rightGate = right.item.gate ? DOD_GATE_SORT[right.item.gate] : 9;
      return leftGate - rightGate;
    })
    .slice(0, limit)
    .map(({ item, pillarId, pillarLabel }) => ({ item, pillarId, pillarLabel }))
);

export const nextComplianceDecisions = nextDodDecisions;
export const recentComplianceCompleted = recentDodCompleted;

/** Promote a legacy free-text company name into the shared Which-company picker. */
export const bootstrapCompanyFromTargetName = (
  state: ComplianceConsoleState,
): ComplianceConsoleState => {
  const name = String(state.targetCompanyName || '').trim();
  const taxonomy = state.taxonomy || emptyDodTaxonomy();
  if (!name || taxonomy.companies.length > 0) {
    return state;
  }
  const id = newDodId('company');
  return selectDodCompany({
    ...state,
    taxonomy: {
      ...taxonomy,
      companies: [...taxonomy.companies, { id, name, ein: '' }],
    },
  }, id);
};

export type ComplianceItemState = {
  status: ComplianceItemStatus;
  notes: string;
  reviewedAt?: string;
  reviewedBy?: string;
  stepProgress?: Record<string, ComplianceStepEvidence>;
  /** Operator confirmed the keep-copy document is saved in the company file. */
  copySavedAt?: string;
};

export type DodCompanyCheckState = {
  items: Record<string, ComplianceItemState>;
  practiceDispositions: Record<string, PracticeDisposition>;
  evidenceConnectors: DodEvidenceConnectors;
  levelAssessment: DodLevelAssessment | null;
  businessCreation?: BusinessCreationAnswers;
  willsTrusts?: WillsTrustsAnswers;
  /** Homepage scan for this website. Other websites on the same account keep their own. */
  lastScan?: WebsiteComplianceLastScan | null;
  lastScanHistory?: WebsiteComplianceScanHistoryEntry[];
  calendarEvents?: ComplianceCalendarEvent[];
};

export type ComplianceConsoleState = {
  updatedAt: string;
  items: Record<string, ComplianceItemState>;
  /** Organization being assessed for this console (independent of billing company). */
  targetCompanyName?: string;
  /** Latest homepage scan payload (website-compliance only). */
  lastScan?: WebsiteComplianceLastScan | null;
  /** Previous homepage scans, newest first (website-compliance only). */
  lastScanHistory?: WebsiteComplianceScanHistoryEntry[];
  /** Homepage rescan cadence (website-compliance only). */
  scanSchedule?: WebsiteScanSchedule | null;
  taxonomy?: DodTaxonomyState;
  practiceDispositions?: Record<string, PracticeDisposition>;
  levelAssessment?: DodLevelAssessment | null;
  evidenceConnectors?: DodEvidenceConnectors;
  /** Per-company checklist snapshots (DoD console). */
  companyChecks?: Record<string, DodCompanyCheckState>;
  /** One-off seed keys already applied. */
  bootstraps?: string[];
  /** Business-creation interview answers. Steps are derived from these. */
  businessCreation?: BusinessCreationAnswers;
  /** Wills and trusts interview answers. Steps are derived from these. */
  willsTrusts?: WillsTrustsAnswers;
  /** Last queued read-only cloud review. It never marks a step done. */
  lastCloudReview?: CloudReadOnlyReview | null;
  /** Dates the operator saved on the compliance calendar. */
  calendarEvents?: ComplianceCalendarEvent[];
};

export type CloudProviderId = 'aws' | 'azure' | 'gcp';

export type CloudReadOnlyReview = {
  queuedAt: string;
  provider: CloudProviderId;
  roleName: string;
  accountName: string;
  status: 'waiting_for_role' | 'queued';
  summary: string;
};

/** The gosslife organization account. Client reviews do not run here. */
export const NEXTCOMPLI_OPS_ACCOUNT_ID = '117687871314';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object'
);

export const normalizeComplianceConsoleState = (parsed: unknown): ComplianceConsoleState => {
  if (!isRecord(parsed) || !isRecord(parsed.items)) {
    return emptyComplianceState();
  }
  const taxonomy = parsed.taxonomy && typeof parsed.taxonomy === 'object'
    ? parsed.taxonomy as DodTaxonomyState
    : emptyDodTaxonomy();
  const practiceDispositions = isRecord(parsed.practiceDispositions)
    ? parsed.practiceDispositions as Record<string, PracticeDisposition>
    : {};
  const evidenceConnectors = isRecord(parsed.evidenceConnectors)
    ? {
      ...emptyDodEvidenceConnectors(),
      ...(parsed.evidenceConnectors as Partial<DodEvidenceConnectors>),
    }
    : emptyDodEvidenceConnectors();
  const levelAssessment = parsed.levelAssessment && typeof parsed.levelAssessment === 'object'
    ? parsed.levelAssessment as DodLevelAssessment
    : null;
  const companyChecks = isRecord(parsed.companyChecks)
    ? parsed.companyChecks as Record<string, DodCompanyCheckState>
    : {};
  const bootstraps = Array.isArray(parsed.bootstraps)
    ? parsed.bootstraps.filter((entry): entry is string => typeof entry === 'string')
    : [];
  return hydrateActiveCompanyCheck({
    updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
    items: parsed.items as Record<string, ComplianceItemState>,
    targetCompanyName: typeof parsed.targetCompanyName === 'string' ? parsed.targetCompanyName : '',
    lastScan:
      parsed.lastScan && typeof parsed.lastScan === 'object'
        ? (parsed.lastScan as WebsiteComplianceLastScan)
        : null,
    lastScanHistory: Array.isArray(parsed.lastScanHistory)
      ? parsed.lastScanHistory.filter((entry): entry is WebsiteComplianceScanHistoryEntry => (
        Boolean(entry) && typeof entry === 'object'
      ))
      : [],
    scanSchedule: isRecord(parsed.scanSchedule) && (
      parsed.scanSchedule.cadence === 'off'
      || parsed.scanSchedule.cadence === 'weekly'
      || parsed.scanSchedule.cadence === 'daily'
    )
      ? {
        cadence: parsed.scanSchedule.cadence,
        nextDueAt: typeof parsed.scanSchedule.nextDueAt === 'string' ? parsed.scanSchedule.nextDueAt : '',
      }
      : null,
    taxonomy,
    practiceDispositions,
    levelAssessment,
    evidenceConnectors,
    companyChecks,
    bootstraps,
    businessCreation: parseBusinessCreationAnswers(parsed.businessCreation),
    willsTrusts: parseWillsTrustsAnswers(parsed.willsTrusts),
    lastCloudReview: parseCloudReadOnlyReview(parsed.lastCloudReview),
    calendarEvents: parseCalendarEvents(parsed.calendarEvents),
  });
};

const parseCloudReadOnlyReview = (value: unknown): CloudReadOnlyReview | null => {
  if (!isRecord(value)) return null;
  const provider = value.provider;
  if (provider !== 'aws' && provider !== 'azure' && provider !== 'gcp') return null;
  if (typeof value.queuedAt !== 'string' || typeof value.summary !== 'string') return null;
  const status = value.status === 'queued' ? 'queued' : 'waiting_for_role';
  return {
    queuedAt: value.queuedAt,
    provider,
    roleName: typeof value.roleName === 'string' ? value.roleName : '',
    accountName: typeof value.accountName === 'string' ? value.accountName : '',
    status,
    summary: value.summary,
  };
};

export const emptyComplianceState = (): ComplianceConsoleState => ({
  updatedAt: new Date().toISOString(),
  items: {},
  targetCompanyName: '',
  lastScan: null,
  lastScanHistory: [],
  taxonomy: emptyDodTaxonomy(),
  practiceDispositions: {},
  levelAssessment: null,
  evidenceConnectors: emptyDodEvidenceConnectors(),
  companyChecks: {},
  bootstraps: [],
  lastCloudReview: null,
});

const SECRET_IN_CLOUD_ROLE = /AKIA[0-9A-Z]{16}|BEGIN (RSA |OPENSSH )?PRIVATE KEY|password\s*[:=]|secret[_ ]?key/i;

export function queueCloudReadOnlyReview(
  state: ComplianceConsoleState,
  input: { provider: string; roleName: string; accountName: string; now?: string },
): ComplianceConsoleState {
  if (input.provider !== 'aws' && input.provider !== 'azure' && input.provider !== 'gcp') {
    throw new Error('Choose Amazon Web Services, Microsoft Azure, or Google Cloud.');
  }
  const accountName = input.accountName.trim();
  const roleName = input.roleName.trim();
  const blob = `${accountName} ${roleName}`;
  if (blob.includes(NEXTCOMPLI_OPS_ACCOUNT_ID)) {
    throw new Error('This review does not run on the NextCompli operations account.');
  }
  if (SECRET_IN_CLOUD_ROLE.test(blob)) {
    throw new Error('Do not put a password or a secret key in the role name.');
  }
  const queuedAt = input.now || new Date().toISOString();
  const waiting = roleName.length === 0;
  const summary = waiting
    ? 'The daily review did not start. Record the read-only role name first. No step was marked done.'
    : 'Queued for today. NextCompli has not signed into the client cloud. No finding is a pass until that read-only role is connected and a person files proof.';
  const previous = state.items['daily-readonly-review'];
  const note = `${queuedAt.slice(0, 10)} ${input.provider}: ${summary}`;
  return {
    ...state,
    updatedAt: queuedAt,
    lastCloudReview: {
      queuedAt,
      provider: input.provider,
      roleName,
      accountName,
      status: waiting ? 'waiting_for_role' : 'queued',
      summary,
    },
    items: {
      ...state.items,
      'daily-readonly-review': {
        status: previous?.status === 'reviewed' ? 'reviewed' : (waiting ? 'not_started' : 'in_progress'),
        notes: [previous?.notes, note].filter(Boolean).join('\n'),
      },
    },
  };
}

export const loadComplianceConsoleState = (storageKey: string): ComplianceConsoleState => {
  if (typeof window === 'undefined') {
    return emptyComplianceState();
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    const loaded = raw
      ? normalizeComplianceConsoleState(JSON.parse(raw))
      : emptyComplianceState();
    return loaded;
  } catch {
    return emptyComplianceState();
  }
};

export const saveComplianceConsoleState = (storageKey: string, state: ComplianceConsoleState): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(storageKey, JSON.stringify(state));
};

/** Percent of in-plan checklist items marked reviewed (0–100). */
export const PLAN_RANK: Record<PricingTierId, number> = {
  basic: 0,
  advanced: 1,
  enterprise: 2,
};

export const PLAN_PUBLIC_NAME: Record<PricingTierId, string> = {
  basic: 'CompliCore',
  advanced: 'CompliAdvantage',
  enterprise: 'CompliComplete',
};

export const itemPlanMin = (item: ComplianceChecklistItemDef): PricingTierId => item.planMin || 'basic';

export const itemIncludedInPlan = (item: ComplianceChecklistItemDef, plan: PricingTierId): boolean => (
  PLAN_RANK[itemPlanMin(item)] <= PLAN_RANK[plan]
);

export const planFromFeatures = (
  effectiveFeatures: string[] | null | undefined,
  options?: { isPlatformAdmin?: boolean },
): PricingTierId => {
  if (options?.isPlatformAdmin) {
    return 'enterprise';
  }
  const granted = new Set(
    (effectiveFeatures || []).map((feature) => String(feature || '').trim().toLowerCase()).filter(Boolean),
  );
  if (
    granted.has('embedded-consultants')
    || granted.has('scheduled-audits')
    || granted.has('case-audit-workspace')
  ) {
    return 'enterprise';
  }
  if (
    granted.has('continuous-monitoring')
    || granted.has('real-time-alerts')
    || granted.has('audit-trail-alert-workflow')
  ) {
    return 'advanced';
  }
  return 'basic';
};

export const complianceProgressPercent = (
  definition: ComplianceConsoleDefinition,
  state: ComplianceConsoleState,
  plan: PricingTierId = 'enterprise',
): number => {
  const items = definition.pillars
    .flatMap((pillar) => pillar.items)
    .filter((item) => itemIncludedInPlan(item, plan));
  if (items.length === 0) {
    return 0;
  }
  const reviewed = items.filter((item) => state.items[item.id]?.status === 'reviewed').length;
  return Math.round((reviewed / items.length) * 100);
};

export const hasComplianceEntitlement = (
  effectiveFeatures: string[] | null | undefined,
  entitlement: string,
  options?: { isPlatformAdmin?: boolean },
): boolean => {
  if (options?.isPlatformAdmin) {
    return true;
  }
  const granted = new Set(
    (effectiveFeatures || []).map((feature) => String(feature || '').trim().toLowerCase()).filter(Boolean),
  );
  return granted.has(String(entitlement).trim().toLowerCase());
};
